import { DelegationsService, POWER } from 'src/app/service/delegations.service';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NotificationService } from 'src/app/core/services/notification.service';
import { FINANCE_SUB_MODULES, hasFinanceSubModuleAccess } from 'src/app/constants/finance.constant';
import { isSessionRejected } from 'src/app/core/interceptors/auth-error.interceptor';

// ─── Data Models ───────────────────────────────────────────────────────────────

export interface ClientPOC {
  name: string;
  email: string;
  phone: string;
}

/** One optional client document (GST certificate, PAN card, VRF, NDA). */
export interface ClientDocument {
  fileUrl: string;
  name?: string;
  uploadedBy?: string;
  uploadedById?: string;
  uploadedAt?: string;
}
export const CLIENT_DOCUMENT_KINDS: { key: string; label: string; hint: string }[] = [
  { key: 'gst_certificate', label: 'GST certificate', hint: 'The client\'s GST registration certificate (PDF)' },
  { key: 'pan_card', label: 'PAN card', hint: 'Company PAN card (PDF or image)' },
  { key: 'vrf', label: 'Vendor registration form (VRF)', hint: 'The form the client uses to register us as their vendor' },
  { key: 'nda', label: 'NDA', hint: 'Signed non-disclosure agreement' },
];

export interface Client {
  legalName: string;
  pocs: ClientPOC[];
  accountsEmail: string;
  gst: string;
  address: string;
  /** Optional documents keyed by CLIENT_DOCUMENT_KINDS (17 Oct 2026). */
  documents?: Record<string, ClientDocument>;
}

/**
 * A client remembered from earlier projects (`GET finance/clients/search`, 30 Oct 2026): the
 * details the new-project form copies into its client step when the BD picks it.
 */
export interface FinanceClientMatch {
  id: string;
  legalName: string;
  gst: string;
  address: string;
  accountsEmail: string;
  pocs: ClientPOC[];
  logoUrl: string;
}

export interface Milestone {
  id: string;
  label: string;
  amount: number;
  pct?: number;       // % of contract value
  /** v2 create form: % of the parent category's share (pct = catPct * catSharePct / 100) */
  catSharePct?: number;
  estDate: string;
  invoiceId?: string;
  /** "Raise when…" — the delivery/approval condition that triggers this invoice (v2) */
  raiseTrigger?: string;
  /** v2: which BillingCategory this milestone belongs to (mirrors parent) */
  categoryKey?: BillingCategoryKey;
  /** Server-stamped on every change to amount / share / category / label / date (20 Oct 2026): who, when, which fields. */
  lastChange?: { by: string; byId?: string; at: string; fields?: string[] };
  /** 'books' when the opening-data merge (30 Oct 2026) attached a bill from the books to it, split it or added it. */
  mergedFrom?: string;
  // Hardware fields — in v2 these are DERIVED from the parent Hardware category
  // (kept on the milestone so Raise-Invoice / E-Way flows keep working unchanged)
  isHardware?: boolean;
  hsnCode?: string;
  eWayBillUrl?: string;
  deliveryChallanUrl?: string;
  /** Set when this milestone is one period of a retainer (see RetainerSeries). */
  seriesId?: string;
}

/**
 * Retainer (10 Oct 2026): a fixed value billed on a schedule inside one
 * category. Each period is an ordinary milestone carrying `seriesId`; the server
 * requests each period's invoice on its date unless the series is paused.
 */
export interface InvoiceDraft {
  status: 'sent' | 'approved' | 'changes_requested';
  fileUrl: string;
  note?: string;
  round?: number;
  sentBy?: string;
  sentById?: string;
  sentAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNote?: string;
  history?: { action: string; by: string; at: string; fileUrl?: string; note?: string }[];
}

/** Client quotation / proposal attached to a project (Accounts prepare PIs from it). */
export interface ProjectQuotation {
  id: string;
  /** 1, 2, 3… in the order they were added (17 Oct 2026). */
  version?: number;
  fileUrl: string;
  label: string;
  uploadedBy?: string;
  uploadedAt?: string;
}

/**
 * Quotations: pasted links are accepted until QUOTATION_LINKS_ALLOWED_UNTIL
 * (Manish, 17 Oct 2026: "in ~30 days strictly PDF / XLSX / CSV"); uploads are
 * already limited to those types. Mirrors the server rule in finance/constants.ts.
 */
export const QUOTATION_FILE_EXTENSIONS = ['.pdf', '.xlsx', '.xls', '.csv'];
export const QUOTATION_ACCEPT = QUOTATION_FILE_EXTENSIONS.join(',');
/** Last day (IST) a pasted link is accepted as any document; quotations set the date on 17 Oct 2026, the founder extended it to every paper on 20 Oct 2026. Mirrors finance/constants.ts on the server. */
export const DOCUMENT_LINKS_ALLOWED_UNTIL = '2026-11-16';
export const QUOTATION_LINKS_ALLOWED_UNTIL = DOCUMENT_LINKS_ALLOWED_UNTIL;
/** File types the finance upload produces (mirrors FINANCE_UPLOAD_EXTENSIONS on the server). */
export const FINANCE_UPLOAD_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.png', '.jpg', '.jpeg', '.webp', '.svg'];
export function documentLinksAllowed(today = toLocalYMD(new Date())): boolean { return today <= DOCUMENT_LINKS_ALLOWED_UNTIL; }
/** An uploaded file (ends in a type the upload produces) as opposed to a pasted page link. */
export function isUploadedDocument(url: unknown): boolean { return isDocLink(url) && FINANCE_UPLOAD_EXTENSIONS.includes(fileExtOf(url)); }
/** '' when a document field is acceptable today, else the sentence to show under it. */
export function documentProblem(url: unknown, today = toLocalYMD(new Date())): string {
  if (!url) return '';
  if (!isDocLink(url)) return 'That is not a link. Upload the file or paste a link starting with http.';
  if (isUploadedDocument(url) || documentLinksAllowed(today)) return '';
  return 'Pasted links are no longer accepted; upload the file.';
}
export function fileExtOf(url: unknown): string {
  const p = String(url || '').trim().split(/[?#]/)[0];
  return (p.toLowerCase().match(/\.[a-z0-9]+$/) || [''])[0];
}
export function isQuotationFile(url: unknown): boolean { return isDocLink(url) && QUOTATION_FILE_EXTENSIONS.includes(fileExtOf(url)); }
export function quotationLinksAllowed(today = toLocalYMD(new Date())): boolean { return today <= QUOTATION_LINKS_ALLOWED_UNTIL; }
/** '' when acceptable today, else the sentence to show under the field. */
export function quotationProblem(url: unknown, today = toLocalYMD(new Date())): string {
  if (!isDocLink(url)) return 'The quotation must be an uploaded file or a link starting with http.';
  if (isQuotationFile(url)) return '';
  return quotationLinksAllowed(today) ? '' : 'Quotations must now be uploaded as PDF, Excel or CSV files; pasted links are no longer accepted.';
}
export function quotationHint(today = toLocalYMD(new Date())): string {
  return quotationLinksAllowed(today)
    ? `Upload a PDF, Excel or CSV. Pasted links still work until ${QUOTATION_LINKS_ALLOWED_UNTIL.split('-').reverse().join('/')}; after that only uploads are accepted.`
    : 'Upload the quotation as a PDF, Excel or CSV file.';
}

export interface RetainerSeries {
  id: string;
  label: string;
  categoryKey: BillingCategoryKey;
  amount: number;          // per period
  frequency: 'monthly' | 'quarterly' | 'half_yearly' | 'yearly';
  startDate: string;       // first invoice date (YYYY-MM-DD)
  count: number;           // number of periods
  paymentDays: number;     // client pays within N days of the request
  autoRequest?: boolean;
  paused?: boolean;
  pausedBy?: string;
  pausedAt?: string;
  createdBy?: string;
  createdAt?: string;
}

export const RETAINER_FREQUENCIES: { value: RetainerSeries['frequency']; label: string; months: number; per: string }[] = [
  { value: 'monthly', label: 'Every month', months: 1, per: 'month' },
  { value: 'quarterly', label: 'Every quarter', months: 3, per: 'quarter' },
  { value: 'half_yearly', label: 'Every 6 months', months: 6, per: 'half-year' },
  { value: 'yearly', label: 'Every year', months: 12, per: 'year' },
];
export function retainerMonths(freq: string): number { return RETAINER_FREQUENCIES.find(f => f.value === freq)?.months || 1; }

/** Same day-of-month N months later, clamped to the month's last day (31 Jan + 1 → 28/29 Feb). */
export function addMonthsYMD(ymd: string, n: number): string {
  const [y, m, d] = (ymd || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  const first = new Date(Date.UTC(y, m - 1 + n, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(d, last))).toISOString().slice(0, 10);
}
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function monthLabel(ymd: string): string { const [y, m] = ymd.split('-').map(Number); return `${MONTHS_SHORT[m - 1]} ${y}`; }
/** "Nov 2026" for monthly, "Nov 2026 – Jan 2027" for longer periods. */
export function retainerPeriodLabel(startYmd: string, months: number): string {
  if (!startYmd) return '';
  return months <= 1 ? monthLabel(startYmd) : `${monthLabel(startYmd)} – ${monthLabel(addMonthsYMD(startYmd, months - 1))}`;
}
/**
 * Lays a retainer out as milestones: `periods` invoices from `firstDate`, one per
 * frequency step, splitting `value` evenly (the last one takes the rounding).
 * `startIndex` continues an existing series (used when extending it).
 */
export function layoutRetainer(o: {
  seriesId: string; label: string; categoryKey: BillingCategoryKey; value: number; periods: number;
  frequency: string; firstDate: string; startIndex?: number; newId: () => string;
}): Milestone[] {
  const n = Math.max(0, Math.floor(o.periods));
  if (!n || !o.firstDate) return [];
  const step = retainerMonths(o.frequency);
  const each = Math.floor(((Number(o.value) || 0) / n) * 100) / 100;
  const out: Milestone[] = [];
  for (let i = 0; i < n; i++) {
    const k = (o.startIndex || 0) + i;
    const date = addMonthsYMD(o.firstDate, k * step);
    const amount = i === n - 1 ? Math.round(((Number(o.value) || 0) - each * (n - 1)) * 100) / 100 : each;
    out.push({
      id: o.newId(), label: `${o.label} · ${retainerPeriodLabel(date, step)}`, amount, pct: 0, estDate: date,
      raiseTrigger: '', categoryKey: o.categoryKey, seriesId: o.seriesId,
    } as Milestone);
  }
  return out;
}

/**
 * % of contract for each amount, rounded to 2 decimals so they add up to exactly
 * `target` (largest-remainder rounding: every value stays within 0.01 of exact).
 */
export function splitPct(amounts: number[], total: number, target?: number): number[] {
  if (!(total > 0) || !amounts.length) return amounts.map(() => 0);
  const exact = amounts.map(a => ((Number(a) || 0) / total) * 10000); // in 0.01% units
  const floors = exact.map(x => Math.floor(x + 1e-9));
  const want = Math.round((target ?? exact.reduce((t, x) => t + x, 0) / 100) * 100);
  let left = want - floors.reduce((t, x) => t + x, 0);
  const order = exact.map((x, i) => ({ i, frac: x - floors[i] })).sort((a, b) => b.frac - a.frac);
  for (let k = 0; left > 0 && k < order.length; k++, left--) floors[order[k].i] += 1;
  for (let k = order.length - 1; left < 0 && k >= 0; k--, left++) floors[order[k].i] -= 1;
  return floors.map(u => u / 100);
}

/** A document field must be an uploaded file or a pasted http(s) link (same rule as the server). */
export function isDocLink(v: unknown): boolean {
  return typeof v === 'string' && v.length <= 2000 && /^https?:\/\/[^\s]+\.[^\s]+/i.test(v.trim());
}
/** E-way bill is needed when a hardware invoice incl. GST is above this. */
export const EWAY_BILL_THRESHOLD = 50000;

export interface PIRecord {
  id: string;
  milestoneIds: string[];       // milestones this PI covers (may be empty)
  amount: number;               // sum of those milestones' amounts unless sent explicitly
  notes: string;
  status: 'requested' | 'raised';
  requestedAt: string;
  requestedBy: string;
  requestedById: string;
  number?: string;
  fileUrl?: string;
  raisedAt?: string;
  raisedBy?: string;
  quotationUrl?: string;
  quotationId?: string;
  /** Draft-PI check (1 Nov 2026): Accounts send a draft, the BD side approves it, then Accounts raise the final, signed PI. */
  draft?: InvoiceDraft;
  /** Files replaced after the raise (3 Nov 2026), oldest first. */
  fileHistory?: ReplacedFile[];
}

export interface PORecord {
  id: string;
  milestoneIds: string[];
  amount: number;               // sum of milestones' amounts unless sent explicitly
  piId: string;                 // '' if not linked to a PI
  number: string;
  notes: string;
  status: 'uploaded';
  filename: string;
  fileUrl: string;
  uploadedAt: string;
  uploadedBy: string;
  /** The PO's own date (YYYY-MM-DD), when Accounts recorded it (3 Nov 2026). */
  date?: string;
  /** Files replaced by Accounts (3 Nov 2026), oldest first. */
  fileHistory?: ReplacedFile[];
}

/** One payment received against an invoice; an invoice may be settled in several. */
export interface PaymentRecord {
  id: string;
  amount: number;
  utr: string;
  paymentDate: string;
  tdsAmount?: number;
  /** How much of this payment (with its TDS) settled the base and the GST (30 Oct 2026); absent on older and books payments. */
  base?: number;
  gst?: number;
  /** Accounts marked this receipt as the GST only. */
  gstOnly?: boolean;
  /** Accounts confirmed one transfer paid this bill and others under the same UTR (2 Nov 2026). */
  sharedUtr?: boolean;
  note?: string;
  recordedBy?: string;
  recordedById?: string;
  recordedAt?: string;
}

/** A payment taken back by Accounts or an Admin (2 Nov 2026): the payment as recorded, plus who undid it, when and why. */
export interface ReversedPaymentRecord extends PaymentRecord {
  reversedAt?: string;
  reversedBy?: string;
  reversedById?: string;
  reverseReason?: string;
}

/** A "GST paid" or "TDS received" mark taken back (2 Nov 2026); `was` keeps the mark's date and reference. */
export interface MoneyUndo {
  kind: 'gst_paid' | 'tds_received';
  undoneAt: string;
  undoneBy: string;
  undoneById?: string;
  reason: string;
  was?: Record<string, any>;
}

/** A raised bill's number or date put right after the raise (2 Nov 2026); `eInvoiceNumber` = what the attached e-invoice still carries. */
export interface InvoiceCorrection {
  correctedAt: string;
  correctedBy: string;
  correctedById?: string;
  reason: string;
  from: { number: string; raisedDate: string };
  to: { number: string; raisedDate: string };
  eInvoiceNumber?: string;
}

/** A file a raised PI, a PO or a bill had before Accounts or an Admin replaced it (3 Nov 2026). */
export interface ReplacedFile {
  fileUrl: string;
  filename?: string;
  replacedAt: string;
  replacedBy: string;
  replacedById?: string;
  reason: string;
}

/** A raised bill's due date moved by Accounts or an Admin on the BD's behalf (3 Nov 2026). */
export interface DueDateChange {
  changedAt: string;
  changedBy: string;
  reason: string;
  from: string;
  to: string;
}

// ── Project hygiene (3 Nov 2026): look-alikes, merge, edits on the BD's behalf, asks to fix ──

/** One live project that looks like a new one, and what they share (`POST finance/projects/look-alikes`). */
export interface LookAlikeProject {
  id: string;
  name: string;
  status: string;
  clientName: string;
  gst: string;
  bdOwnerName: string;
  createdAt: string;
  kinds: string[];
  /** "the same client GSTIN and a very similar name". */
  words: string;
}
/** What the look-alike check found: projects the asker may open, and a count of the others. */
export interface LookAlikes {
  total: number;
  matches: LookAlikeProject[];
  more: number;
  hidden: { count: number; kinds: string[]; words: string };
}

/** A detail Accounts asked the BD to fix; open until it is filled, the BD replies in the comments, or it is withdrawn. */
export interface FixAsk {
  id: string;
  fields: string[];
  note: string;
  askedAt: string;
  askedBy: string;
  askedById?: string;
  askedOf: string;
  askedOfName: string;
  status: 'open' | 'closed';
  closedAt?: string;
  closedBy?: string;
  closedHow?: 'filled' | 'reply' | 'withdrawn';
  closeNote?: string;
}
/** The details an ask can name (the server's `FIX_FIELDS`). */
export const FIX_ASK_FIELDS: { key: string; label: string }[] = [
  { key: 'client.legalName', label: 'Client legal name' },
  { key: 'client.gst', label: 'Client GSTIN' },
  { key: 'client.address', label: 'Billing address' },
  { key: 'client.accountsEmail', label: 'Accounts e-mail' },
  { key: 'client.pocs', label: 'Client contacts' },
  { key: 'billingCompany', label: '"Bills from"' },
  { key: 'pos', label: 'PO (number and file)' },
];

/** A change Accounts (or an Admin) made on the BD's behalf, each value old → new. */
export interface AccountsEdit {
  at: string;
  by: string;
  department: string;
  reason: string;
  changes: { field: string; label: string; from: string; to: string }[];
}

/** What `accounts-edit` takes: only what changes is sent. */
export interface AccountsEditBody {
  reason: string;
  client?: Partial<Pick<Client, 'legalName' | 'gst' | 'address' | 'accountsEmail' | 'pocs'>>;
  billingCompany?: string;
  pos?: { id: string; number?: string; date?: string; fileUrl?: string; filename?: string }[];
  invoices?: { id: string; dueDate: string }[];
  expectedUpdatedAt?: string;
}

/** One side of a merge as the preview shows it (the budget only for those who may see it). */
export interface MergeSide {
  id: string;
  name: string;
  status: string;
  clientName: string;
  gst: string;
  billingCompany: string;
  bdOwnerName: string;
  totalAmount: number;
  budget?: number;
  bills: number;
  requests: number;
  pis: number;
  pos: number;
  milestones: number;
  updatedAt: string;
}
/** A request on the folded project not raised yet: it moves unless dropped; `repeats` says when it looks like a kept bill. */
export interface MergeRequest {
  id: string;
  kind: 'bill' | 'pi';
  label: string;
  amount: number;
  requestedBy: string;
  requestedAt: string;
  repeats: string;
}
/** `add`: more work for the same client; `used`: the same work entered twice (only what was billed comes over). */
export type MergeBilling = 'add' | 'used';
export interface MergePreview {
  keep: MergeSide;
  fold: MergeSide;
  billing: MergeBilling;
  canMerge: boolean;
  refusals: string[];
  warnings: string[];
  lines: string[];
  requests: MergeRequest[];
  contractAfter: number;
  keepUpdatedAt: string;
  foldUpdatedAt: string;
}

/** Another bill a UTR is already recorded on: the server asks before taking it again (2 Nov 2026). */
export interface SharedUtrBill {
  projectId: string;
  projectName: string;
  invoiceId: string;
  number: string;
  amount: number;
  paymentDate: string;
}

/** What recording a payment came to: done, a question about a UTR already on other bills, or refused (null). */
export type RecordPaymentResult = { ok: true } | { sharedUtr: { utr: string; bills: SharedUtrBill[] } } | null;

export interface ProjectChangeRequest {
  id: string;
  projectId: string;
  projectName: string;
  /** `budget` = "Budget only": the expense budget and its lines, nothing else (1 Nov 2026). */
  type: 'increase' | 'reduction' | 'rebalance' | 'other' | 'budget';
  reason: string;
  poNumber?: string;
  poFileUrl?: string;
  status: 'requested' | 'applied' | 'rejected';
  before?: any;
  after?: any;
  proposed?: any;
  changes?: { field: string; from: string; to: string }[];
  requestedBy?: string;
  requestedById?: string;
  requestedAt?: string;
  decidedBy?: string;
  decidedAt?: string;
  decisionNote?: string;
  /** Only an Admin decides it (18 Oct 2026): a business head proposed it, the project is the directors', or the head cannot see the budget it changes. */
  needsAdmin?: boolean;
  needsAdminReason?: 'head' | 'directors' | 'budget' | '';
  /** Set by the server for the person reading it: whether they may approve or reject it now (1 Nov 2026). */
  canDecide?: boolean;
  /** An Admin's correction after the PO: the reason they gave, and who they are (1 Nov 2026). */
  poLockReason?: string;
  poLockBy?: string;
}

/**
 * A comment on a project (30 Oct 2026): anyone who can see the project writes one (a discrepancy found
 * against the books, a question); Admins and Accounts resolve it with a note or reopen it. `unseen`
 * comes with the thread and says the comment moved (posted, resolved, reopened) since the reader last
 * opened the project's comments.
 */
export interface ProjectComment {
  id: string;
  projectId: string;
  text: string;
  authorId: string;
  authorName: string;
  authorDepartment: string;
  createdAt: string;
  open: boolean;
  resolvedAt: string;
  resolvedBy: string;
  resolvedByName: string;
  resolution: string;
  reopenedAt?: string;
  reopenedByName?: string;
  lastActivity?: 'posted' | 'resolved' | 'reopened';
  lastActivityAt?: string;
  unseen?: boolean;
}
/** Per project, for the cards: comments in all, still open, and new to the signed-in person. */
export interface ProjectCommentCounts { total: number; open: number; unseen: number; }
/** The server's limit on one comment (and on a resolution note). */
export const PROJECT_COMMENT_MAX_LENGTH = 2000;

export interface CreditNoteRequestSummary {
  noteId: string;
  status: 'requested' | 'approved' | 'rejected' | 'issued' | 'cancelled';
  amount: number;
  reason?: string;
  requestedBy?: string;
  requestedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectReason?: string;
  issuedBy?: string;
  issuedAt?: string;
  noteNumber?: string;
  fileUrl?: string;
}

/** Money received against an invoice so far (recorded payments, else the full amount of a paid invoice). */
export function invoicePaidAmount(inv: Partial<InvoiceRecord> | null | undefined): number {
  if (!inv) return 0;
  if (typeof inv.paidAmount === 'number' && inv.paidAmount > 0) return inv.paidAmount;
  if (Array.isArray(inv.payments) && inv.payments.length) return inv.payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  return inv.status === 'paid' ? (Number(inv.amount) || 0) : 0;
}

/** GST on an invoice: 0 when raised without GST (`gstRate` 0), else the stored `gstAmount` (18% of the amount for older records). */
export function invoiceGstTotal(inv: Partial<InvoiceRecord> | null | undefined): number {
  if (!inv) return 0;
  if (inv.gstRate !== undefined && inv.gstRate !== null && Number(inv.gstRate) === 0) return 0;
  const gst = Number(inv.gstAmount);
  return gst > 0 ? gst : calcGstAmount(Number(inv.amount) || 0);
}

/**
 * A bill whose GST is still to be marked deposited (2 Nov 2026, the server's `gstStillToMark`): raised, sent,
 * part-paid or paid, carrying GST, not marked paid — whether or not the client has paid. Accounts may mark
 * it paid on any such bill; GST is due on the 20th of the month after the invoice.
 */
export function gstStillToMark(inv: Partial<InvoiceRecord> | null | undefined): boolean {
  return !!inv && ['raised', 'sent', 'part_paid', 'paid'].includes(String(inv.status)) && inv.gstStatus !== 'paid' && invoiceGstTotal(inv) > 0;
}

/** A payment recorded in Link carries who recorded it; the ones the opening data brought from the books do not. */
function isLinkPayment(p: Partial<PaymentRecord> | null | undefined): boolean {
  return !!String(p?.recordedById ?? '').trim();
}

/** What a client still owes on an invoice: base and GST (both ≥ 0) and their sum. */
export interface InvoiceDue { base: number; gst: number; open: number }

/**
 * Money to collect = base + GST (Manish, 30 Oct 2026; it was the base only before). The same rule as
 * the server's `invoiceDue`: every open bill owes its base and its GST, less credit notes (before GST —
 * each also takes its share of the GST off) and less what came in. A payment settles the base first,
 * then the GST, with the TDS the client deducted; a "GST only" receipt settles the GST alone. A bill
 * from the books starts from Tally's total due (`booksDue`, else `balanceAmount`) with the GST owed
 * last. An invoice older payments had marked paid stays settled.
 */
export function invoiceDue(inv: Partial<InvoiceRecord> | null | undefined): InvoiceDue {
  const zero: InvoiceDue = { base: 0, gst: 0, open: 0 };
  if (!inv) return zero;
  const status = String(inv.status || '');
  if (status === 'credited' || status === 'cancelled') return zero;
  const amount = Number(inv.amount) || 0;
  const gstTotal = invoiceGstTotal(inv);
  const credited = Number(inv.creditedAmount) || 0;
  const gstShare = (b: number) => (amount > 0 && gstTotal > 0 ? (b * gstTotal) / amount : 0);
  const payments: Partial<PaymentRecord>[] = Array.isArray(inv.payments) ? inv.payments : [];
  const booksFrozen = inv.booksDue !== undefined && inv.booksDue !== null && (inv.booksDue as unknown) !== '';
  const fromBooks = !!inv.imported && (booksFrozen || !payments.some(isLinkPayment));
  let base: number;
  let gst: number;
  let applied: Partial<PaymentRecord>[];
  if (fromBooks) {
    const due0 = Math.max(0, Number(booksFrozen ? inv.booksDue : inv.balanceAmount) || 0);
    const credited0 = Number(inv.booksCredited ?? credited) || 0;
    gst = Math.min(Math.max(0, gstTotal - gstShare(credited0)), due0);
    base = due0 - gst;
    const later = Math.max(0, credited - credited0);
    base -= later;
    gst -= gstShare(later);
    applied = payments.filter(isLinkPayment);
  } else {
    base = amount - credited;
    gst = gstTotal - gstShare(credited);
    applied = payments.length ? payments : Number(inv.paidAmount) > 0 ? [{ amount: Number(inv.paidAmount) }] : [];
  }
  let unsplit = false;
  for (const p of applied) {
    if (p?.base !== undefined && p?.base !== null) {
      base -= Number(p.base) || 0;
      gst -= Number(p.gst) || 0;
      continue;
    }
    unsplit = true;
    const settles = (Number(p?.amount) || 0) + (Number(p?.tdsAmount) || 0);
    const toBase = Math.min(Math.max(0, base), settles);
    base -= toBase;
    gst -= settles - toBase;
  }
  if (!fromBooks && status === 'paid' && (unsplit || !applied.length)) return zero;
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const b = Math.max(0, r2(base));
  const g = Math.max(0, r2(gst));
  return { base: b, gst: g, open: r2(b + g) };
}

/** What is still owed on an invoice: base + GST (30 Oct 2026). */
export function invoiceBalance(inv: Partial<InvoiceRecord> | null | undefined): number {
  if (!inv) return 0;
  if (inv.status === 'paid' || inv.status === 'credited') return 0;
  return invoiceDue(inv).open;
}

/** Base (before GST) settled on a raised invoice: its value net of credit notes less the base still owed — comparable with "billed" and the contract. */
export function invoiceBaseCollected(inv: Partial<InvoiceRecord> | null | undefined): number {
  const status = String(inv?.status || '');
  if (!inv || status === 'pending' || status === 'cancelled') return 0;
  return Math.max(0, Math.round((invoiceNetAmount(inv) - invoiceDue(inv).base) * 100) / 100);
}

/** Invoice value net of issued credit notes. */
export function invoiceNetAmount(inv: Partial<InvoiceRecord> | null | undefined): number {
  if (!inv) return 0;
  return Math.max(0, Math.round(((Number(inv.amount) || 0) - (Number(inv.creditedAmount) || 0)) * 100) / 100);
}

/**
 * "Fully collected" (1 Nov 2026): what the projects page's "Hide fully collected" switch hides. The one
 * place this is decided. A project is fully collected only when all of these hold — in any doubt it stays
 * visible:
 * - it is not a pipeline deal, and its contract value is above 0;
 * - it is billed in full: its raised invoices, net of credit notes, reach the contract value (within ₹1 per
 *   milestone, the rounding the server allows between milestones and the contract);
 * - no invoice request is still waiting with Accounts;
 * - nothing is left to collect: every raised invoice owes no base and no GST (`invoiceBalance` — after
 *   payments, TDS and credit notes, the server's `invoiceDue` rule).
 */
export function isProjectFullyCollected(p: { status?: string; totalAmount?: number; milestones?: unknown[]; invoices?: Partial<InvoiceRecord>[] } | null | undefined): boolean {
  if (!p || p.status === 'pipeline') return false;
  const contract = Number(p.totalAmount) || 0;
  if (contract <= 0) return false;
  const invoices = Array.isArray(p.invoices) ? p.invoices : [];
  if (invoices.some(i => !i?.status || i.status === 'pending')) return false;
  const raised = invoices.filter(i => (i.status as string) !== 'cancelled');
  const billed = raised.reduce((s, i) => s + invoiceNetAmount(i), 0);
  const slack = Math.max(1, Array.isArray(p.milestones) ? p.milestones.length : 0);
  if (billed < contract - slack) return false;
  return raised.every(i => invoiceBalance(i) === 0);
}

/** Server-computed access flags for the logged-in user (GET finance/access/me). */
export interface FinanceMe {
  isBusinessHead: boolean;
  canApproveScopeChange: boolean;
  isScopeAdmin: boolean;
  canApproveCreditNotes: boolean;
  canApproveVendorRequests: boolean;
  canSelectVendorQuotes: boolean;
  /** Confirm or drop a pipeline deal and group projects (the server's `canManageProjects`, 1 Nov 2026). */
  canManageProjects?: boolean;
  /** Raises purchase requests: the HR head, the BD team and Admins — Accounts none (founder, 3 and 5 Nov 2026). */
  canRaisePurchase?: boolean;
  /** …on the company budget: the HR head and Admins (founder, 5 Nov 2026). */
  canRaiseCompanyPurchase?: boolean;
  /** …on a project's budget: the BD team and Admins. */
  canRaiseProjectPurchase?: boolean;
  /** "+ Payment request" on the Vendors page: everyone but the BD team (founder, 4 Nov 2026). */
  canRaiseVendorRequest?: boolean;
  /** …and there only company spends, on their department's route: everyone but Admins and Accounts (founder, 4 Nov 2026). */
  vendorRequestInternalOnly?: boolean;
}

export interface InvoiceRecord {
  id: string;
  /** 18 (default) or 0 for an invoice raised without GST (25 Oct 2026). */
  gstRate?: number;
  /** Loaded from the directors' dashboard (opening data, 25 Oct 2026), not raised in Link. */
  imported?: boolean;
  /** A bill from the books: Tally's total due (GST included) and its credit notes, frozen when Link first records money on it (30 Oct 2026). */
  booksDue?: number;
  booksCredited?: number;
  sourceNote?: string;
  /** Merged from the books into a project a BD created (30 Oct 2026): when, and what the merge did with this bill. */
  merged?: boolean;
  mergedAt?: string;
  mergeNote?: string;
  /** E-invoice (25 Oct 2026): the GST portal's IRN and the signed e-invoice; given on raise or attached later by Accounts. */
  irn?: string;
  /** The IRN's date and time ("2026-10-23 18:06:00") when it was read off the e-invoice's QR (2 Nov 2026). */
  irnDate?: string;
  eInvoiceUrl?: string;
  eInvoiceAt?: string;
  eInvoiceBy?: string;
  /** First covered milestone (older rows have only this). */
  milestoneId: string;
  /** Every milestone this invoice covers (17 Oct 2026: one invoice may cover several). */
  milestoneIds?: string[];
  /** Joined milestone labels, set by the server at request time. */
  label?: string;
  number: string;
  /** True when the server's daily retainer job requested it. */
  autoRequested?: boolean;
  /** Draft-invoice check: Accounts send a draft, the BD approves or asks for changes, then it is raised. */
  draft?: InvoiceDraft;
  /** User id of whoever requested the invoice (set by the server). */
  requestedById?: string;
  amount: number;
  status: 'pending' | 'raised' | 'sent' | 'part_paid' | 'paid' | 'credited' | 'overdue';
  // partial payments
  payments?: PaymentRecord[];
  paidAmount?: number;
  balanceAmount?: number;
  // credit notes (issued total) and the latest request on this invoice
  creditedAmount?: number;
  creditNoteRequest?: CreditNoteRequestSummary;
  // BD ticks when the invoice has gone to the client
  sentToClientAt?: string;
  sentToClientBy?: string;
  // dates
  requestedAt?: string;
  requestedBy?: string;
  raisedAt?: string;
  /** Invoice date as printed (IST YYYY-MM-DD); BD Targets count the bill in this month. */
  raisedDate?: string;
  dueDate: string;
  gstDate?: string;         // auto: 20th of next month after invoice raised
  r1Date?: string;          // auto: 12th of same month as invoice raised
  // legacy compat
  gstPaid?: boolean;
  gstRemaining?: number;
  tdsPaid?: boolean;
  paymentDate?: string;
  // document
  fileUrl?: string;         // required PDF / drive link
  notes?: string;           // optional notes for invoice
  requestedAmount?: number; // partial invoice requested amount
  parentInvoiceId?: string; // parent invoice id if created as remainder of partial raise
  isPartial?: boolean;      // true if partial / remainder invoice
  // payment details
  utr?: string;
  // GST
  gstStatus?: 'paid' | 'not_paid';
  gstAmount?: number;       // always 18% of invoice amount, auto-calculated
  gstPaidDate?: string;
  gstUtr?: string;          // GST payment UTR/transaction number
  // TDS
  tdsStatus?: 'deducted' | 'not_deducted';
  tdsMode?: 'single' | 'multi';  // single = one % auto-calc, multi = manual entries
  tdsPct?: number;          // 2% or 10%, manual
  tdsAmount?: number;       // auto: tdsPct × amount / 100
  tdsEntries?: { pct: number; amount: number }[];  // multi-TDS line items
  tdsReceivedInPortal?: boolean;
  tdsReceivedDate?: string;
  tdsReferenceNumber?: string;  // TDS reference/document number
  /** The TDS the "TDS received" mark covers (2 Nov 2026): a later payment that brings more takes the mark off. */
  tdsReceivedAmount?: number;
  /** Payments Accounts or an Admin took back, with the reason (2 Nov 2026); never counted. */
  reversedPayments?: ReversedPaymentRecord[];
  /** "GST paid" / "TDS received" marks taken back, oldest first (2 Nov 2026). */
  moneyUndos?: MoneyUndo[];
  /** Number or date corrections after the raise, oldest first (2 Nov 2026). */
  corrections?: InvoiceCorrection[];
  /** Due date moved by Accounts or an Admin on the BD's behalf (3 Nov 2026), oldest first. */
  dueDateChanges?: DueDateChange[];
  /** Files replaced after the raise (3 Nov 2026), oldest first. */
  fileHistory?: ReplacedFile[];
}

export interface BudgetLineItem {
  id: string;
  category: string;      // e.g. 'Production', 'Talent', 'Media Buying', 'Software', 'Travel'
  planned: number;
  notes?: string;
}

// ─── Billing Structure v2 (REVENUE side — never conflate with Expense Budget) ─

/** Fixed revenue categories. 'uncategorized' exists only for migrated legacy projects. */
export type BillingCategoryKey = 'software' | 'content' | 'hardware' | 'amc' | 'uncategorized';

export const BILLING_CATEGORIES: { key: BillingCategoryKey; name: string; sub: string; hw?: boolean }[] = [
  { key: 'software', name: 'Software', sub: 'Apps, CMS, kiosk builds, integrations' },
  { key: 'content', name: 'Content', sub: 'Films, motion, copy, design assets' },
  { key: 'hardware', name: 'Hardware', sub: 'Physical supply — carries HSN & E-Way rules', hw: true },
  { key: 'amc', name: 'AMC', sub: 'Annual maintenance & support' },
];

export interface BillingCategory {
  key: BillingCategoryKey;
  pct: number;            // % of contract value — all categories must sum to 100
  amount: number;         // derived: totalAmount × pct / 100
  hsnCode?: string;       // Hardware only — attaches at CATEGORY level
  milestones: Milestone[];  // must sum to this category's pct
}

export const PROJECT_TYPES = ['V-Retail', 'V-Estate', 'MarTech', 'Data Vault', 'Event', 'Experience Center', 'Service', 'Other'] as const;
export type ProjectType = typeof PROJECT_TYPES[number];

/** Departments that can always see a project's Expense Budget regardless of budgetVisibleTo */
export const BUDGET_ALWAYS_VISIBLE = ['ADMIN'];
/** A project with no share of its own: never "My team" (31 Oct 2026, Manish) — the edit form ticked it on every save. */
export const BUDGET_DEFAULT_VISIBLE = ['ADMIN', 'ACCOUNTS'];

export interface FinanceProject {
  id: string;
  name: string;
  bdOwner: string;
  bdOwnerName: string;
  client: Client;
  totalAmount: number;
  currency: string;
  /** 'pipeline' = a deal still being nurtured (17 Oct 2026): estimates only, nothing billable, own tab. */
  status: 'pipeline' | 'active' | 'on_hold' | 'completed' | 'cancelled';
  /** Pipeline deals: when the BD expects to close; cleared on confirmation. */
  expectedCloseOn?: string;
  confirmedAt?: string;
  confirmedBy?: string;
  droppedReason?: string;
  /** Project group (two billing entities, one job) — hygiene only. */
  groupId?: string;
  groupName?: string;
  hasHardware?: boolean;
  clientLogoUrl?: string;
  createdAt: string;
  /** When the server last saved the project; an edit form sends it back so a stale save is refused (2 Nov 2026). */
  updatedAt?: string;
  milestones: Milestone[];
  pis: PIRecord[];
  pos: PORecord[];
  poNotApplicable?: boolean;
  invoices: InvoiceRecord[];
  description?: string;
  notes?: string;
  /** The lead this deal came from (25 Oct 2026): id in the lead tracker plus its card; the lead shows the deal back. */
  crmLeadId?: string;
  crmLeadName?: string;
  crmLeadProject?: string;
  crmLeadCompany?: string;
  /** Opening data (25 Oct 2026): where the project was loaded from (`iic-ops`), which export, when; empty for projects added by hand. */
  importedFrom?: string;
  importRef?: string;
  importedAt?: string;
  /** The Admin / Accounts check of an imported project (27 Oct 2026): until set, the card wears the "From the books" marker. */
  importCheckedAt?: string;
  importCheckedBy?: string;
  /** The books were merged into this BD-created project (30 Oct 2026): when, by whom, and what changed, line by line. */
  importMerged?: boolean;
  mergedAt?: string;
  mergedBy?: string;
  mergeSummary?: string[];
  // ── v2 ──
  billingStructure?: BillingCategory[];  // revenue split; `milestones` above is a flattened mirror of this tree
  /** Retainers; their periods are milestones with `seriesId`. */
  recurringSeries?: RetainerSeries[];
  /** Client quotations (first one required at creation). */
  quotations?: ProjectQuotation[];
  projectType?: ProjectType;
  /** Billing entity the invoices are raised from: IIC (default) | SPS | LLP. */
  billingCompany?: 'IIC' | 'SPS' | 'LLP';
  bdTeam?: string[];                     // user ids who worked the close (excludes bdOwner); visibility only, no target credit
  /** BD targets: the day the deal was won (YYYY-MM-DD). Defaults to the creation day. */
  closedOn?: string;
  /** BD targets: Admin keeps demo / internal projects out of every figure. */
  targetsExcluded?: boolean;
  /** BD targets: 'directors' = closed by a BD on a director's behalf; counts for the company, never for the BD. */
  targetCredit?: 'owner' | 'directors';
  /** Share (0–100) of BD-target credit that goes to the directors; `targetCredit` is derived from it (100 → 'directors'). */
  directorsSharePct?: number;
  budgetVisibleTo?: string[];            // departments allowed to see the Expense Budget (ADMIN always)
  // Budget tracking (EXPENSE side)
  budget?: number;                    // total expense budget for this project
  budgetLineItems?: BudgetLineItem[]; // category-wise planned spend
  /** The server left the budget out because this person may not see it (1 Nov 2026): show and send no budget fields. */
  budgetHidden?: boolean;
  /**
   * Project hygiene (3 Nov 2026): how many projects looked like this one when it was created, what they shared, and
   * why the creator went ahead. The ids (of those the creator could open) come to Accounts and Admins only.
   */
  lookAlikes?: { count: number; kinds: string[]; reason: string; at: string; by: string; projectIds?: string[]; hiddenCount?: number };
  /** Details Accounts or an Admin asked the BD to fix. */
  fixAsks?: FixAsk[];
  /** Changes Accounts or an Admin made on the BD's behalf. */
  accountsEdits?: AccountsEdit[];
  /** Projects folded into this one. */
  mergedProjects?: { projectId: string; name: string; at: string; by: string; reason: string; billing: MergeBilling; summary: string[] }[];
  /** On an archived project folded into another: where its records are now. */
  mergedInto?: string;
  mergedIntoName?: string;
  mergedIntoAt?: string;
}

export interface ProjectDraft {
  id?: string;
  _id?: string;
  name?: string;
  client?: Partial<Client>;
  totalAmount?: number;
  currency?: string;
  status?: 'pipeline' | 'active' | 'on_hold' | 'completed' | 'cancelled';
  expectedCloseOn?: string;
  clientLogoUrl?: string;
  quotationUrl?: string;
  quotations?: any[];
  retainer?: any;
  billingStructure?: BillingCategory[];
  projectType?: string;
  bdOwner?: string;
  bdOwnerName?: string;
  bdTeam?: string[];
  budgetVisibleTo?: string[];
  budget?: number;
  budgetLineItems?: BudgetLineItem[];
  description?: string;
  notes?: string;
  crmLeadId?: string;
  crmLeadName?: string;
  crmLeadProject?: string;
  crmLeadCompany?: string;
  createdAt?: string;
  updatedAt?: string;
  step?: number;
  [key: string]: any;
}

/** A project group: clients sometimes split one job across two legal billing entities (17 Oct 2026). */
export interface ProjectGroup {
  id: string;
  name: string;
  note?: string;
  /** The group's projects the signed-in person may see (1 Nov 2026: never the others, never archived ones). */
  projectIds: string[];
  projects: {
    id: string; name: string; clientName: string; status: string; totalAmount: number; bdOwnerName: string;
    currency?: string; closedOn?: string; createdAt?: string;
  }[];
  /** Every live project in the group, and how many of them the signed-in person may not see. */
  memberCount?: number;
  hiddenCount?: number;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** One of the signed-in person's pinned projects (GET finance/project-pins, 1 Nov 2026). */
export interface ProjectPin { projectId: string; pinnedAt: string; }

/** Plan feed row shapes (GET /finance/plan). */
export interface PlanMilestone {
  projectId: string; projectName: string; clientName: string; bdOwnerName: string; projectType: string; pipeline: boolean; onHold: boolean; currency: string;
  milestoneId: string; label: string; amount: number; estDate: string; isHardware: boolean; retainer: boolean; status: 'open' | 'requested' | 'raised' | 'paid'; invoiceId: string;
}
export interface PlanInvoiceDue {
  projectId: string; projectName: string; clientName: string; bdOwnerName: string; pipeline: boolean; currency: string;
  invoiceId: string; number: string; label: string; amount: number; balance: number; dueDate: string; status: string; overdue: boolean;
  /** The bill's value with its GST, and the base / GST still owed (30 Oct 2026); `balance` is base + GST. */
  gross?: number; baseDue?: number; gstDue?: number;
}
export interface PlanFeed {
  from: string;
  today: string;
  months: { month: string; label: string; milestones: PlanMilestone[]; invoicesDue: PlanInvoiceDue[]; totals: { milestones: number; milestonesAmount: number; softwareAmount: number; hardwareAmount: number; pipelineAmount: number; stillToInvoice: number; invoicesDue: number; invoicesDueAmount: number } }[];
}

/**
 * GST by invoice month (per founder, 2 Nov 2026): a bill's GST counts in its invoice month and is due on the
 * 20th of the next month whether or not the client has paid; credit notes count in the month they are issued.
 * Served by `GET finance/gst/deposit` (Accounts and Admins only).
 */
export interface GstDepositMonth {
  month: string;
  label: string;
  noticeFrom: string;
  dueDate: string;
  gst: number;
  bills: number;
  creditNotesGst: number;
  creditNotes: number;
  net: number;
  markedGst: number;
  markedBills: number;
  toMarkGst: number;
  toMarkBills: number;
  overdue: boolean;
}
export interface GstDeposit {
  today: string;
  /** Last month's bills until the 20th, then this month's. */
  nowDue: GstDepositMonth;
  /** The month still being billed, when it is not the one now due. */
  running: GstDepositMonth | null;
  /** Earlier months past their 20th with bills still not marked paid. */
  overdue: GstDepositMonth[];
  /** The last six months, newest first. */
  months: GstDepositMonth[];
}
/** One bill whose client still owes GST (`GET finance/gst/with-clients`, Accounts and Admins only). */
export interface GstWithClientRow {
  projectId: string;
  projectName: string;
  clientName: string;
  bdOwnerName: string;
  invoiceId: string;
  number: string;
  status: string;
  invoiceDate: string;
  invoiceMonth: string;
  daysSinceInvoice: number;
  ageBand: '0-30' | '31-60' | '61-90' | '90+';
  billGst: number;
  gstOwed: number;
  baseOwed: number;
  dueDate: string;
}
export interface GstWithClients {
  today: string;
  rows: GstWithClientRow[];
  total: { gst: number; bills: number };
  byMonth: { month: string; label: string; gst: number; bills: number }[];
  byAge: { band: string; label: string; gst: number; bills: number }[];
}

export interface FinanceVendor {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  category: string;
  gst?: string;
  address?: string;
  bankName?: string;
  bankAccount?: string;
  bankIfsc?: string;
  createdAt: string;
  createdBy: string;
  // ── Vendor onboarding (11 Oct 2026) ──
  /** small: ₹10,000 a month cap (GST included), minimal details; general: GSTIN, PAN, address + papers, bigger payments. */
  vendorType?: 'small' | 'general';
  gstin?: string;
  pan?: string;
  bank?: { accountName?: string; accountNumber?: string; ifsc?: string; bankName?: string };
  upiId?: string;
  preferredMode?: VendorPaymentMode;
  /** Named on a quote before its papers were in (24 Oct 2026); nothing is paid until Accounts / Admin complete it. */
  registrationPending?: boolean;
  documents?: { gstCertificateUrl?: string; panCardUrl?: string; cancelledChequeUrl?: string; msmeUrl?: string };
  verified?: boolean;
  monthlyCap?: number | null;
  bankChangedAt?: string;
  bankChangedBy?: string;
}

export type VendorPaymentMode = 'bank' | 'upi' | 'cash' | 'cheque';
export const VENDOR_PAYMENT_MODES: { value: VendorPaymentMode; label: string; refLabel: string }[] = [
  { value: 'bank', label: 'Bank transfer (NEFT / RTGS / IMPS)', refLabel: 'UTR' },
  { value: 'upi', label: 'UPI / GPay', refLabel: 'UPI transaction ID' },
  { value: 'cash', label: 'Cash', refLabel: 'Voucher no. (optional)' },
  { value: 'cheque', label: 'Cheque', refLabel: 'Cheque number' },
];
/** Ready-made vendor categories (anything else can be typed). */
export const VENDOR_CATEGORIES = [
  'Carpenter', 'Transporter', 'Printing', 'Fabrication', 'Electrical', 'LED / Screens', 'AV / Hardware', 'Event setup',
  'Furniture rental', 'Manpower', 'Catering', 'Venue', 'Photography / Video', 'Talent / Artist', 'Freelancer',
  'Software / SaaS', 'Logistics / Courier', 'Stationery', 'Travel', 'Other',
];
export const SMALL_VENDOR_MONTHLY_CAP = 10000;
/** The "Small / urgent" flow takes one payment of up to this much before GST — ₹10,000 + GST (link-backend `SMALL_URGENT_FLOW_CAP`, founder 1 Nov 2026; before GST since 3 Nov). */
export const SMALL_URGENT_FLOW_ID = 'flow_small';
export const SMALL_URGENT_FLOW_CAP = 10000;
/** HR and the HR head above SMALL_URGENT_FLOW_CAP here: an Admin approves it in person (link-backend `ADMIN_ABOVE_SMALL_CAP_DEPARTMENTS`, founder 1 Nov 2026). */
export const ADMIN_ABOVE_SMALL_CAP_DEPARTMENTS: readonly string[] = ['HR', 'HR-HEAD'];
/** Who raises purchase requests: the people who hold the budgets — the HR head, the BD team and Admins; Accounts none (founder, 3 and 5 Nov 2026; the server refuses everyone else). */
export const PURCHASE_RAISER_DEPARTMENTS: readonly string[] = ['HR-HEAD', 'SALES', 'ASSISTANT-SALES', 'ADMIN'];
/** The BD team never uses "+ Payment request": every spend for a project starts as a purchase request from its BD (founder, 3–4 Nov 2026). */
export const PURCHASE_ONLY_DEPARTMENTS: readonly string[] = ['SALES', 'ASSISTANT-SALES'];
/** Admins and Accounts keep the direct payment route and may tag a project; everyone else raises company spends only, on their department's route (founder, 4 Nov 2026). */
export const DIRECT_PAYMENT_DEPARTMENTS: readonly string[] = ['ADMIN', 'ACCOUNTS'];
/**
 * The company-spend routes, one per department group (founder, 4 Nov 2026: "perfectly mapped"), in the order the
 * flows editor lists them; the server names them and says which departments each covers.
 */
export const DEPARTMENT_ROUTE_IDS: readonly string[] = ['flow_internal_hr_head', 'flow_internal_hr', 'flow_internal_it', 'flow_internal'];
/** A department's route steps when the server sends none: IT goes HR → HR head, the HR head's own straight to Accounts, everyone else to the HR head. */
export const departmentRouteDefault = (department: string): string[] =>
  department === 'IT-ADMIN' ? ['HR', 'HR-HEAD'] : department === 'HR-HEAD' ? [] : ['HR-HEAD'];

/** One approval step in an Admin-editable flow (Accounts always pay at the end). */
export interface ApprovalFlowStep { role: string; label: string; addedForCap?: boolean; addedForEmergency?: boolean; addedForAccounts?: boolean; addedForHr?: boolean; addedForInternal?: boolean; addedForBudget?: boolean }
export interface ApprovalFlow {
  id: string;
  name: string;
  steps: ApprovalFlowStep[];
  requesterDepartments: string[];
  defaultFor: string[];
  active?: boolean;
  isDefault?: boolean;
  /** 'internal': a department's company-spend route (4 Nov 2026) — never picked; every company spend raised outside Admins and Accounts follows the route of its raiser's department. */
  kind?: string;
  /** On a department route: above this amount before GST an Admin approves too. */
  adminAbove?: number;
  /** On a department route: the departments it covers; empty = every department without a route of its own. */
  covers?: string[];
  /** The route's defaults, shown until an Admin saves it. */
  notSaved?: boolean;
}
/** The project's budget beside a payment for it: figures only for someone the BD's sharing lets see it (3 Nov 2026). */
export interface VendorProjectBudget { visible: boolean; planned?: number; committed?: number; asked?: number; remaining?: number }
export const APPROVAL_STEP_ROLES: { role: string; label: string }[] = [
  { role: 'BUSINESS_HEAD', label: 'Business head' },
  { role: 'OPERATIONS', label: 'Operations' },
  { role: 'HR-HEAD', label: 'Procurement (HR-Head)' },
  { role: 'HR', label: 'HR' },
  { role: 'SALES', label: 'Sales / BD' },
  { role: 'ADMIN', label: 'Admin (Directors)' },
  { role: 'PROJECT_CREATOR', label: 'Project creator' },
  // First on every Vendors-page request charged to a project unless its flow places it later (server's routeVendorChain, 2 Nov 2026).
  { role: 'PROJECT_BD', label: 'Project BD (or their business head)' },
];

export interface VendorQuote {
  id: string;
  vendorId: string;
  vendorName: string;
  vendorType?: string;
  amount: number;
  gstPct: number;
  gstAmount: number;
  total: number;
  docType: 'quote' | 'pi' | 'invoice' | 'einvoice';
  fileUrl: string;
  note?: string;
  round: number;
  addedBy?: string;
  addedAt?: string;
  /** Vendor flow v2 (24 Oct 2026): this vendor's own payment terms, the change against the previous round, a new vendor's pending registration. */
  terms?: QuoteTerms | null;
  previous?: { quoteId: string; round: number; amount: number; change: number; changePct: number } | null;
  registrationPending?: boolean;
}
export interface QuoteTerms {
  key: string;
  label: string;
  installments: { no: number; pct: number; dueDate?: string; onDelivery: boolean }[];
}
/** Payment terms a quote may carry (mirrors QUOTE_TERMS on the server). */
export const QUOTE_TERMS: { key: string; label: string }[] = [
  { key: 'advance', label: '100% advance' },
  { key: '50_50', label: '50% advance · 50% on delivery' },
  { key: '30_70', label: '30% advance · 70% on delivery' },
  { key: 'on_delivery', label: '100% on delivery' },
  { key: 'net30', label: 'Net 30 after the invoice' },
  { key: 'custom', label: 'Custom installments' },
];
/** The head's three send-backs on the quotes. */
export interface VendorNegotiation {
  id: string;
  kind: 'target_price' | 'another_vendor' | 'change_terms';
  kindLabel: string;
  quoteId: string;
  vendorId: string;
  vendorName: string;
  target: number;
  terms: QuoteTerms | null;
  note: string;
  by: string;
  byId: string;
  at: string;
  resolvedAt: string;
  resolvedBy: string;
  resolvedQuoteId: string;
  resolvedNote: string;
}
export interface VendorPayment {
  id: string;
  kind: 'base' | 'gst';
  amount: number;
  mode: VendorPaymentMode;
  modeLabel?: string;
  reference?: string;
  date: string;
  note?: string;
  gstBasis?: string;
  paidBy?: string;
  paidAt?: string;
  /** Optional screenshot or voucher beside the reference (24 Oct 2026). */
  proofUrl?: string;
  /** TDS Accounts deducted from this base payment; it settles the base with the amount paid (1 Nov 2026). */
  tdsAmount?: number;
  tdsSection?: string;
}

export interface ApprovalStep {
  department: string;     // e.g. 'SALES', 'ADMIN', 'HR-HEAD', 'ACCOUNTS'
  action?: 'approved' | 'rejected';
  note?: string;
  actedBy?: string;
  actedAt?: string;
}

/** Vendor flow v2 (24 Oct 2026): a purchase request — the business team's ask against a budget line, before any vendor. */
export type PurchaseRequestStatus = 'open' | 'quoting' | 'approving' | 'paying' | 'done' | 'withdrawn';
export type PurchaseHandlerKind = 'me' | 'team' | 'procurement' | 'operations' | 'it' | 'anyone';
export const PURCHASE_HANDLER_KINDS: { kind: PurchaseHandlerKind; label: string; hint: string }[] = [
  { kind: 'me', label: 'Me', hint: 'I will collect the quotes myself' },
  { kind: 'team', label: 'Someone in my team', hint: 'Pick the people who will collect quotes' },
  { kind: 'procurement', label: 'Procurement (HR-Head)', hint: 'Procurement collects the quotes' },
  { kind: 'operations', label: 'Operations', hint: 'Operations collects the quotes' },
  { kind: 'it', label: 'IT', hint: 'IT collects the quotes' },
  { kind: 'anyone', label: 'Anyone', hint: 'Whoever picks it up in Purchase requests' },
];
export const PURCHASE_STATUS_LABELS: Record<PurchaseRequestStatus, string> = {
  open: 'Open — quotes wanted',
  quoting: 'Quotes in',
  approving: 'In approval',
  paying: 'Being paid',
  done: 'Done',
  withdrawn: 'Withdrawn',
};
export interface PurchaseBudgetLine {
  id: string;
  category: string;
  notes?: string;
  /** Present only when the reader may see figures. */
  planned?: number;
  committed?: number;
  asked?: number;
  paid?: number;
  remaining?: number;
}
export interface PurchaseRequest {
  id: string;
  title: string;
  details: string;
  attachments: { url: string; name: string }[];
  isGoods: boolean;
  forWhom: 'client' | 'company';
  projectType: 'project' | 'internal';
  projectId: string;
  projectName: string;
  department: string;
  internalCompany: string;
  budgetLineId: string;
  budgetCategory: string;
  /** Absent when `amountsHidden`. */
  estimate?: number;
  neededBy: string;
  handler: { kind: PurchaseHandlerKind; userIds: string[]; names: string[]; department: string };
  status: PurchaseRequestStatus;
  vendorRequestId: string;
  withdrawnReason: string;
  history: { action: string; actedBy: string; actedById: string; department: string; actedAt: string; [k: string]: any }[];
  createdBy: string;
  createdByName: string;
  createdByDepartment: string;
  createdAt?: string;
  updatedAt?: string;
  /** Server-computed on every read. */
  ageDays: number;
  amountsHidden: boolean;
  /** The raiser, while the purchase is open: exactly when the server takes an edit or a withdrawal (1 Nov 2026). */
  canEdit: boolean;
  canWithdraw?: boolean;
  canStart: boolean;
  /** Sent for a decision that is this reader's to make ("Waiting for my decision", 1 Nov 2026). */
  awaitingMyDecision?: boolean;
  line?: PurchaseBudgetLine;
  /** Slice 2 (24 Oct 2026): the head's decision, the delivery record, and — on a deep read — the vendor request behind the purchase and what this reader may do. */
  decisionRequestedAt?: string;
  decidedAt?: string;
  decidedBy?: string;
  decidedByName?: string;
  delivery?: { challanNo: string; scanUrl: string; receivedBy: string; date: string; tag: 'client' | 'company'; note?: string; by?: string; at?: string } | null;
  vendorRequest?: VendorPaymentRequest | null;
  canAddQuote?: boolean;
  canSendForDecision?: boolean;
  canDecide?: boolean;
  canAnswerNegotiation?: boolean;
  canRecordDelivery?: boolean;
  isDecider?: boolean;
  isHandler?: boolean;
}

export interface VendorPaymentRequest {
  id: string;
  vendorId: string;
  vendorName: string;
  projectId?: string;
  projectName?: string;
  projectType?: 'project' | 'internal';  // project or internal/capex
  internalCompany?: string;              // 'ink_in_caps' or 'pixel_studio'
  amount: number;
  description: string;
  // Document uploads (at least one mandatory)
  documentUrl?: string;         // legacy
  quoteUrl?: string;
  piUrl?: string;
  invoiceUrl?: string;
  // GST
  hasGst?: boolean;
  gstPct?: number;
  gstAmount?: number;
  gstUpfront?: boolean;
  // Payment schedule
  paymentSplit?: string;        // '100_advance' | '50_50' | 'custom'
  splitDetails?: { pct: number; amount: number; date: string }[];
  budgetCategory?: string;       // linked budget line item category from project
  /** Vendor flow v2 (24 Oct 2026): the budget line and the purchase request this was started from, when it was. */
  budgetLineId?: string;
  purchaseRequestId?: string;
  neededBy?: string;
  negotiations?: VendorNegotiation[];
  selectedById?: string;
  /** Issued once the request is fully approved (25 Oct 2026): PO/<entity>/<FY>/<sequence>. */
  poNumber?: string;
  poIssuedAt?: string;
  /** The head chose a quote above what the line had left and confirmed it. */
  overBudget?: { amount: number; lineLeft: number; by: string; byId: string; at: string; note?: string } | null;
  currentStepIndex: number;
  // Approval
  approvalFlow: string;         // flow identifier
  approvalChain: any[];         // new: [{ role, label }] steps; older requests: department names
  pendingDepartment?: string;   // which dept needs to act next
  approvalHistory: ApprovalStep[];  // full audit trail
  status: 'pending' | 'awaiting_selection' | 'in_progress' | 'sent_back' | 'approved' | 'base_paid' | 'rejected' | 'payment_released';
  // ── Rebuild (11 Oct 2026): quotes, flows, cap, two-part payment ──
  quotes?: VendorQuote[];
  selectedQuoteId?: string;
  selectedBy?: string;
  selectedAt?: string;
  flowId?: string;
  flowName?: string;
  /** Which of the founder's routes built the chain (2 Nov 2026): 'internal' (HR → HR head → Admin above ₹10,000 before GST), 'project' (the project's BD first) or ''. */
  routing?: 'internal' | 'project' | '';
  flowSteps?: ApprovalFlowStep[];
  isGoods?: boolean;
  deliveryChallanUrl?: string;
  capApplies?: boolean;
  capKind?: 'small_vendor' | 'no_pi_invoice' | null;
  capOverride?: { usedThisMonth: number; afterThis: number; cap: number; reason: string } | null;
  sentBack?: { stepIndex: number; label: string; by: string; at: string; note: string; resolvedAt?: string; afterApproval?: boolean; backToQuotes?: boolean };
  payments?: VendorPayment[];
  basePaid?: number;
  /** TDS deducted from the base; the base is settled by basePaid + baseTds (1 Nov 2026). */
  baseTds?: number;
  gstPaid?: number;
  /** On a purchase page: the figures are for the purchase's budget people, its handler, deciders and Accounts (1 Nov 2026). */
  amountsHidden?: boolean;
  /** "Small / urgent" above its cap (founder, 1 Nov 2026): the reason given, and an Admin in person approves it. */
  emergencyReason?: string;
  /** `total` with GST; `amount` before GST, what the cap counts (since 3 Nov 2026; older emergencies have no `amount`). */
  emergency?: { reason: string; total: number; amount?: number; cap: number } | null;
  /** The Admin step is for an Admin in person (an emergency, or a request Accounts raised). */
  adminOnly?: boolean;
  /** A Vendors-page payment over what its project's budget had left (3 Nov 2026): the reason given; an Admin approves it. */
  overBudgetReason?: string;
  overProjectBudget?: { reason: string; at: string } | null;
  /** Accounts confirmed the vendor's paper is for this project before the first payment (3 Nov 2026). */
  paperCheckedAt?: string;
  paperCheckedBy?: string;
  /** Internal capex: the Admin line the route had when the chain was built. */
  internalAdminAbove?: number;
  /** What the current user should do: select | approve | resubmit | pay ('' = nothing). Set by the server. */
  myAction?: string;
  /** Installments (% of the base amount, each with a pay-on date) + when GST goes. */
  paymentSchedule?: { split: string; installments: { no: number; pct: number; dueDate: string; onDelivery?: boolean }[]; gstUpfront?: boolean; gstDueDate?: string; termsKey?: string; termsLabel?: string; builtFromQuoteId?: string };
  // Legacy compat
  bdLeadNote?: string;
  adminNote?: string;
  directorNote?: string;
  paymentMode?: string;
  paymentUtr?: string;
  /** Paid without the vendor's tax invoice (e.g. an advance); cleared when it is added. */
  vendorInvoicePending?: boolean;
  receivedProofs?: { id: string; url: string; fileName?: string; note?: string; receivedDate: string; uploadedBy: string; uploadedById?: string; uploadedAt: string }[];
  receivedAt?: string;
  receivedDate?: string;
  paymentDate?: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  createdByDepartment?: string;
}

export interface DashboardCounts {
  invoices?: number;
  overdue?: number;
  gstPending?: number;
  pending?: number;
  requested?: number;
  activeProjects?: number;
  totalProjects?: number;
  totalVendors?: number;
  [key: string]: any;
}

export interface DashboardSummaryResponse {
  totalInvoiced?: number;
  totalCollected?: number;
  totalOutstanding?: number;
  totalRequested?: number;
  collectionRate?: number;
  totalPending?: number;
  totalOverdue?: number;
  totalGstPending?: number;
  totalTdsPending?: number;
  /** Every open bill, whatever the period: base and GST still owed (30 Oct 2026). */
  toCollect?: ToCollect;
  counts?: DashboardCounts;
  totalProjects?: number;
  activeProjects?: number;
  totalVendors?: number;
  expenses?: any;
  [key: string]: any;
}

/** Money still to collect across every open bill: base, GST, their sum and how many bills (30 Oct 2026). */
export interface ToCollect { base: number; gst: number; total: number; invoices: number }

export interface DashboardStats {
  /** Money that came in during the period, whichever bill it paid (server summary, 30 Oct 2026); absent from the offline fallback. */
  totalReceived?: number;
  /** Every open bill, whatever the period: base + GST still owed (30 Oct 2026). */
  toCollect?: ToCollect;
  totalInvoiced: number;
  totalCollected: number;
  totalOutstanding?: number;
  totalRequested?: number;
  totalPending: number;
  totalOverdue: number;
  totalGstPending: number;
  totalTdsPending: number;
  totalProjects: number;
  activeProjects: number;
  totalVendors: number;
  collectionRate: number;
  counts?: DashboardCounts;
  recentInvoices: (InvoiceRecord & { projectName: string })[];
  recentProjects: FinanceProject[];
  pendingActions: PendingAction[];
}

export interface PendingAction {
  type: 'raise_pi' | 'send_pi_draft' | 'check_pi_draft' | 'raise_invoice' | 'mark_paid' | 'approve_vendor' | 'decide_purchase' | 'follow_up' | 'upload_po' | 'send_invoice' | 'approve_credit_note' | 'issue_credit_note' | 'approve_scope_change' | 'send_draft' | 'check_draft' | 'set_cbe' | 'resolve_carried' | 'cbe_missing' | 'lock_cbe' | 'add_irn';
  /** A purchase (or the payment request behind one): the action opens the purchase page. */
  purchaseRequestId?: string;
  /** BD targets actions: the month and person they point at. */
  month?: string;
  userId?: string;
  projectId?: string;
  projectName?: string;
  piId?: string;
  invoiceId?: string;
  requestId?: string;
  description: string;
  requestedBy?: string;
  requestedAt?: string;
  amount?: number;
  isRecurring?: boolean;
  recurringLabel?: string;
}

export interface RecurringInvoice {
  id: string;
  projectId: string;
  projectName: string;
  clientName: string;
  label: string;           // e.g. "Monthly Retainer"
  amount: number;
  frequency: 'weekly' | 'monthly' | 'quarterly' | 'half_yearly' | 'yearly';
  nextDueDate: string;     // ISO date
  startDate: string;
  endDate?: string;        // optional end date
  endAfterCount?: number;  // optional: stop after N generations
  enabled: boolean;
  createdAt: string;
  createdBy: string;
  lastGenerated?: string;
  generatedCount: number;
  description?: string;
}

export interface ReminderRecord {
  id: string;
  invoiceId: string;
  sentAt: string;
  sentBy: string;
  method: 'email' | 'notification' | 'manual';
  note?: string;
  daysOverdue?: number;
}

export interface ReminderConfig {
  invoiceId: string;
  projectName: string;
  enabled: boolean;
  frequencyDays: number[];    // e.g. [3, 7, 14] — send at 3, 7, 14 days overdue
  repeatEveryDays?: number;   // e.g. 7 = repeat every 7 days after thresholds
  lastAutoSentAt?: string;
  lastAutoSentDays?: number;
}

export interface OverdueInvoice {
  id: string;
  invoiceId?: string;
  projectId?: string;
  projectName: string;
  number?: string;
  status?: string;
  canRemind?: boolean;
  amount: number;
  dueDate?: string;
  daysOverdue: number;
  clientEmail?: string;
  clientPocEmails?: string[];
  reminderCount?: number;
  lastReminderAt?: string | null;
  lastReminderBy?: string | null;
  lastReminderTrigger?: string | null;
  config?: ReminderConfig | null;
  nextReminderDate?: string | null;
  /** The 10:00 job found a client reminder due and left it for a person to send (2 Nov 2026). */
  reminderReady?: { at: string; label: string; days: number } | null;
  /** A reminder already went to the client today; the next one can go tomorrow. */
  remindedToday?: { at: string; by: string } | null;
}

/** What "Send reminder" would e-mail the client (GET /finance/reminder-preview/:invoiceId). */
export interface ReminderPreview {
  invoiceId: string;
  projectId: string;
  projectName: string;
  number: string;
  to: string;
  subject: string;
  html: string;
  /** The e-mail's paragraphs as the client reads them. */
  lines: string[];
  amount: number;
  gstAmount: number;
  daysOverdue: number;
  reminderCount: number;
  lastReminderAt: string | null;
  lastReminderBy: string | null;
  lastReminderTrigger: string | null;
  remindedToday: { at: string; by: string } | null;
  reminderReady: { at: string; label: string; days: number } | null;
  canSend: boolean;
  cannotSendReason: string | null;
}

export interface TaggedEmployee {
  userId: string;
  username: string;
  department: string;
  approved: boolean;
  approvedAt?: string;
}

export interface ExpenseApprovalStep {
  step: string;       // 'hr_procurement' | 'accounts' | 'admin'
  status: 'pending' | 'approved' | 'rejected';
  actedBy?: string;
  actedAt?: string;
  note?: string;
}

export interface Expense {
  id: string;
  date: string;
  category: 'travel' | 'meals' | 'software' | 'office' | 'other';
  amount: number;
  description: string;
  receiptUrl?: string;
  // Project linkage (optional — for tracking, doesn't affect budget)
  projectId?: string;
  projectName?: string;
  // Submitter
  submittedBy: string;
  submittedByName?: string;
  submittedByDepartment: string;
  submittedAt: string;
  // Multi-step approval: HR → ACCOUNTS → ADMIN
  approvalSteps?: ExpenseApprovalStep[];
  currentApprovalStep?: string;
  status: 'pending' | 'peer_approval' | 'in_approval' | 'approved' | 'rejected' | 'reimbursed';
  // Tagged employees (shared expenses)
  taggedEmployees?: TaggedEmployee[];
  allPeersApproved?: boolean;
  // Category limit
  categoryLimit?: number;
  exceedsLimit?: boolean;
  // Legacy / simple approval
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  reimbursedAt?: string;
  reimbursedBy?: string;
  reimbursementUtr?: string;
  reimbursementBatchMonth?: string;
}

export interface ExpenseCategoryLimit {
  category: string;
  maxAmount: number;
  description?: string;
}

export interface BudgetStatus {
  projectId: string;
  projectName: string;
  budget: number;
  totalSpent: number;
  totalExpenses: number;
  remaining: number;
  pct: number;
  lineItems: (BudgetLineItem & { actual: number; remaining: number; pct: number })[];
  vendorPayments: { id: string; vendorName: string; amount: number; budgetCategory?: string; date?: string; description: string }[];
  taggedExpenses: { id: string; amount: number; category: string; description: string; date: string; submittedBy: string }[];
}

export interface CreditDebitNote {
  id: string;
  type: 'credit' | 'debit';
  noteNumber: string;
  invoiceId: string;
  projectId: string;
  projectName: string;
  amount: number;
  reason: string;
  description: string;
  status: 'draft' | 'requested' | 'approved' | 'rejected' | 'issued' | 'accepted' | 'cancelled';
  invoiceNumber?: string;
  requestedBy?: string;
  requestedById?: string;
  requestedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectReason?: string;
  issuedDate?: string;
  issuedAt?: string;
  issuedBy?: string;
  acceptedAt?: string;
  acceptedBy?: string;
  fileUrl?: string;
  gstImpact?: number;
  createdAt: string;
  createdBy: string;
}

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  user: string;
  department: string;
  action: string;            // e.g. 'project_created', 'invoice_raised', 'vendor_approved'
  entityType: 'project' | 'invoice' | 'vendor' | 'vendor_request' | 'recurring' | 'system' | 'commitment';
  entityId?: string;
  entityName?: string;       // human-readable label
  description: string;       // full sentence describing what happened
  changes?: { field: string; from?: string; to?: string }[];  // field-level diff
  meta?: Record<string, any>;
}

// ─── Storage Keys ──────────────────────────────────────────────────────────────
const KEYS = {
  PROJECTS: 'finance_projects_v2',
  VENDORS: 'finance_vendors_v2',
  VENDOR_REQUESTS: 'finance_vendor_requests_v2',
  INVOICE_COUNTER: 'finance_invoice_counter_v2',
  RECURRING: 'finance_recurring_v2',
  REMINDERS: 'finance_reminders_v2',
  AUDIT_LOG: 'finance_audit_log_v2',
  REMINDER_CONFIGS: 'finance_reminder_configs_v2',
  EXPENSES: 'finance_expenses_v2',
  CREDIT_DEBIT_NOTES: 'finance_credit_debit_notes_v2',
  CN_COUNTER: 'finance_cn_counter_v2',
  DN_COUNTER: 'finance_dn_counter_v2',
  EXPENSE_LIMITS: 'finance_expense_limits',
  OVERDUE_INVOICES: 'finance_overdue_invoices_v2',
};
export type FinanceStore = 'projects' | 'vendors' | 'vendor-requests' | 'credit-debit-notes' | 'reminder-configs' | 'reminders' | 'expense-limits' | 'audit-log' | 'overdue-invoices';

// ─── Date Helpers ──────────────────────────────────────────────────────────────

/** Format a Date as YYYY-MM-DD using LOCAL-time components.
 *  NEVER use `.toISOString().split('T')[0]` for date-only strings — toISOString
 *  converts to UTC, which rolls the date back a day in IST (UTC+5:30) for any
 *  local time before 05:30. This silently corrupted GST/R1 due dates. */
export function toLocalYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** GST due = always 20th of the month FOLLOWING the invoice raised date */
export function calcGstDate(fromDate: string): string {
  const d = new Date(fromDate);
  const gst = new Date(d.getFullYear(), d.getMonth() + 1, 20); // next month, 20th
  return toLocalYMD(gst);
}

/** R1 date = always 12th of the SAME month as the invoice raised date */
export function calcR1Date(fromDate: string): string {
  const d = new Date(fromDate);
  const r1 = new Date(d.getFullYear(), d.getMonth(), 12);
  return toLocalYMD(r1);
}

/** Everything Ink in Caps bills sits in the 18% GST slab. Single source of truth — never hardcode 0.18 elsewhere. */
export const GST_RATE = 0.18;

/** GST amount = always GST_RATE × invoice amount */
export function calcGstAmount(invoiceAmount: number): number {
  return Math.round(invoiceAmount * GST_RATE);
}

// ─── Billing Structure v2 helpers ───────────────────────────────────────────────

/** Flatten a billing tree into the legacy `project.milestones` array.
 *  Stamps categoryKey + derived hardware fields onto each milestone so every
 *  existing consumer (invoices, dashboard, BD tracker, Raise-Invoice E-Way flow)
 *  keeps working without knowing the tree exists. */
export function flattenBillingStructure(billing: BillingCategory[]): Milestone[] {
  const out: Milestone[] = [];
  for (const cat of billing || []) {
    if ((Number(cat.amount) || 0) <= 0 && (Number(cat.pct) || 0) <= 0) continue;
    const isHw = cat.key === 'hardware';
    for (const ms of cat.milestones || []) {
      out.push({
        ...ms,
        categoryKey: cat.key,
        isHardware: isHw,
        hsnCode: isHw ? (cat.hsnCode || ms.hsnCode || '') : ms.hsnCode,
      });
    }
  }
  return out;
}

/** Legacy flat milestones → a single 'uncategorized' group at 100%.
 *  Idempotent: projects that already have a billingStructure are returned as-is. */
export function migrateProjectToV2(p: FinanceProject): FinanceProject {
  if (!p) return p;
  let changed = false;
  const id = p.id || (p as any)._id || '';
  let next: FinanceProject = (p.id !== id) ? { ...p, id } : p;
  if (next.id !== p.id) changed = true;

  const hasCategorizedMilestones = (p.milestones || []).some(m => m.categoryKey && m.categoryKey !== 'uncategorized');
  const isOnlyUncategorized = p.billingStructure?.length === 1 && p.billingStructure[0].key === 'uncategorized';

  if (!p.billingStructure || !p.billingStructure.length || (isOnlyUncategorized && hasCategorizedMilestones)) {
    if (hasCategorizedMilestones) {
      const catMap = new Map<BillingCategoryKey, Milestone[]>();
      for (const m of p.milestones || []) {
        const k = (m.categoryKey || (m.isHardware ? 'hardware' : 'uncategorized')) as BillingCategoryKey;
        if (!catMap.has(k)) catMap.set(k, []);
        catMap.get(k)!.push(m);
      }
      const structure: BillingCategory[] = [];
      for (const [key, msList] of catMap.entries()) {
        const msPct = msList.reduce((s, m) => s + (Number(m.pct) || 0), 0);
        const msAmount = msList.reduce((s, m) => s + (Number(m.amount) || 0), 0);
        const isHw = key === 'hardware' || msList.some(m => m.isHardware);
        structure.push({
          key,
          pct: msPct || (p.totalAmount ? Math.round((msAmount / p.totalAmount) * 100) : 0),
          amount: msAmount,
          hsnCode: isHw ? msList.find(m => m.hsnCode)?.hsnCode : undefined,
          milestones: msList,
        });
      }
      next = { ...next, billingStructure: structure };
      changed = true;
    } else {
      const isHw = !!p.hasHardware;
      const ms = (p.milestones || []).map(m => ({
        ...m,
        categoryKey: (m.isHardware || isHw ? 'hardware' : 'uncategorized') as BillingCategoryKey,
      }));
      next = {
        ...next,
        billingStructure: [{
          key: (isHw ? 'hardware' : 'uncategorized') as BillingCategoryKey,
          pct: 100,
          amount: p.totalAmount || 0,
          milestones: ms,
        }],
        milestones: ms,
      };
      changed = true;
    }
  }
  if (!p.budgetVisibleTo) { next = { ...next, budgetVisibleTo: [...BUDGET_DEFAULT_VISIBLE] }; changed = true; }
  if (!p.bdTeam) { next = { ...next, bdTeam: [] }; changed = true; }
  if (!Array.isArray(next.pis)) { next = { ...next, pis: [] }; changed = true; }
  if (!Array.isArray(next.pos)) { next = { ...next, pos: [] }; changed = true; }
  if (next.poNotApplicable === undefined) { next = { ...next, poNotApplicable: false }; changed = true; }

  const pis: any[] = next.pis;
  const pos: any[] = next.pos;
  const nextAny = next as any;
  if (pis.length || !nextAny.pi) {
    const raised = [...pis].reverse().find(x => x?.status === 'raised');
    const requested = [...pis].reverse().find(x => x?.status === 'requested');
    const src = raised || requested;
    const derived: any = src
      ? { ...src, status: raised ? 'raised' : 'requested' }
      : (nextAny.pi && !pis.length ? nextAny.pi : { status: 'not_requested' });
    if (!nextAny.pi || nextAny.pi.status !== derived.status || nextAny.pi.id !== derived.id) {
      nextAny.pi = derived;
      changed = true;
    }
  }
  if (pos.length || !nextAny.po) {
    const uploaded = [...pos].reverse().find(x => x?.status === 'uploaded');
    const derived: any = uploaded
      ? { ...uploaded, status: 'uploaded' }
      : (next.poNotApplicable ? { status: 'na', notApplicable: true } : (nextAny.po && !pos.length ? nextAny.po : { status: 'not_uploaded', notApplicable: false }));
    if (!nextAny.po || nextAny.po.status !== derived.status || nextAny.po.id !== derived.id) {
      nextAny.po = derived;
      changed = true;
    }
  }
  return changed ? next : p;
}

export function milestoneLabels(p: FinanceProject | null | undefined, ids: string[] = []): string[] {
  if (!p || !Array.isArray(ids)) return [];
  const msMap = new Map((p.milestones || []).map(m => [m.id, m.label]));
  return ids.map(id => msMap.get(id) || id);
}

export function piSummary(p: FinanceProject | null | undefined): 'none' | 'requested' | 'raised' {
  if (!p || !p.pis || p.pis.length === 0) return 'none';
  return p.pis.every(x => x.status === 'raised') ? 'raised' : 'requested';
}

export function poSummary(p: FinanceProject | null | undefined): 'none' | 'uploaded' | 'na' {
  if (!p) return 'none';
  if (p.poNotApplicable) return 'na';
  if (p.pos && p.pos.length > 0) return 'uploaded';
  return 'none';
}

export function pendingPIs(p: FinanceProject | null | undefined): PIRecord[] {
  if (!p || !p.pis) return [];
  return p.pis.filter(x => x.status === 'requested');
}

export function pisForMilestone(p: FinanceProject | null | undefined, msId: string): PIRecord[] {
  if (!p || !p.pis || !msId) return [];
  return p.pis.filter(x => (x.milestoneIds || []).includes(msId));
}

export function posForMilestone(p: FinanceProject | null | undefined, msId: string): PORecord[] {
  if (!p || !p.pos || !msId) return [];
  return p.pos.filter(x => (x.milestoneIds || []).includes(msId));
}

/** Does this invoice cover the milestone? (new list or the single id older rows carry) */
export function invoiceCovers(inv: InvoiceRecord | null | undefined, msId: string): boolean {
  if (!inv || !msId) return false;
  return inv.milestoneIds?.length ? inv.milestoneIds.includes(msId) : inv.milestoneId === msId;
}
/** The milestone ids an invoice covers. */
export function invoiceMilestoneIds(inv: InvoiceRecord | null | undefined): string[] {
  if (!inv) return [];
  return inv.milestoneIds?.length ? inv.milestoneIds : inv.milestoneId ? [inv.milestoneId] : [];
}
/** "Advance, Delivery" for an invoice: the stored label, else the covered milestones' labels. */
export function invoiceMilestoneLabel(p: FinanceProject | null | undefined, inv: InvoiceRecord | null | undefined): string {
  if (!inv) return '';
  if (inv.label) return inv.label;
  return invoiceMilestoneIds(inv).map(id => (p?.milestones || []).find(m => m.id === id)?.label || '').filter(Boolean).join(', ');
}
export function isMilestoneLocked(p: FinanceProject | null | undefined, msId: string): boolean {
  if (!p || !msId) return false;
  // Only a raised invoice locks a milestone (credited or cancelled invoices don't, and PIs and POs never lock).
  return (p.invoices || []).some(i => invoiceCovers(i, msId) && i.status && !['pending', 'credited', 'cancelled'].includes(i.status));
}

export function sortProjectsByDate(projects: FinanceProject[]): FinanceProject[] {
  return [...projects].sort((a, b) => {
    const timeA = new Date(a.createdAt || (a as any).updatedAt || (a as any).clientCreatedAt || 0).getTime();
    const timeB = new Date(b.createdAt || (b as any).updatedAt || (b as any).clientCreatedAt || 0).getTime();
    return timeB - timeA;
  });
}

/** Sum of category pcts (should be 100) */
export function billingTotalPct(billing: BillingCategory[]): number {
  return Math.round((billing || []).reduce((s, c) => s + (Number(c.pct) || 0), 0) * 100) / 100;
}

/** Sum of milestone pcts inside one category (should equal cat.pct) */
export function categoryMilestonePct(cat: BillingCategory): number {
  return Math.round((cat.milestones || []).reduce((s, m) => s + (Number(m.pct) || 0), 0) * 100) / 100;
}

/** TDS amount from % */
export function calcTdsAmount(invoiceAmount: number, tdsPct: number): number {
  return Math.round(invoiceAmount * tdsPct / 100);
}

@Injectable({ providedIn: 'root' })
export class FinanceService {

  private notificationUrl = environment.host + 'notification';
  private apiUrl = environment.host + 'finance';

  readonly changed$ = new Subject<FinanceStore>();
  private writeQueue = new Map<string, Promise<unknown>>();
  private hydrating: Promise<void> | null = null;

  private summaryCache = new Map<string, { data: DashboardSummaryResponse; time: number }>();
  private summaryInflight = new Map<string, Promise<DashboardSummaryResponse | null>>();

  private get authToken(): string {
    return localStorage.getItem('guestToken') || localStorage.getItem('token') || '';
  }

  private readonly delegations = inject(DelegationsService);
  constructor(private http: HttpClient, private toast: NotificationService) { }

  /** The server's "Someone changed this project … Reload" refusal of a form saved from an older copy (2 Nov 2026). */
  static isChangedSince(e: any): boolean {
    return e?.status === 409 && /Someone changed this project/.test(FinanceService.errorMessage(e));
  }

  static errorMessage(e: any): string {
    if (!e) return 'Request failed. Please try again or save as draft.';
    if (typeof e === 'string' && e.trim() && e !== '[object Object]') return e;

    if (e?.status === 500) {
      return 'Internal server error occurred while creating project. Please try again or save as draft.';
    }

    const err = e.error !== undefined ? e.error : e;
    if (typeof err === 'string' && err.trim() && err !== '[object Object]') return err;

    // Check message field
    if (typeof err?.message === 'string' && err.message.trim() && err.message !== '[object Object]') return err.message;
    if (Array.isArray(err?.message)) {
      const msgs = err.message
        .map((x: any) => typeof x === 'string' ? x : (typeof x?.message === 'string' ? x.message : ''))
        .filter((x: string) => x && x.trim() && x !== '[object Object]');
      if (msgs.length > 0) return msgs.join(', ');
    }
    if (typeof err?.message?.message === 'string' && err.message.message.trim() && err.message.message !== '[object Object]') {
      return err.message.message;
    }

    // Check error field
    if (typeof err?.error === 'string' && err.error.trim() && err.error !== '[object Object]') return err.error;
    if (typeof err?.error?.message === 'string' && err.error.message.trim() && err.error.message !== '[object Object]') return err.error.message;

    // Check top-level message on e
    if (typeof e?.message === 'string' && !e.message.includes('[object Object]') && !e.message.startsWith('Http failure response')) {
      return e.message;
    }
    if (typeof e?.statusText === 'string' && e.statusText !== 'OK' && e.statusText !== 'Unknown Error') {
      return `${e.statusText} (${e.status || 'Error'})`;
    }

    return 'Internal server error occurred while creating project. Please try again or save as draft.';
  }

  /** Every refusal shows the server's reason, 403s included (1 Nov 2026); only the guard's "session over" 403 stays quiet — the interceptor logs the person out. */
  private reportError(context: string, e: any): void {
    console.warn(`[FinanceService] ${context} failed:`, e);
    if (e && isSessionRejected(e)) return;
    this.toast.error(`${context} failed: ${FinanceService.errorMessage(e)}`, { duration: 6000 });
  }

  get hasVendorOrOpsAccess(): boolean {
    const dept = (localStorage.getItem('department') || '').trim().toUpperCase();
    return hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS) || dept === 'OPERATIONS';
  }

  private async api<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: any, params?: Record<string, any>): Promise<T> {
    if ((path.startsWith('/vendors') || path.startsWith('/vendor-requests')) && !this.hasVendorOrOpsAccess) {
      console.warn(`[FinanceService] Skipping Vendor API call '${path}' - user does not have VENDORS sub-module access`);
      return null as unknown as T;
    }
    const isProjectReadForVendors = method === 'GET' && (path === '/projects' || path.startsWith('/projects?')) && this.hasVendorOrOpsAccess;
    if (
      (path.startsWith('/projects') ||
        path.startsWith('/credit-debit-notes') ||
        path.startsWith('/reminder-configs') ||
        path.startsWith('/reminders') ||
        path.startsWith('/send-reminder') ||
        path.startsWith('/reminder-preview') ||
        path.startsWith('/check-auto-reminders') ||
        path.startsWith('/overdue-invoices') ||
        path.startsWith('/expense-limits') ||
        path.startsWith('/audit-log') ||
        path.startsWith('/project-comments') ||
        path.startsWith('/dashboard/summary')) &&
      !hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS) &&
      !isProjectReadForVendors
    ) {
      console.warn(`[FinanceService] Skipping Project API call '${path}' - user does not have PROJECTS sub-module access`);
      return null as unknown as T;
    }
    let httpParams = new HttpParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') httpParams = httpParams.set(k, String(v));
    });
    const res: any = await firstValueFrom(this.http.request(method, `${this.apiUrl}${path}`, { body, params: httpParams }));
    return (res?.data !== undefined ? res.data : res) as T;
  }

  private enqueue<T>(key: string, task: () => Promise<T>): Promise<T> {
    const prev = this.writeQueue.get(key) || Promise.resolve();
    const next = prev.catch(() => undefined).then(task);
    this.writeQueue.set(key, next);
    return next;
  }

  private readStore<T>(key: string): T[] {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') {
        if (Array.isArray(parsed.vendors)) return parsed.vendors;
        if (Array.isArray(parsed.requests)) return parsed.requests;
        if (Array.isArray(parsed.vendorRequests)) return parsed.vendorRequests;
        if (Array.isArray(parsed.projects)) return parsed.projects;
        if (Array.isArray(parsed.data)) return parsed.data;
        if (Array.isArray(parsed.items)) return parsed.items;
      }
      return [];
    } catch {
      return [];
    }
  }

  private writeStore<T>(key: string, items: T[]): void {
    localStorage.setItem(key, JSON.stringify(Array.isArray(items) ? items : []));
  }

  private upsertStore<T extends Record<string, any>>(key: string, doc: T, store: FinanceStore, idField = 'id'): void {
    if (!doc || !doc[idField]) return;
    const list = this.readStore<T>(key);
    const idx = list.findIndex(x => x[idField] === doc[idField]);
    if (idx === -1) list.unshift(doc); else list[idx] = { ...list[idx], ...doc };
    this.writeStore(key, list);
    this.changed$.next(store);
  }

  private readonly storeLoaders: Record<FinanceStore, () => Promise<void>> = {
    'projects': () => (hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS) || this.hasVendorOrOpsAccess) ? this.refreshProjects() : Promise.resolve(),
    'vendors': () => this.hasVendorOrOpsAccess ? this.hydrateStore<FinanceVendor>(KEYS.VENDORS, '/vendors', 'vendors', 'Vendors') : Promise.resolve(),
    'vendor-requests': () => this.hasVendorOrOpsAccess ? this.hydrateStore<VendorPaymentRequest>(KEYS.VENDOR_REQUESTS, '/vendor-requests', 'vendor-requests', 'Vendor requests', FinanceService.mergeVendorRequests) : Promise.resolve(),
    'credit-debit-notes': () => hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS) ? this.hydrateStore<CreditDebitNote>(KEYS.CREDIT_DEBIT_NOTES, '/credit-debit-notes', 'credit-debit-notes', 'Credit/debit notes') : Promise.resolve(),
    'reminder-configs': () => hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS) ? this.hydrateStore<ReminderConfig>(KEYS.REMINDER_CONFIGS, '/reminder-configs', 'reminder-configs', 'Reminder configs') : Promise.resolve(),
    'reminders': () => hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS) ? this.hydrateStore<ReminderRecord>(KEYS.REMINDERS, '/reminders', 'reminders', 'Reminder history') : Promise.resolve(),
    'overdue-invoices': () => hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS) ? this.hydrateStore<OverdueInvoice>(KEYS.OVERDUE_INVOICES, '/overdue-invoices', 'overdue-invoices', 'Overdue invoices') : Promise.resolve(),
    'expense-limits': () => hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS) ? this.hydrateExpenseLimits() : Promise.resolve(),
    'audit-log': () => hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS) ? this.hydrateAuditLog() : Promise.resolve(),
  };
  private inflight = new Map<FinanceStore, Promise<void>>();
  private fetchedAt = new Map<FinanceStore, number>();
  private static readonly FRESH_MS = 5000;

  refresh(stores: FinanceStore[], force = false): Promise<void> {
    if (!this.authToken) return Promise.resolve();
    void this.loadMe();
    return Promise.all(stores.map(s => this.refreshStore(s, force))).then(() => undefined);
  }

  private refreshStore(store: FinanceStore, force: boolean): Promise<void> {
    if ((store === 'vendors' || store === 'vendor-requests') && !this.hasVendorOrOpsAccess) {
      return Promise.resolve();
    }
    if (store === 'projects') {
      if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS) && !this.hasVendorOrOpsAccess) {
        return Promise.resolve();
      }
    } else if (
      (store === 'credit-debit-notes' ||
        store === 'reminder-configs' ||
        store === 'reminders' ||
        store === 'overdue-invoices' ||
        store === 'expense-limits' ||
        store === 'audit-log') &&
      !hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)
    ) {
      return Promise.resolve();
    }
    const running = this.inflight.get(store);
    if (running) return running;
    if (!force && Date.now() - (this.fetchedAt.get(store) || 0) < FinanceService.FRESH_MS) return Promise.resolve();
    const task = this.storeLoaders[store]()
      .then(() => { this.fetchedAt.set(store, Date.now()); })
      .finally(() => this.inflight.delete(store));
    this.inflight.set(store, task);
    return task;
  }

  hydrate(): Promise<void> {
    if (!this.authToken) return Promise.resolve();
    if (this.hydrating) return this.hydrating;
    this.hydrating = (async () => {
      const storesToRefresh: FinanceStore[] = [];
      if (hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) {
        storesToRefresh.push(
          'projects',
          'credit-debit-notes',
          'reminder-configs',
          'reminders',
          'overdue-invoices',
          'expense-limits',
          'audit-log'
        );
      }
      if (this.hasVendorOrOpsAccess) {
        storesToRefresh.push('vendors', 'vendor-requests');
        if (!storesToRefresh.includes('projects')) {
          storesToRefresh.push('projects');
        }
      }
      if (storesToRefresh.length) {
        await this.refresh(storesToRefresh);
      }
    })().finally(() => { this.hydrating = null; });
    return this.hydrating;
  }

  async searchProjects(filters: { status?: string; search?: string; page?: number; limit?: number }): Promise<{ projects: FinanceProject[]; total?: number; page?: number; limit?: number; totalPages?: number; hasMore: boolean } | null> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS) && !this.hasVendorOrOpsAccess) {
      return { projects: [], total: 0, page: 1, limit: filters.limit || 200, totalPages: 0, hasMore: false };
    }
    const limit = filters.limit || 200;
    const page = filters.page || 1;
    try {
      const res: any = await this.api<any>('GET', '/projects', undefined, {
        status: filters.status || 'all',
        search: filters.search?.trim(),
        page,
        limit,
      });
      let list: FinanceProject[] = [];
      let total = 0;
      if (Array.isArray(res)) {
        list = res;
        total = res.length;
      } else if (res && typeof res === 'object') {
        list = Array.isArray(res.projects) ? res.projects : (Array.isArray(res.data) ? res.data : []);
        total = typeof res.total === 'number' ? res.total : list.length;
      }
      const totalPages = Math.max(1, Math.ceil(total / limit));
      return { projects: list.map(migrateProjectToV2), total, page, limit, totalPages, hasMore: list.length >= limit || page < totalPages };
    } catch (e) {
      this.reportError('Searching projects', e);
      return null;
    }
  }

  async searchVendors(filters: { search?: string; page?: number; limit?: number }): Promise<{ vendors: FinanceVendor[]; total: number; page: number; limit: number; totalPages: number } | null> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) {
      return { vendors: [], total: 0, page: 1, limit: filters.limit || 20, totalPages: 0 };
    }
    const limit = filters.limit || 20;
    const page = filters.page || 1;
    try {
      const res: any = await this.api<any>('GET', '/vendors', undefined, {
        search: filters.search?.trim(),
        page,
        limit,
      });
      let list: FinanceVendor[] = [];
      let total = 0;
      if (Array.isArray(res)) {
        list = res;
        total = res.length;
      } else if (res && typeof res === 'object') {
        list = Array.isArray(res.vendors) ? res.vendors : (Array.isArray(res.data) ? res.data : (Array.isArray(res.items) ? res.items : []));
        total = typeof res.total === 'number' ? res.total : list.length;
      }
      const totalPages = Math.max(1, Math.ceil(total / limit));
      if (page === 1 && !filters.search?.trim()) {
        this.saveVendors(list);
        localStorage.setItem(KEYS.VENDORS + '_total', String(total));
      }
      return { vendors: list, total, page, limit, totalPages };
    } catch (e) {
      this.reportError('Searching vendors', e);
      return null;
    }
  }

  getVendorTotal(): number {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) {
      return 0;
    }
    const stored = localStorage.getItem(KEYS.VENDORS + '_total');
    if (stored !== null && !isNaN(Number(stored))) {
      return Number(stored);
    }
    return this.getVendors().length;
  }

  async searchVendorRequests(filters: { filter?: string; search?: string; page?: number; limit?: number }): Promise<{ requests: VendorPaymentRequest[]; total?: number; page?: number; limit?: number; totalPages?: number; hasMore: boolean } | null> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) {
      return { requests: [], total: 0, page: 1, limit: filters.limit || 200, totalPages: 0, hasMore: false };
    }
    const limit = filters.limit || 200;
    const page = filters.page || 1;
    try {
      const res: any = await this.api<any>('GET', '/vendor-requests', undefined, {
        filter: filters.filter || 'all',
        search: filters.search?.trim(),
        page,
        limit,
      });
      let list: VendorPaymentRequest[] = [];
      let total = 0;
      if (Array.isArray(res)) {
        list = res;
        total = res.length;
      } else if (res && typeof res === 'object') {
        list = Array.isArray(res.requests) ? res.requests : (Array.isArray(res.vendorRequests) ? res.vendorRequests : (Array.isArray(res.data) ? res.data : []));
        total = typeof res.total === 'number' ? res.total : list.length;
      }
      const totalPages = Math.max(1, Math.ceil(total / limit));
      return { requests: list, total, page, limit, totalPages, hasMore: list.length >= limit || page < totalPages };
    } catch (e) {
      this.reportError('Searching vendor requests', e);
      return null;
    }
  }

  async fetchRemindersForInvoice(invoiceId: string): Promise<void> {
    if (!this.authToken || !invoiceId || !hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) return;
    try {
      const list = (await this.api<ReminderRecord[]>('GET', '/reminders', undefined, { invoiceId })) || [];
      const others = this.getReminderHistory().filter(r => r.invoiceId !== invoiceId);
      this.writeStore(KEYS.REMINDERS, [...list, ...others]);
      this.changed$.next('reminders');
    } catch (e) {
      this.reportError('Loading reminder history', e);
    }
  }

  async refreshProjects(): Promise<void> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS) && !this.hasVendorOrOpsAccess) return;
    try {
      const res: any = await this.api<any>('GET', '/projects');
      let server: FinanceProject[] = [];
      if (Array.isArray(res)) {
        server = res;
      } else if (res && typeof res === 'object') {
        server = Array.isArray(res.projects) ? res.projects : (Array.isArray(res.data) ? res.data : (Array.isArray(res.items) ? res.items : (Array.isArray(res.result) ? res.result : [])));
      }
      const local = this.getProjects();
      const serverMapped = server.map(migrateProjectToV2);
      const serverIds = new Set(serverMapped.map(p => p.id || (p as any)._id));
      // The server is the source of truth (since the 19 Oct 2026 migration): a project it no longer
      // returns — deleted, archived, or never saved — leaves the local copy too. Keeping such
      // "missing locals" left ghost projects in every list, and actions on them failed with
      // "project not found" (founder, 24 Oct 2026).
      const ghosts = local.filter(p => !serverIds.has(p.id) && !serverIds.has((p as any)._id));
      if (ghosts.length) console.info(`[FinanceService] dropping ${ghosts.length} cached project(s) the server no longer returns:`, ghosts.map(p => p.name));
      const merged = sortProjectsByDate(serverMapped);
      this.saveProjects(merged);
      this.changed$.next('projects');
    } catch (e) {
      this.reportError('Loading projects', e);
    }
  }

  private async hydrateStore<T>(key: string, path: string, store: FinanceStore, label: string, merge?: (server: T[], local: T[]) => T[]): Promise<void> {
    if ((path.startsWith('/vendors') || path.startsWith('/vendor-requests')) && !this.hasVendorOrOpsAccess) {
      return;
    }
    const isProjectReadForVendors = path.startsWith('/projects') && this.hasVendorOrOpsAccess;
    if (
      (path.startsWith('/projects') ||
        path.startsWith('/credit-debit-notes') ||
        path.startsWith('/reminder-configs') ||
        path.startsWith('/reminders') ||
        path.startsWith('/overdue-invoices')) &&
      !hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS) &&
      !isProjectReadForVendors
    ) {
      return;
    }
    try {
      const res: any = await this.api<any>('GET', path);
      if (!res) return;
      let server: T[] = [];
      if (Array.isArray(res)) {
        server = res;
      } else if (res && typeof res === 'object') {
        if (Array.isArray(res.vendors)) {
          server = res.vendors;
          if (res.total !== undefined) localStorage.setItem(KEYS.VENDORS + '_total', String(res.total));
        } else if (Array.isArray(res.requests)) {
          server = res.requests;
          if (res.total !== undefined) localStorage.setItem(KEYS.VENDOR_REQUESTS + '_total', String(res.total));
        } else if (Array.isArray(res.vendorRequests)) {
          server = res.vendorRequests;
          if (res.total !== undefined) localStorage.setItem(KEYS.VENDOR_REQUESTS + '_total', String(res.total));
        } else if (Array.isArray(res.projects)) {
          server = res.projects;
          if (res.total !== undefined) localStorage.setItem(KEYS.PROJECTS + '_total', String(res.total));
        } else if (Array.isArray(res.data)) {
          server = res.data;
        } else if (Array.isArray(res.items)) {
          server = res.items;
        } else if (Array.isArray(res.records)) {
          server = res.records;
        }
      }
      const local = this.readStore<T>(key);
      this.writeStore(key, merge ? merge(server, local) : server);
      this.changed$.next(store);
    } catch (e) {
      this.reportError(`Loading ${label.toLowerCase()}`, e);
    }
  }

  private static mergeVendorRequests(server: VendorPaymentRequest[], _local: VendorPaymentRequest[]): VendorPaymentRequest[] {
    // Every vendor action now waits for the server, so the server copy is always the truth.
    return server;
  }
  private static mergeVendorRequestsLegacy(server: VendorPaymentRequest[], local: VendorPaymentRequest[]): VendorPaymentRequest[] {
    const localById = new Map(local.map(r => [r.id, r]));
    return server.map(s => {
      const l = localById.get(s.id);
      if (!l || (l.approvalHistory || []).length <= (s.approvalHistory || []).length) return s;
      return {
        ...s,
        status: l.status,
        currentStepIndex: l.currentStepIndex,
        pendingDepartment: l.pendingDepartment,
        approvalHistory: l.approvalHistory,
        paymentMode: l.paymentMode,
        paymentUtr: l.paymentUtr,
        paymentDate: l.paymentDate,
      };
    });
  }

  private async hydrateExpenseLimits(): Promise<void> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) return;
    try {
      const server = (await this.api<ExpenseCategoryLimit[]>('GET', '/expense-limits')) || [];
      this.writeStore(KEYS.EXPENSE_LIMITS, server.map(l => ({ category: l.category, maxAmount: l.maxAmount, description: l.description })));
      this.changed$.next('expense-limits');
    } catch (e) {
      this.reportError('Loading expense limits', e);
    }
  }

  private async hydrateAuditLog(): Promise<void> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) return;
    try {
      const server = (await this.api<AuditLogEntry[]>('GET', '/audit-log', undefined, { limit: 500 })) || [];
      const local = this.getAuditLog();
      const byId = new Map<string, AuditLogEntry>();
      [...local, ...server].forEach(e => byId.set(e.id, e));
      const merged = [...byId.values()].sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || '')).slice(0, 500);
      localStorage.setItem(KEYS.AUDIT_LOG, JSON.stringify(merged));
      this.changed$.next('audit-log');
    } catch (e) {
      this.reportError('Loading audit log', e);
    }
  }

  // ── File upload ────────────────────────────────────────────────────────────

  async uploadDocument(file: File, folder: string = 'finance'): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    let data: any;
    try {
      data = await this.api<any>('POST', '/upload', formData);
    } catch (e) {
      throw new Error(FinanceService.errorMessage(e));
    }
    let rawUrl = data?.url || data?.fileUrl || data?.path || data?.data?.url || (typeof data === 'string' ? data : '');
    if (!rawUrl) throw new Error('Upload failed: server returned no file URL');
    if (rawUrl.startsWith('/') && !rawUrl.startsWith('//')) {
      rawUrl = `${environment.host.replace(/\/$/, '')}${rawUrl}`;
    }
    return rawUrl;
  }

  // ── ID generators ──────────────────────────────────────────────────────────

  private nextId(key: string): string {
    const n = (parseInt(localStorage.getItem(key) || '0', 10) + 1);
    localStorage.setItem(key, String(n));
    return String(n).padStart(4, '0');
  }

  generateInvoiceNumber(): string {
    const n = this.nextId(KEYS.INVOICE_COUNTER);
    const year = new Date().getFullYear();
    return `INV-${year}-IIC-${n}`;
  }

  generateProjectId(): string {
    return 'proj_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
  }

  generateMilestoneId(): string {
    return 'ms_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
  }

  generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  }

  // ── Notification ───────────────────────────────────────────────────────────

  sendNotification(payload: {
    recipientDepartments: string[];
    message: string;
    subType: string;
    redirectUrl?: string;
    meta?: any;
  }): void {
    const senderName = localStorage.getItem('username') || 'Finance';
    const body = {
      senderName,
      message: payload.message,
      subType: payload.subType,
      recipientDepartments: payload.recipientDepartments,
      redirectUrl: payload.redirectUrl ? encodeURIComponent(payload.redirectUrl) : '',
      meta: payload.meta || {},
      type: 'finance',
    };
    this.http.post(this.notificationUrl, body).subscribe({
      next: () => { },
      error: (e) => console.warn('Finance notification failed:', e),
    });
  }

  // ── Projects ───────────────────────────────────────────────────────────────

  getProjects(): FinanceProject[] {
    const raw = localStorage.getItem(KEYS.PROJECTS);
    const list: FinanceProject[] = raw ? JSON.parse(raw) : [];
    // v2 migration: flat milestones → single Uncategorized billing group. Runs once,
    // persists, then is a no-op (migrateProjectToV2 returns the same ref if untouched).
    let migrated = false;
    const out = list.map(p => { const n = migrateProjectToV2(p); if (n !== p) migrated = true; return n; });
    if (migrated) {
      localStorage.setItem(KEYS.PROJECTS, JSON.stringify(out));
      console.info(`[finance] migrated ${out.filter((p, i) => p !== list[i]).length} project(s) to Billing Structure v2`);
    }
    return out;
  }

  saveProjects(projects: FinanceProject[]): void {
    localStorage.setItem(KEYS.PROJECTS, JSON.stringify(projects));
  }

  getProjectById(id: string): FinanceProject | null {
    return this.getProjects().find(p => p.id === id) || null;
  }

  private storeProject(project: FinanceProject | null | undefined): void {
    if (!project?.id) return;
    const next = migrateProjectToV2(project);
    const projects = this.getProjects();
    const idx = projects.findIndex(p => p.id === next.id);
    if (idx === -1) projects.unshift(next); else projects[idx] = next;
    this.saveProjects(projects);
    this.changed$.next('projects');
  }

  private patchLocalProject(id: string, updates: Partial<FinanceProject>): FinanceProject | null {
    const projects = this.getProjects();
    const idx = projects.findIndex(p => p.id === id);
    if (idx === -1) return null;
    projects[idx] = { ...projects[idx], ...updates };
    this.saveProjects(projects);
    return projects[idx];
  }

  private projectBody(project: FinanceProject, include: (keyof FinanceProject)[]): Record<string, any> {
    const { pi, po, pis, pos, poNotApplicable, invoices, createdAt, ...rest } = project as any;
    delete rest._mongoId;
    const body: Record<string, any> = { ...rest, clientCreatedAt: createdAt };
    if (include.includes('invoices')) body['invoices'] = invoices;

    if (Array.isArray(body['billingStructure'])) {
      body['billingStructure'] = body['billingStructure']
        .filter((c: BillingCategory) => (Number(c.amount) || 0) > 0 && (Number(c.pct) || 0) > 0)
        .map((c: BillingCategory) => ({
          ...c,
          pct: Number(c.pct) || 0,
          amount: Number(c.amount) || 0,
          milestones: Array.isArray(c.milestones)
            ? c.milestones.filter(m => (Number(m.amount) || 0) > 0 || (Number(m.pct) || 0) > 0 || !!m.label?.trim())
            : [],
        }));
    }

    return body;
  }

  private withBillingMirror(old: FinanceProject, updates: Partial<FinanceProject>): Partial<FinanceProject> {
    if (!updates.billingStructure?.length) return updates;
    // v2: billing tree is the source of truth — re-derive the flat mirror, but keep
    // per-milestone runtime fields (invoiceId, eWayBillUrl, challan) that live on the mirror
    const oldById = new Map((old.milestones || []).map(m => [m.id, m]));
    const flat = flattenBillingStructure(updates.billingStructure).map(m => {
      const prev = oldById.get(m.id);
      return prev ? { ...m, invoiceId: m.invoiceId ?? prev.invoiceId, eWayBillUrl: m.eWayBillUrl || prev.eWayBillUrl, deliveryChallanUrl: m.deliveryChallanUrl || prev.deliveryChallanUrl } : m;
    });
    return { ...updates, milestones: flat };
  }

  private saveProjectWith(id: string, mutate: (p: FinanceProject) => Partial<FinanceProject>, label: string): Promise<FinanceProject | null> {
    const current = this.getProjectById(id);
    if (!current) return Promise.resolve(null);
    this.patchLocalProject(id, this.withBillingMirror(current, mutate(current)));
    return this.enqueue(id, async () => {
      const latest = this.getProjectById(id);
      if (!latest) return null;
      const updates = this.withBillingMirror(latest, mutate(latest));
      const merged = { ...latest, ...updates };
      const saved = await this.api<FinanceProject>('PUT', `/projects/${id}`, this.projectBody(merged, Object.keys(updates) as (keyof FinanceProject)[]));
      this.storeProject(saved);
      return saved;
    }).catch(e => {
      this.reportError(label, e);
      this.refreshProjects();
      return null;
    });
  }

  private projectAction<T = FinanceProject>(projectId: string, path: string, body: any, label: string): Promise<T | null> {
    return this.enqueue(projectId, () => this.api<T>('POST', `/projects/${projectId}/${path}`, body))
      .then(res => {
        const project = (res as any)?.data ?? (res as any)?.project ?? res;
        this.storeProject(project);
        this.scheduleAuditRefresh();
        return res;
      })
      .catch(e => {
        this.reportError(label, e);
        this.refreshProjects();
        return null;
      });
  }

  async createProject(
    data: Omit<FinanceProject, 'id' | 'createdAt' | 'pis' | 'pos' | 'poNotApplicable' | 'invoices'>,
    opts: { lookAlikeReason?: string } = {}
  ): Promise<FinanceProject> {
    // v2: if a billing tree is supplied, `milestones` is always its flattened mirror
    if (data.billingStructure?.length) {
      const cleaned = data.billingStructure
        .filter((c: BillingCategory) => (Number(c.amount) || 0) > 0 && (Number(c.pct) || 0) > 0)
        .map((c: BillingCategory) => ({
          ...c,
          pct: Number(c.pct) || 0,
          amount: Number(c.amount) || 0,
          milestones: Array.isArray(c.milestones)
            ? c.milestones.filter(m => (Number(m.amount) || 0) > 0 || (Number(m.pct) || 0) > 0 || !!m.label?.trim())
            : [],
        }));
      data = { ...data, billingStructure: cleaned, milestones: flattenBillingStructure(cleaned) };
    }
    const project: FinanceProject = {
      ...data,
      id: this.generateProjectId(),
      createdAt: new Date().toISOString(),
      pis: [],
      pos: [],
      poNotApplicable: false,
      invoices: [],
    };

    try {
      const body = this.projectBody(project, ['invoices']);
      // Why the creator went ahead although projects looked like this one (3 Nov 2026).
      if (opts.lookAlikeReason?.trim()) body['lookAlikeReason'] = opts.lookAlikeReason.trim();
      const saved = await this.api<FinanceProject>('POST', '/projects', body);
      const finalProject: FinanceProject = migrateProjectToV2({
        ...project,
        ...(saved || {}),
        id: (saved as any)?.id || (saved as any)?._id || project.id,
      });
      const projects = this.getProjects();
      const existingIdx = projects.findIndex(p => (p.id && p.id === finalProject.id) || ((p as any)._id && (p as any)._id === (finalProject as any)._id));
      if (existingIdx >= 0) {
        projects[existingIdx] = finalProject;
      } else {
        projects.unshift(finalProject);
      }
      this.saveProjects(sortProjectsByDate(projects));
      // Audit entry is written by the server (single source); refresh the log view.
      this.scheduleAuditRefresh();
      this.changed$.next('projects');
      return finalProject;
    } catch (e: any) {
      console.error('[FinanceService] Creating project failed:', e);
      throw e;
    }
  }

  // ── Client list (30 Oct 2026) ──────────────────────────────────────────────

  /**
   * Clients from earlier projects whose legal name or GSTIN matches what the BD typed (at least two
   * characters; at most ten, best matches first). Never throws: no match, no access or a failed call
   * all answer an empty list, and the BD types the client's details as before.
   */
  async searchClients(q: string): Promise<FinanceClientMatch[]> {
    const text = (q || '').trim();
    if (text.length < 2 || !hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) return [];
    try {
      const rows = await this.api<FinanceClientMatch[]>('GET', '/clients/search', undefined, { q: text });
      return Array.isArray(rows) ? rows : [];
    } catch (e: any) {
      console.warn('[FinanceService] Client search failed:', e);
      return [];
    }
  }

  // ── Project Drafts ─────────────────────────────────────────────────────────

  async getProjectDrafts(page = 1, limit = 20): Promise<ProjectDraft[]> {
    try {
      const res: any = await this.api<any>('GET', '/projects/drafts', undefined, { page, limit });
      if (Array.isArray(res)) return res;
      if (Array.isArray(res?.drafts)) return res.drafts;
      if (Array.isArray(res?.items)) return res.items;
      if (Array.isArray(res?.data)) return res.data;
      if (res && typeof res === 'object') {
        const arr = Object.values(res).find(v => Array.isArray(v));
        if (arr) return arr as ProjectDraft[];
      }
      return [];
    } catch (e: any) {
      console.warn('[FinanceService] Failed to fetch project drafts:', e);
      return [];
    }
  }

  async saveProjectDraft(draft: Partial<ProjectDraft>, silent = false): Promise<ProjectDraft | null> {
    try {
      const res = await this.api<ProjectDraft>('POST', '/projects/drafts', draft);
      if (!silent) {
        this.toast.success('Draft saved successfully');
      }
      return res;
    } catch (e: any) {
      console.error('[FinanceService] Saving project draft failed:', e);
      if (!silent) {
        this.toast.error(FinanceService.errorMessage(e));
      }
      throw e;
    }
  }

  async deleteProjectDraft(id: string, silent = false): Promise<boolean> {
    try {
      await this.api('DELETE', `/projects/drafts/${id}`);
      if (!silent) {
        this.toast.success('Draft deleted successfully');
      }
      return true;
    } catch (e: any) {
      console.error('[FinanceService] Deleting project draft failed:', e);
      if (!silent) {
        this.toast.error(FinanceService.errorMessage(e));
      }
      return false;
    }
  }

  updateProject(id: string, updates: Partial<FinanceProject>): FinanceProject | null {
    const old = this.getProjectById(id);
    if (!old) return null;
    this.saveProjectWith(id, () => updates, 'Saving project');
    const updated = this.getProjectById(id)!;
    // Build field-level diff for audit
    const changes: { field: string; from?: string; to?: string }[] = [];
    if (updates.name && updates.name !== old.name) changes.push({ field: 'name', from: old.name, to: updates.name });
    if (updates.totalAmount !== undefined && updates.totalAmount !== old.totalAmount) changes.push({ field: 'totalAmount', from: String(old.totalAmount), to: String(updates.totalAmount) });
    if (updates.status && updates.status !== old.status) changes.push({ field: 'status', from: old.status, to: updates.status });
    if (updates.client?.legalName && updates.client.legalName !== old.client.legalName) changes.push({ field: 'client', from: old.client.legalName, to: updates.client.legalName });
    if (changes.length > 0) {
      // Audit entry is written by the server (single source); refresh the log view.
      this.scheduleAuditRefresh();
    }
    return updated;
  }

  patchInvoice(projectId: string, invoiceId: string, fields: Partial<InvoiceRecord>, label = 'Saving invoice'): Promise<FinanceProject | null> {
    return this.saveProjectWith(projectId, p => ({
      invoices: (p.invoices || []).map(inv => inv.id === invoiceId ? { ...inv, ...fields } : inv),
    }), label);
  }

  patchMilestone(projectId: string, milestoneId: string, fields: Partial<Milestone>, label = 'Saving milestone'): Promise<FinanceProject | null> {
    return this.saveProjectWith(projectId, p => {
      const updates: Partial<FinanceProject> = {
        milestones: (p.milestones || []).map(m => m.id === milestoneId ? { ...m, ...fields } : m),
      };
      if (p.billingStructure?.length) {
        updates.billingStructure = p.billingStructure.map(c => ({
          ...c,
          milestones: (c.milestones || []).map(m => m.id === milestoneId ? { ...m, ...fields } : m),
        }));
      }
      return updates;
    }, label);
  }

  deleteProject(id: string): void {
    const project = this.getProjectById(id);
    const projects = this.getProjects().filter(p => p.id !== id);
    this.saveProjects(projects);
    // Audit entry is written by the server (single source).
    this.enqueue(id, () => this.api('DELETE', `/projects/${id}`))
      .catch(e => {
        this.reportError('Deleting project', e);
        this.refreshProjects();
      });
  }

  // ── PI Workflow ────────────────────────────────────────────────────────────

  /** Resolves to the server's answer, or null when it refused (the error is shown). */
  requestPI(projectId: string, data: { milestoneIds: string[]; notes?: string; note?: string; amount?: number; quotationUrl?: string; quotationId?: string }): Promise<any> {
    const project = this.getProjectById(projectId);
    if (!project) return Promise.resolve(null);
    const msIds = data.milestoneIds || [];
    const notes = (data.notes !== undefined ? data.notes : data.note) || '';
    // The PI shows once the server has it (1 Nov 2026: nothing looks done when the server refused it).
    const payload: { milestoneIds: string[]; notes?: string; amount?: number; quotationUrl?: string; quotationId?: string } = {
      milestoneIds: msIds,
      ...(notes ? { notes } : {}),
      ...(data.amount !== undefined ? { amount: data.amount } : {}),
      ...(data.quotationUrl ? { quotationUrl: data.quotationUrl } : {}),
      ...(data.quotationId ? { quotationId: data.quotationId } : {}),
    };

    // The server tells Accounts and the BD side (it is the single sender of finance notifications).
    return this.projectAction(projectId, 'request-pi', payload, 'Requesting PI');
  }

  /**
   * Accounts raise the final, signed PI once the BD side approved its draft (1 Nov 2026). Nothing changes on
   * screen until the server agrees; resolves to its answer, or null when it refused (the error is shown).
   */
  raisePI(projectId: string, piId: string, piNumber: string, fileUrl: string): Promise<any> {
    return this.projectAction(projectId, 'raise-pi', { piId, piNumber, fileUrl }, 'Raising PI');
  }

  // ── Draft PI check (1 Nov 2026): Accounts → the BD side → the final, signed PI ──
  sendPIDraft(projectId: string, piId: string, fileUrl: string, note: string): Promise<any> {
    return this.projectAction(projectId, `pis/${piId}/draft`, { fileUrl, note }, 'Sending draft PI');
  }
  approvePIDraft(projectId: string, piId: string, note = ''): Promise<any> {
    return this.projectAction(projectId, `pis/${piId}/draft/approve`, { note }, 'Approving draft PI');
  }
  requestPIDraftChanges(projectId: string, piId: string, note: string): Promise<any> {
    return this.projectAction(projectId, `pis/${piId}/draft/changes`, { note }, 'Asking for draft PI changes');
  }
  /**
   * May I check this draft (invoice or PI)? The BD side of the project — requester, creator, owner, team — or an
   * Admin or a business head; never Accounts and never whoever sent it (the server's rule, 1 Nov 2026).
   */
  canCheckDraftOf(p: FinanceProject | null | undefined, item: { requestedById?: string; draft?: InvoiceDraft } | null | undefined): boolean {
    if (!p || !item || item.draft?.status !== 'sent') return false;
    const me = localStorage.getItem('guestID') || '';
    const dept = (localStorage.getItem('department') || '').toUpperCase();
    if (dept === 'ACCOUNTS' || (me && item.draft.sentById === me)) return false;
    if (dept === 'ADMIN' || this.isBusinessHead) return true;
    return [item.requestedById, (p as any).createdBy, p.bdOwner, ...(p.bdTeam || [])].map(x => String(x || '')).includes(me);
  }

  // ── PO Workflow ────────────────────────────────────────────────────────────

  uploadPO(
    projectId: string,
    dataOrFilename:
      | string
      | {
        milestoneIds?: string[];
        piId?: string;
        number?: string;
        amount?: number;
        notes?: string;
        filename?: string;
        fileUrl?: string;
      },
    legacyFileUrl?: string,
    legacyData?: any
  ): void {
    const project = this.getProjectById(projectId);
    if (!project) return;
    const uploadedBy = localStorage.getItem('username') || '';
    const uploadedAt = new Date().toISOString();

    let data: {
      milestoneIds?: string[];
      piId?: string;
      number?: string;
      amount?: number;
      notes?: string;
      filename?: string;
      fileUrl?: string;
    };

    if (typeof dataOrFilename === 'string') {
      data = {
        ...legacyData,
        filename: dataOrFilename,
        fileUrl: legacyFileUrl,
      };
    } else {
      data = dataOrFilename || {};
    }

    const msIds = data.milestoneIds || [];
    const autoAmount = (project.milestones || [])
      .filter(m => msIds.includes(m.id))
      .reduce((s, m) => s + (m.amount || 0), 0);
    const amount = data.amount !== undefined ? data.amount : autoAmount;
    const filename = data.filename || 'PO Document';
    const fileUrl = data.fileUrl || '';

    const optimisticPO: PORecord = {
      id: this.generateId(),
      milestoneIds: msIds,
      amount,
      piId: data.piId || '',
      number: data.number || '',
      notes: data.notes || '',
      status: 'uploaded',
      filename,
      fileUrl,
      uploadedAt,
      uploadedBy,
    };

    this.patchLocalProject(projectId, {
      pos: [...(project.pos || []), optimisticPO],
      poNotApplicable: false,
    });

    const payload: {
      milestoneIds?: string[];
      piId?: string;
      number?: string;
      amount?: number;
      notes?: string;
      filename?: string;
      fileUrl?: string;
    } = {
      ...(data.milestoneIds ? { milestoneIds: data.milestoneIds } : {}),
      ...(data.piId ? { piId: data.piId } : {}),
      ...(data.number ? { number: data.number } : {}),
      ...(data.amount !== undefined ? { amount: data.amount } : {}),
      ...(data.notes !== undefined ? { notes: data.notes } : {}),
      ...(filename ? { filename } : {}),
      ...(fileUrl ? { fileUrl } : {}),
    };

    this.projectAction(projectId, 'upload-po', payload, 'Uploading PO');
  }

  removePO(projectId: string, poId: string): Promise<FinanceProject | null> {
    const project = this.getProjectById(projectId);
    if (project) {
      this.patchLocalProject(projectId, {
        pos: (project.pos || []).filter(po => po.id !== poId),
      });
    }
    return this.enqueue(projectId, () => this.api<any>('DELETE', `/projects/${projectId}/pos/${poId}`))
      .then(res => {
        const updated = (res as any)?.data ?? (res as any)?.project ?? res;
        if (updated && (updated.id || updated._id)) {
          this.storeProject(updated);
        }
        return updated;
      })
      .catch(e => {
        this.reportError('Removing PO', e);
        this.refreshProjects();
        return null;
      });
  }

  markPONotApplicable(projectId: string, reason?: string): void {
    this.patchLocalProject(projectId, { poNotApplicable: true, ...(reason ? { poNotApplicableReason: reason } : {}) });
    this.projectAction(projectId, 'po-na', reason ? { reason } : {}, 'Marking PO not applicable');
  }

  undoPONA(projectId: string): void {
    this.patchLocalProject(projectId, { poNotApplicable: false });
    this.projectAction(projectId, 'undo-po-na', {}, 'Undoing PO not applicable');
  }

  // ── Helpers on Service ─────────────────────────────────────────────────────

  milestoneLabels(p: FinanceProject | null | undefined, ids: string[] = []): string[] { return milestoneLabels(p, ids); }
  piSummary(p: FinanceProject | null | undefined): 'none' | 'requested' | 'raised' { return piSummary(p); }
  poSummary(p: FinanceProject | null | undefined): 'none' | 'uploaded' | 'na' { return poSummary(p); }
  pendingPIs(p: FinanceProject | null | undefined): PIRecord[] { return pendingPIs(p); }
  pisForMilestone(p: FinanceProject | null | undefined, msId: string): PIRecord[] { return pisForMilestone(p, msId); }
  posForMilestone(p: FinanceProject | null | undefined, msId: string): PORecord[] { return posForMilestone(p, msId); }
  isMilestoneLocked(p: FinanceProject | null | undefined, msId: string): boolean { return isMilestoneLocked(p, msId); }

  // ── Invoice Workflow ───────────────────────────────────────────────────────

  /** One invoice for one or several milestones (17 Oct 2026). */
  requestInvoice(projectId: string, milestoneIds: string | string[], dueDate: string): InvoiceRecord | null {
    const project = this.getProjectById(projectId);
    if (!project) return null;
    const ids = (Array.isArray(milestoneIds) ? milestoneIds : [milestoneIds]).filter(Boolean);
    if (!ids.length) return null;
    const covered = project.milestones.filter(m => ids.includes(m.id));
    const now = new Date().toISOString();
    const invoice: InvoiceRecord = {
      id: this.generateId(),
      milestoneId: ids[0],
      milestoneIds: ids,
      label: covered.map(m => m.label || '').filter(Boolean).join(', '),
      number: '',
      amount: Math.round(covered.reduce((s, m) => s + (Number(m.amount) || 0), 0) * 100) / 100,
      status: 'pending',
      dueDate,
      gstDate: calcGstDate(now),
      r1Date: calcR1Date(now),
      requestedAt: now,
      requestedBy: localStorage.getItem('username') || '',
    };
    this.patchLocalProject(projectId, {
      invoices: [...(project.invoices || []), invoice],
      milestones: project.milestones.map(m => ids.includes(m.id) ? { ...m, invoiceId: invoice.id } : m),
    });
    // In-app notification, email and chat alert are all sent by the server.
    this.projectAction<{ project: FinanceProject; invoice: InvoiceRecord }>(projectId, 'request-invoice', { milestoneIds: ids, milestoneId: ids[0], dueDate, amount: invoice.amount, invoiceId: invoice.id }, 'Requesting invoice');
    return invoice;
  }

  raiseInvoice(
    projectId: string,
    invoiceId: string,
    invoiceNumber: string,
    fileUrl: string,
    amount?: number,
    notes?: string,
    hardwareDocs?: { milestoneId: string; eWayBillUrl?: string; deliveryChallanUrl?: string },
    raisedDate?: string,
    gstRate: 0 | 18 = 18,
    eInvoice?: { irn: string; url?: string; irnDate?: string }
  ): void {
    const project = this.getProjectById(projectId);
    if (!project) return;
    const now = new Date().toISOString();
    const existingInv = (project.invoices || []).find(i => i.id === invoiceId);
    const finalAmount = (typeof amount === 'number' && amount > 0)
      ? amount
      : (existingInv?.amount || 0);

    this.patchLocalProject(projectId, {
      invoices: (project.invoices || []).map(inv => inv.id !== invoiceId ? inv : {
        ...inv,
        number: invoiceNumber,
        amount: finalAmount,
        notes: notes || inv.notes,
        status: 'raised' as const,
        raisedAt: now,
        raisedDate: raisedDate || toLocalYMD(new Date()),
        fileUrl,
        gstDate: calcGstDate(now),
        r1Date: calcR1Date(now),
        gstAmount: gstRate === 0 ? 0 : calcGstAmount(finalAmount),
        gstRate,
        gstStatus: (gstRate === 0 ? 'paid' : 'not_paid') as any,
        tdsStatus: 'not_deducted' as const,
      }),
    });

    const payload: { invoiceNumber: string; fileUrl: string; amount?: number; notes?: string; eWayBillUrl?: string; deliveryChallanUrl?: string; raisedDate?: string; gstRate?: number; irn?: string; irnDate?: string; eInvoiceUrl?: string } = {
      invoiceNumber,
      fileUrl,
    };
    // No-GST invoices (25 Oct 2026): exempt / export / SEZ bills carry no GST.
    if (gstRate === 0) payload.gstRate = 0;
    // E-invoice (25 Oct 2026): the IRN and the signed document travel with the raise when Accounts have them.
    if (eInvoice?.irn) {
      payload.irn = normalizeIrn(eInvoice.irn);
      if (eInvoice.url) payload.eInvoiceUrl = eInvoice.url;
      // Read off the e-invoice's QR with the IRN (2 Nov 2026).
      if (eInvoice.irnDate) payload.irnDate = eInvoice.irnDate;
    }
    // Invoice date as printed (BD targets count the invoice in that month); the server allows up to 60 days back.
    if (raisedDate) payload.raisedDate = raisedDate;
    // Hardware papers travel with the raise action (they used to go through the
    // Admin-only project save and were lost when Accounts raised the invoice).
    if (hardwareDocs?.eWayBillUrl) payload.eWayBillUrl = hardwareDocs.eWayBillUrl;
    if (hardwareDocs?.deliveryChallanUrl) payload.deliveryChallanUrl = hardwareDocs.deliveryChallanUrl;
    if (typeof amount === 'number' && amount > 0) {
      payload.amount = amount;
    }
    if (notes && notes.trim()) {
      payload.notes = notes.trim();
    }

    // In-app notification, email and chat alert are all sent by the server.
    this.projectAction(projectId, 'raise-invoice/' + invoiceId, payload, 'Raising invoice');
  }

  /**
   * Record a payment against a raised invoice. `amount` defaults to the open
   * balance; a smaller amount is a part payment (status 'part_paid') and the
   * invoice keeps accepting payments until the balance is zero. Each payment
   * keeps its own UTR, date and amount.
   *
   * The server's answer is awaited (2 Nov 2026): a UTR already recorded on another bill comes back as a
   * question (`sharedUtr`) the dialog asks before sending it again with `confirmSharedUtr`, and nothing
   * shows as recorded until the server has taken it.
   */
  markInvoicePaid(projectId: string, invoiceId: string, data: {
    utr: string;
    paymentDate: string;
    amount?: number;
    note?: string;
    /** This receipt is the GST only (the client paid the base earlier) — 30 Oct 2026. */
    gstOnly?: boolean;
    /** TDS the client deducted on this payment; it settles the base with the payment. */
    paymentTdsAmount?: number;
    tdsStatus?: 'deducted' | 'not_deducted';
    tdsPct?: number;
    tdsAmount?: number;
    tdsMode?: 'single' | 'multi';
    tdsEntries?: { pct: number; amount: number }[];
    /** One transfer paid this bill and the others already holding its UTR (the person confirmed it). */
    confirmSharedUtr?: boolean;
  }): Promise<RecordPaymentResult> {
    const { tdsMode, tdsEntries, ...rest } = data;
    const body: any = { ...rest };
    if (tdsMode) { body.tdsMode = tdsMode; body.tdsEntries = tdsMode === 'multi' ? [...(tdsEntries || [])] : []; }
    if (body.amount === undefined || body.amount === null || !(Number(body.amount) > 0)) delete body.amount;
    if (!body.gstOnly) delete body.gstOnly;
    if (!(Number(body.paymentTdsAmount) > 0)) delete body.paymentTdsAmount;
    if (!body.confirmSharedUtr) delete body.confirmSharedUtr;
    // In-app notification and email are sent by the server.
    return this.enqueue(projectId, () => this.api<any>('POST', `/projects/${projectId}/mark-paid/${invoiceId}`, body))
      .then((res): RecordPaymentResult => {
        this.storeProject(res?.data ?? res?.project ?? res);
        this.scheduleAuditRefresh();
        return { ok: true };
      })
      .catch((e): RecordPaymentResult => {
        const shared = e?.error?.message?.sharedUtr;
        if (shared && Array.isArray(shared.bills)) return { sharedUtr: shared };
        this.reportError('Recording payment', e);
        this.refreshProjects();
        return null;
      });
  }

  // ── Undo a money entry (2 Nov 2026): Accounts or an Admin, with a reason kept on the bill ──
  /** Take back a payment recorded in Link: it stops counting everywhere and stays on the bill, struck through. */
  reversePayment(projectId: string, invoiceId: string, paymentId: string, reason: string): Promise<FinanceProject | null> {
    return this.projectAction(projectId, `invoices/${invoiceId}/payments/${paymentId}/reverse`, { reason }, 'Undoing the payment');
  }
  /** Take back "GST paid": the bill is "GST not paid" again. */
  undoGstPaid(projectId: string, invoiceId: string, reason: string): Promise<FinanceProject | null> {
    return this.projectAction(projectId, `invoices/${invoiceId}/gst-paid/undo`, { reason }, 'Undoing "GST paid"');
  }
  /** Take back "TDS received": the bill is back on the TDS list. */
  undoTdsReceived(projectId: string, invoiceId: string, reason: string): Promise<FinanceProject | null> {
    return this.projectAction(projectId, `invoices/${invoiceId}/tds-received/undo`, { reason }, 'Undoing "TDS received"');
  }
  /** Correct a raised bill's number or invoice date (a bill is raised once); the old values stay on the bill. */
  correctInvoice(projectId: string, invoiceId: string, data: { invoiceNumber?: string; raisedDate?: string; reason: string }): Promise<FinanceProject | null> {
    return this.projectAction(projectId, `invoices/${invoiceId}/correct`, data, 'Correcting the bill');
  }

  // ── Project hygiene (3 Nov 2026): Accounts and the Admins keep the projects clean ───────────

  /**
   * The live projects that look like a new one (same client GSTIN, legal name or PO number, or a very similar name).
   * Never throws: null when the check could not run, and the form then creates the project as before.
   */
  async findLookAlikes(candidate: { name?: string; gst?: string; legalName?: string; poNumbers?: string[]; excludeId?: string }): Promise<LookAlikes | null> {
    try {
      return (await this.api<LookAlikes>('POST', '/projects/look-alikes', candidate)) || null;
    } catch (e) {
      console.warn('[FinanceService] Look-alike check failed:', e);
      return null;
    }
  }

  /** What folding `foldId` into `keepId` would do, or why it cannot (Admin and Accounts); writes nothing. */
  async mergePreview(keepId: string, body: { foldId: string; billing: MergeBilling; dropRequestIds: string[] }): Promise<MergePreview | null> {
    try {
      return await this.api<MergePreview>('POST', `/projects/${keepId}/merge-preview`, body);
    } catch (e) {
      this.reportError('Previewing the merge', e);
      return null;
    }
  }

  /** Fold one project into another; the folded one leaves the list. */
  async mergeProjects(
    keepId: string,
    body: { foldId: string; billing: MergeBilling; dropRequestIds: string[]; reason: string; keepUpdatedAt: string; foldUpdatedAt: string }
  ): Promise<{ project: FinanceProject; lines: string[]; warnings?: string[]; notMoved?: string[] } | null> {
    const res = await this.projectAction<{ project: FinanceProject; lines: string[]; warnings?: string[]; notMoved?: string[] }>(keepId, 'merge', body, 'Merging the projects');
    if (res) await this.refreshProjects();
    return res;
  }

  /** Accounts (or an Admin) change client details, "Bills from", POs or a bill's due date on the BD's behalf, with a reason. */
  accountsEdit(projectId: string, body: AccountsEditBody): Promise<FinanceProject | null> {
    return this.projectAction(projectId, 'accounts-edit', body, "Changing the project on the BD's behalf");
  }

  /** Ask the project's BD to fix details: a comment and a task for them. */
  askBdToFix(projectId: string, body: { note: string; fields: string[] }): Promise<FinanceProject | null> {
    return this.projectAction(projectId, 'fix-asks', body, 'Asking the BD');
  }

  withdrawFixAsk(projectId: string, askId: string, note = ''): Promise<FinanceProject | null> {
    return this.projectAction(projectId, `fix-asks/${askId}/withdraw`, { note }, 'Withdrawing the ask');
  }

  /** Put the right file on a raised PI or bill; the old one stays in its history. */
  replaceRecordFile(projectId: string, kind: 'pi' | 'invoice', recordId: string, body: { fileUrl: string; reason: string }): Promise<FinanceProject | null> {
    const path = kind === 'pi' ? `pis/${recordId}/replace-file` : `invoices/${recordId}/replace-file`;
    return this.projectAction(projectId, path, body, 'Replacing the file');
  }

  // ── Scope changes (propose → approve → apply) ──────────────────────────────
  /** What the server says about me (GET finance/access/me): the business-head flag set by Admins in BD Targets → Setup → Team replaces the old hard-coded list (20 Oct 2026). */
  me: FinanceMe | null = null;
  private mePromise: Promise<void> | null = null;
  loadMe(force = false): Promise<void> {
    if (!this.authToken) return Promise.resolve();
    if (this.mePromise && !force) return this.mePromise;
    this.mePromise = this.api<FinanceMe>('GET', '/access/me')
      .then(me => { this.me = me || null; this.changed$.next('projects'); })
      .catch(() => { /* keep the previous answer; the server enforces the rules anyway */ });
    return this.mePromise;
  }
  /** Flagged as a business head by an Admin (Setup → Team). */
  get isBusinessHead(): boolean { return !!this.me?.isBusinessHead; }
  /** Who may confirm / drop pipeline deals and group projects, as the server decides it; null until it has answered. */
  get canManageProjects(): boolean | null { return typeof this.me?.canManageProjects === 'boolean' ? this.me.canManageProjects : null; }
  /** Who raises purchase requests, as the server decides it (the department lists until it has answered; founder, 3 Nov 2026). */
  get canRaisePurchase(): boolean {
    if (typeof this.me?.canRaisePurchase === 'boolean') return this.me.canRaisePurchase;
    return PURCHASE_RAISER_DEPARTMENTS.includes((localStorage.getItem('department') || '').toUpperCase());
  }
  /** …a company purchase: the HR head and Admins (founder, 5 Nov 2026). */
  get canRaiseCompanyPurchase(): boolean {
    if (typeof this.me?.canRaiseCompanyPurchase === 'boolean') return this.me.canRaiseCompanyPurchase;
    return this.canRaisePurchase; // no answer yet, or a server without the flag: both kinds
  }
  /** …a project purchase: the BD team and Admins (the HR head raises company purchases only). */
  get canRaiseProjectPurchase(): boolean {
    if (typeof this.me?.canRaiseProjectPurchase === 'boolean') return this.me.canRaiseProjectPurchase;
    return this.canRaisePurchase; // no answer yet, or a server without the flag: both kinds
  }
  /** Who may use "+ Payment request" on the Vendors page. */
  get canRaiseVendorRequest(): boolean {
    if (typeof this.me?.canRaiseVendorRequest === 'boolean') return this.me.canRaiseVendorRequest;
    return !PURCHASE_ONLY_DEPARTMENTS.includes((localStorage.getItem('department') || '').toUpperCase());
  }
  /** …and whether there only for company spends up to ₹10,000 + GST. */
  get vendorRequestInternalOnly(): boolean {
    if (typeof this.me?.vendorRequestInternalOnly === 'boolean') return this.me.vendorRequestInternalOnly;
    return this.canRaiseVendorRequest && !DIRECT_PAYMENT_DEPARTMENTS.includes((localStorage.getItem('department') || '').toUpperCase());
  }
  /** Business heads (the profile flag) and admins approve scope changes; delegates acting for an Admin too. */
  get canApproveScopeChange(): boolean {
    const dept = (localStorage.getItem('department') || '').toUpperCase();
    return dept === 'ADMIN' || this.isBusinessHead || this.delegations.has(POWER.FINANCE_APPROVE_SCOPE_CHANGES);
  }
  /** Admins and Admin delegates: their project changes apply at once; business heads propose like a BD (18 Oct 2026). */
  get isScopeAdmin(): boolean {
    const dept = (localStorage.getItem('department') || '').toUpperCase();
    return dept === 'ADMIN' || this.delegations.has(POWER.FINANCE_APPROVE_SCOPE_CHANGES);
  }
  /** Admins, and anyone acting for an Admin with the credit-note power. */
  get canApproveCreditNotes(): boolean {
    const dept = (localStorage.getItem('department') || '').toUpperCase();
    return dept === 'ADMIN' || this.delegations.has(POWER.FINANCE_APPROVE_CREDIT_NOTES);
  }

  /**
   * Set when the last project save was refused because the project changed after its form was opened
   * (the server's 409, 2 Nov 2026); the page offers a reload instead of the usual error toast.
   */
  lastProjectClash: string | null = null;

  /** `expectedUpdatedAt`: the project's `updatedAt` when the form was opened — the server refuses the change if it moved since. */
  proposeProjectChange(projectId: string, payload: { project: Record<string, any>; reason: string; type: string; poNumber?: string; poFileUrl?: string; poLockReason?: string; expectedUpdatedAt?: string }): Promise<{ request: ProjectChangeRequest; project: FinanceProject | null } | null> {
    this.lastProjectClash = null;
    return this.api<{ request: ProjectChangeRequest; project: FinanceProject }>('POST', `/projects/${projectId}/change-requests`, payload)
      .then(res => {
        if (res?.project) this.storeProject(migrateProjectToV2(res.project));
        this.scheduleAuditRefresh();
        this.changed$.next('projects');
        return res ? { request: res.request, project: res.project || null } : null;
      })
      .catch(e => {
        if (FinanceService.isChangedSince(e)) { this.lastProjectClash = FinanceService.errorMessage(e); return null; }
        this.reportError('Proposing scope change', e);
        return null;
      });
  }

  listProjectChanges(projectId: string): Promise<ProjectChangeRequest[]> {
    return this.api<ProjectChangeRequest[]>('GET', `/projects/${projectId}/change-requests`).then(r => r || []).catch(() => []);
  }

  listChangeRequests(status?: string): Promise<ProjectChangeRequest[]> {
    return this.api<ProjectChangeRequest[]>('GET', '/change-requests', undefined, status ? { status } : undefined).then(r => r || []).catch(() => []);
  }

  private decideChange(requestId: string, decision: 'approve' | 'reject', body: Record<string, any>, label: string) {
    return this.api<{ request: ProjectChangeRequest; project: FinanceProject | null }>('POST', `/change-requests/${requestId}/${decision}`, body)
      .then(res => {
        if (res?.project) this.storeProject(migrateProjectToV2(res.project));
        this.scheduleAuditRefresh();
        this.changed$.next('projects');
        return res || null;
      })
      .catch(e => { this.reportError(label, e); return null; });
  }
  approveProjectChange(requestId: string, note?: string) { return this.decideChange(requestId, 'approve', { note }, 'Approving scope change'); }
  rejectProjectChange(requestId: string, reason: string) { return this.decideChange(requestId, 'reject', { reason }, 'Rejecting scope change'); }

  // ── Archive / restore (Admin) ──────────────────────────────────────────────
  /** Archived projects (soft-deleted); Admin only. */
  async listArchivedProjects(): Promise<FinanceProject[]> {
    try {
      const rows = await this.api<FinanceProject[]>('GET', '/projects/archived');
      return (rows || []).map(migrateProjectToV2);
    } catch (e) {
      this.reportError('Loading archived projects', e);
      return [];
    }
  }

  /** Bring an archived project back; Admin only. */
  // ── Draft invoice check + client quotations ──────────────────────────────
  sendInvoiceDraft(projectId: string, invoiceId: string, fileUrl: string, note: string): Promise<any> {
    return this.projectAction(projectId, `invoices/${invoiceId}/draft`, { fileUrl, note }, 'Sending draft invoice');
  }
  approveInvoiceDraft(projectId: string, invoiceId: string, note = ''): Promise<any> {
    return this.projectAction(projectId, `invoices/${invoiceId}/draft/approve`, { note }, 'Approving draft invoice');
  }
  requestInvoiceDraftChanges(projectId: string, invoiceId: string, note: string): Promise<any> {
    return this.projectAction(projectId, `invoices/${invoiceId}/draft/changes`, { note }, 'Asking for draft changes');
  }
  addQuotation(projectId: string, fileUrl: string, label: string): Promise<any> {
    return this.projectAction(projectId, 'quotations', { fileUrl, label }, 'Adding quotation');
  }

  // ── Pipeline deals, client documents, groups (17 Oct 2026) ─────────────
  /** Save a pipeline deal's estimates directly (the server allows this only while the deal is in the pipeline). */
  saveProjectDirect(projectId: string, updates: Partial<FinanceProject>, expectedUpdatedAt?: string): Promise<FinanceProject | null> {
    const current = this.getProjectById(projectId);
    if (!current) return Promise.resolve(null);
    this.lastProjectClash = null;
    const next = migrateProjectToV2({ ...current, ...updates } as FinanceProject);
    const body = { ...this.projectBody(next, ['invoices']), ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}) };
    return this.enqueue(projectId, () => this.api<FinanceProject>('PUT', `/projects/${projectId}`, body))
      .then(p => { if (p) this.storeProject(migrateProjectToV2(p)); this.scheduleAuditRefresh(); this.changed$.next('projects'); return p || null; })
      .catch(e => {
        if (FinanceService.isChangedSince(e)) { this.lastProjectClash = FinanceService.errorMessage(e); return null; }
        this.reportError('Saving estimates', e);
        return null;
      });
  }
  /** The deal is won: becomes an active project (same id); Accounts can bill it. */
  confirmPipeline(projectId: string, body: { closedOn?: string; quotationUrl?: string; quotationLabel?: string; note?: string }): Promise<FinanceProject | null> {
    return this.projectAction<FinanceProject>(projectId, 'confirm', body, 'Confirming the deal');
  }
  /** The deal is lost: leaves the pipeline as a cancelled project. */
  dropPipeline(projectId: string, reason: string): Promise<FinanceProject | null> {
    return this.projectAction<FinanceProject>(projectId, 'drop', { reason }, 'Marking the deal lost');
  }
  setClientDocument(projectId: string, kind: string, fileUrl: string, name = ''): Promise<FinanceProject | null> {
    return this.projectAction<FinanceProject>(projectId, `client-documents/${kind}`, { fileUrl, name }, 'Saving client document');
  }
  removeClientDocument(projectId: string, kind: string): Promise<FinanceProject | null> {
    return this.enqueue(projectId, () => this.api<FinanceProject>('DELETE', `/projects/${projectId}/client-documents/${kind}`))
      .then(p => { if (p) this.storeProject(migrateProjectToV2(p)); this.scheduleAuditRefresh(); this.changed$.next('projects'); return p || null; })
      .catch(e => { this.reportError('Removing client document', e); return null; });
  }
  /** The groups the signed-in person may see; null when the server could not be reached (keep the last list). */
  listProjectGroups(): Promise<ProjectGroup[] | null> {
    return this.api<ProjectGroup[]>('GET', '/project-groups').then(r => r || []).catch(e => { console.warn('[FinanceService] Loading groups failed:', e); return null; });
  }
  /**
   * A group change: a refusal shows its message, a 403 included (1 Nov 2026). The projects page reloads the
   * projects and the groups itself, once each, after putting the changed group in place — two reloads used to race.
   */
  private afterGroupChange<T>(label: string, p: Promise<T>): Promise<T | null> {
    return p
      .then(r => { this.scheduleAuditRefresh(); return r; })
      .catch(e => { this.reportRefusal(label, e); return null; });
  }
  createProjectGroup(body: { name: string; projectIds: string[]; note?: string }): Promise<ProjectGroup | null> {
    return this.afterGroupChange('Grouping projects', this.api<ProjectGroup>('POST', '/project-groups', body));
  }
  updateProjectGroup(groupId: string, body: { name?: string; projectIds?: string[]; note?: string }): Promise<ProjectGroup | null> {
    return this.afterGroupChange('Updating the group', this.api<ProjectGroup>('PUT', `/project-groups/${groupId}`, body));
  }
  removeProjectGroup(groupId: string): Promise<any> {
    return this.afterGroupChange('Dissolving the group', this.api<any>('DELETE', `/project-groups/${groupId}`));
  }
  /** Like reportError, but a business refusal (403) shows its message too: nothing may look done when the server said no. */
  private reportRefusal(context: string, e: any): void {
    if (e?.status === 403 && !isSessionRejected(e)) {
      console.warn(`[FinanceService] ${context} refused:`, e);
      this.toast.error(`${context} failed: ${FinanceService.errorMessage(e)}`, { duration: 6000 });
      return;
    }
    this.reportError(context, e);
  }

  // ── Pins (1 Nov 2026): each person's projects pinned to the top of their list, kept on the server ──
  /** The signed-in person's pins, the latest first; null when the server could not be reached. */
  listProjectPins(): Promise<ProjectPin[] | null> {
    return this.api<ProjectPin[]>('GET', '/project-pins').then(r => (Array.isArray(r) ? r : [])).catch(e => { console.warn('[FinanceService] Loading pins failed:', e); return null; });
  }
  pinProject(projectId: string): Promise<{ projectId: string; pinned: boolean; pinnedAt?: string } | null> {
    return this.api<{ projectId: string; pinned: boolean; pinnedAt?: string }>('POST', `/projects/${projectId}/pin`)
      .catch(e => { this.reportRefusal('Pinning the project', e); return null; });
  }
  unpinProject(projectId: string): Promise<{ projectId: string; pinned: boolean } | null> {
    return this.api<{ projectId: string; pinned: boolean }>('POST', `/projects/${projectId}/unpin`)
      .catch(e => { this.reportRefusal('Unpinning the project', e); return null; });
  }
  /** Plan feed (page to follow): milestones and invoices due per month. */
  plan(from?: string, months = 6): Promise<PlanFeed | null> {
    return this.api<PlanFeed>('GET', '/plan', undefined, { from, months }).then(r => r || null).catch(() => null);
  }

  // ── GST by invoice month (2 Nov 2026): Accounts and Admins only; null when the server says no or is unreachable ──
  /** GST to deposit: the month now due, the running month, overdue months and the last six. */
  gstDeposit(): Promise<GstDeposit | null> {
    return this.api<GstDeposit>('GET', '/gst/deposit').then(r => r || null).catch(() => null);
  }
  /** The GST each client still owes, per bill, with totals by invoice month and by age. */
  gstWithClients(): Promise<GstWithClients | null> {
    return this.api<GstWithClients>('GET', '/gst/with-clients').then(r => r || null).catch(() => null);
  }

  // ── Retainers ────────────────────────────────────────────────────────────
  /** Stop / restart the automatic invoice requests of one retainer (no approval needed). */
  setRetainerPaused(projectId: string, seriesId: string, paused: boolean): Promise<FinanceProject | null> {
    return this.api<FinanceProject>('POST', `/projects/${projectId}/recurring/${seriesId}/${paused ? 'pause' : 'resume'}`)
      .then(p => { if (p) this.storeProject(migrateProjectToV2(p)); this.scheduleAuditRefresh(); this.changed$.next('projects'); return p || null; })
      .catch(e => { this.reportError(paused ? 'Pausing retainer' : 'Resuming retainer', e); return null; });
  }
  /** Admin: run today's retainer requests now (the server also does this daily at 9 AM). */
  runRetainersNow(): Promise<{ requested: string[] } | null> {
    return this.api<{ requested: string[] }>('POST', '/recurring/run')
      .then(r => { this.refreshProjects?.(); this.changed$.next('projects'); return r || null; })
      .catch(e => { this.reportError('Running retainer requests', e); return null; });
  }
  /** The retainer a milestone belongs to, if any. */
  seriesOf(p: FinanceProject | null | undefined, milestoneId: string | undefined): RetainerSeries | null {
    if (!p || !milestoneId) return null;
    const ms = (p.milestones || []).find(m => m.id === milestoneId);
    return ms?.seriesId ? (p.recurringSeries || []).find(r => r.id === ms.seriesId) || null : null;
  }

  restoreProject(id: string): Promise<FinanceProject | null> {
    return this.api<FinanceProject>('POST', `/projects/${id}/restore`)
      .then(p => { if (p) this.storeProject(migrateProjectToV2(p)); this.scheduleAuditRefresh(); this.changed$.next('projects'); return p || null; })
      .catch(e => { this.reportError('Restoring project', e); return null; });
  }

  // ── From the books (27 Oct 2026): Admin / Accounts check a project the opening-data load brought in ──
  /** They have checked this imported project against the books: the "From the books" marker goes from its card. */
  markProjectImportChecked(projectId: string): Promise<FinanceProject | null> {
    return this.projectAction<FinanceProject>(projectId, 'import-checked', {}, 'Marking the project as checked');
  }
  /** Undo that check: the marker comes back. */
  unmarkProjectImportChecked(projectId: string): Promise<FinanceProject | null> {
    return this.projectAction<FinanceProject>(projectId, 'import-unchecked', {}, 'Undoing the check');
  }

  // ── Comments on a project (30 Oct 2026): shown in the app only — the server sends no push, mail or chat line ──
  /** The project's comments, oldest first; null when they could not be loaded (the error is shown). */
  listProjectComments(projectId: string): Promise<ProjectComment[] | null> {
    return this.api<ProjectComment[]>('GET', `/projects/${projectId}/comments`)
      .then(r => (Array.isArray(r) ? r : []))
      .catch(e => { this.reportError('Loading comments', e); return null; });
  }
  /** Post a comment; answers the new comment, or null when refused (the error is shown). */
  addProjectComment(projectId: string, text: string): Promise<ProjectComment | null> {
    return this.api<ProjectComment>('POST', `/projects/${projectId}/comments`, { text })
      .then(c => { this.scheduleAuditRefresh(); return c || null; })
      .catch(e => { this.reportError('Posting the comment', e); return null; });
  }
  /** The signed-in person has opened the project's comments: nothing there is new to them any more. Quiet on failure. */
  markProjectCommentsSeen(projectId: string): Promise<boolean> {
    return this.api<{ projectId: string; seenAt: string }>('POST', `/projects/${projectId}/comments/seen`)
      .then(r => !!r)
      .catch(() => false);
  }
  /** Counts per visible project for the cards, in one call; null when unavailable (cards then show no comment pill). */
  projectCommentSummary(): Promise<Record<string, ProjectCommentCounts> | null> {
    return this.api<Record<string, ProjectCommentCounts>>('GET', '/project-comments/summary')
      .then(r => (r && typeof r === 'object' ? r : null))
      .catch(() => null);
  }
  /** Admin / Accounts: the comment is dealt with; the note (optional) says how. */
  resolveProjectComment(commentId: string, resolution: string): Promise<ProjectComment | null> {
    return this.api<ProjectComment>('POST', `/project-comments/${commentId}/resolve`, { resolution })
      .then(c => { this.scheduleAuditRefresh(); return c || null; })
      .catch(e => { this.reportError('Resolving the comment', e); return null; });
  }
  /** Admin / Accounts: open the comment again. */
  reopenProjectComment(commentId: string): Promise<ProjectComment | null> {
    return this.api<ProjectComment>('POST', `/project-comments/${commentId}/reopen`)
      .then(c => { this.scheduleAuditRefresh(); return c || null; })
      .catch(e => { this.reportError('Reopening the comment', e); return null; });
  }

  // ── Sent to client / credit notes (all server-driven) ─────────────────────
  /** BD ticks that the raised invoice went to the client (status raised → sent). */
  markInvoiceSent(projectId: string, invoiceId: string, sent: boolean, sentDate?: string): Promise<FinanceProject | null> {
    const project = this.getProjectById(projectId);
    if (project) {
      this.patchLocalProject(projectId, {
        invoices: (project.invoices || []).map(inv => inv.id !== invoiceId ? inv : {
          ...inv,
          sentToClientAt: sent ? (sentDate || toLocalYMD(new Date())) : '',
          sentToClientBy: sent ? (localStorage.getItem('username') || '') : '',
          status: (sent && inv.status === 'raised') ? 'sent' : (!sent && inv.status === 'sent') ? 'raised' : inv.status,
        }),
      });
    }
    return this.projectAction(projectId, `invoices/${invoiceId}/mark-sent`, { sent, sentDate }, sent ? 'Marking invoice as sent' : 'Un-marking invoice as sent');
  }

  /** Opening data from the directors' dashboard (25 Oct 2026): dry run by default; Admin only. */
  importOpeningData(body: { importRef: string; dryRun: boolean; projects: any[] }): Promise<ImportReport | null> {
    return this.api<any>('POST', '/import/opening-data', body).then(res => (res as any)?.data ?? res).catch(err => { this.toast?.error?.(err?.error?.message || 'Import failed'); return null; });
  }

  /** Accounts attach (or replace) the e-invoice on a raised invoice: the portal's IRN and, optionally, the signed document. */
  attachEInvoice(projectId: string, invoiceId: string, irn: string, url?: string, irnDate?: string): Promise<FinanceProject | null> {
    return this.projectAction(projectId, `invoices/${invoiceId}/einvoice`, { irn: normalizeIrn(irn), eInvoiceUrl: url || '', ...(irnDate ? { irnDate } : {}) }, 'Attaching the e-invoice');
  }

  /** BD requests a credit note; Admin must approve before Accounts can issue it. */
  requestCreditNote(projectId: string, invoiceId: string, amount: number, reason: string): Promise<FinanceProject | null> {
    return this.projectAction<{ project: FinanceProject; note: CreditDebitNote }>(projectId, `invoices/${invoiceId}/request-credit-note`, { amount, reason }, 'Requesting credit note')
      .then(res => { this.refresh(['credit-debit-notes'], true); return (res as any)?.project || null; });
  }

  private creditNoteAction(noteId: string, action: 'approve' | 'reject' | 'issue', body: Record<string, any>, label: string): Promise<FinanceProject | null> {
    return this.api<{ project: FinanceProject; note: CreditDebitNote }>('POST', `/credit-notes/${noteId}/${action}`, body)
      .then(res => {
        if (res?.project) this.storeProject(res.project);
        this.scheduleAuditRefresh();
        this.refresh(['credit-debit-notes'], true);
        return res?.project || null;
      })
      .catch(e => { this.reportError(label, e); this.refreshProjects(); return null; });
  }
  approveCreditNote(noteId: string, note?: string) { return this.creditNoteAction(noteId, 'approve', { note }, 'Approving credit note'); }
  rejectCreditNote(noteId: string, reason: string) { return this.creditNoteAction(noteId, 'reject', { reason }, 'Rejecting credit note'); }
  issueCreditNote(noteId: string, data: { noteNumber: string; fileUrl: string; amount?: number; issuedDate?: string }) {
    return this.creditNoteAction(noteId, 'issue', data, 'Issuing credit note');
  }

  // Mark GST as paid (Accounts action)
  markGstPaid(projectId: string, invoiceId: string, gstPaidDate?: string, gstUtr?: string): void {
    const project = this.getProjectById(projectId);
    if (!project) return;
    const paidDate = gstPaidDate || toLocalYMD(new Date());
    this.patchLocalProject(projectId, {
      invoices: (project.invoices || []).map(inv =>
        inv.id === invoiceId ? { ...inv, gstStatus: 'paid' as const, gstPaidDate: paidDate, ...(gstUtr ? { gstUtr } : {}) } : inv
      ),
    });
    this.projectAction(projectId, 'mark-gst-paid/' + invoiceId, { gstPaidDate: paidDate, gstUtr }, 'Marking GST paid');
  }

  // Mark TDS as received in portal (Accounts only, after 3-month window)
  markTdsReceived(projectId: string, invoiceId: string, details?: { tdsReferenceNumber?: string; tdsReceivedDate?: string }): void {
    const project = this.getProjectById(projectId);
    if (!project) return;
    const extra = { tdsReceivedDate: details?.tdsReceivedDate || toLocalYMD(new Date()), ...(details?.tdsReferenceNumber ? { tdsReferenceNumber: details.tdsReferenceNumber } : {}) };
    this.patchLocalProject(projectId, {
      invoices: (project.invoices || []).map(inv =>
        inv.id === invoiceId ? { ...inv, tdsReceivedInPortal: true, ...extra } : inv
      ),
    });
    this.projectAction(projectId, 'mark-tds-received/' + invoiceId, extra, 'Marking TDS received');
  }

  // ── Pending Actions (for Accounts dashboard) ───────────────────────────────

  getPendingActions(): PendingAction[] {
    const dept = localStorage.getItem('department') || '';
    const isAccounts = ['ACCOUNTS', 'ADMIN'].includes(dept);
    const projects = this.getProjects();
    const actions: PendingAction[] = [];

    // Raise-PI / raise-invoice / record-payment are ACCOUNTS actions.
    // Other roles only REQUEST these — the pending action isn't theirs to see.
    projects.forEach(p => {
      if (!isAccounts) return;
      // PI requested — Accounts send the draft, then raise the final PI once the BD side approved it
      (p.pis || []).forEach(pi => {
        if (pi.status !== 'requested') return;
        const ds = pi.draft?.status;
        if (ds === 'sent') return; // with the BD for checking
        const msLabels = this.milestoneLabels(p, pi.milestoneIds || []);
        const labelStr = msLabels.length ? ` (${msLabels.join(', ')})` : '';
        const what = ds === 'approved' ? 'Raise the final PI' : ds === 'changes_requested' ? 'Send a corrected draft PI' : 'Send a draft PI';
        actions.push({
          type: ds === 'approved' ? 'raise_pi' : 'send_pi_draft',
          projectId: p.id,
          projectName: p.name,
          piId: pi.id,
          description: `${what} for "${p.name}"${labelStr}${ds === 'changes_requested' && pi.draft?.reviewNote ? ' (' + pi.draft.reviewNote + ')' : ''}`,
          requestedBy: pi.requestedBy || p.bdOwnerName,
          requestedAt: pi.requestedAt,
          amount: pi.amount || p.totalAmount,
        });
      });
      // Invoice pending — Accounts needs to raise
      (p.invoices || []).forEach(inv => {
        if (inv.status === 'pending') {
          const series = this.seriesOf(p, inv.milestoneId);
          const isRecurring = !!series;
          const periodLabel = invoiceMilestoneLabel(p, inv);
          const recurringLabel = series ? (periodLabel || series.label) : '';
          const ds = inv.draft?.status;
          if (ds === 'sent') return; // with the BD for checking
          const what = ds === 'approved' ? 'Raise invoice' : ds === 'changes_requested' ? 'Send a corrected draft' : 'Send draft invoice';
          actions.push({
            type: ds === 'approved' ? 'raise_invoice' : 'send_draft',
            projectId: p.id,
            projectName: p.name,
            invoiceId: inv.id,
            description: isRecurring
              ? `${what} for "${p.name}" — ${recurringLabel}`
              : `${what} for "${p.name}"${periodLabel ? ' — ' + periodLabel : ''}${ds === 'changes_requested' && inv.draft?.reviewNote ? ' (' + inv.draft.reviewNote + ')' : ''}`,
            requestedBy: inv.autoRequested ? 'Requested automatically' : inv.requestedBy,
            requestedAt: inv.requestedAt,
            amount: inv.amount,
            isRecurring,
            recurringLabel,
          });
        }
        // A bill raised in Link without its e-invoice number (2 Nov 2026): one item until the IRN is in.
        if (irnMissing(p, inv)) {
          actions.push({
            type: 'add_irn',
            projectId: p.id,
            projectName: p.name,
            invoiceId: inv.id,
            description: `Add the e-invoice number (IRN) for bill #${inv.number || ''} — "${p.name}"`,
            requestedAt: inv.raisedAt,
            amount: inv.amount,
          });
        }
        // Same rule as All Invoices' queue: raised, sent to the client or part-paid, with money still owed.
        if (['raised', 'sent', 'part_paid'].includes(inv.status) && invoiceBalance(inv) > 0) {
          actions.push({
            type: 'mark_paid',
            projectId: p.id,
            projectName: p.name,
            invoiceId: inv.id,
            description: `Mark payment for invoice #${inv.number} — "${p.name}"`,
            requestedAt: inv.raisedAt,
            amount: inv.amount,
          });
        }
      });
    });

    // BD / SALES: chase overdue client payments — their real action in this flow
    if (['BD', 'BD-LEAD', 'SALES', 'SALES-LEAD', 'ASSISTANT-SALES', 'ADMIN'].includes(dept)) {
      this.getOverdueInvoices().forEach(inv => {
        actions.push({
          type: 'follow_up',
          projectName: inv.projectName,
          invoiceId: inv.id,
          description: `Follow up payment for "${inv.projectName}" — ${inv.daysOverdue} days overdue`,
          requestedAt: inv.dueDate,
          amount: inv.amount,
        });
      });
    }

    // ADMIN (or a delegate with the power): credit note requests waiting for approval
    if (this.canApproveCreditNotes) {
      projects.forEach(p => (p.invoices || []).forEach(inv => {
        const cn = inv.creditNoteRequest;
        if (cn && cn.status === 'requested') {
          actions.push({
            type: 'approve_credit_note',
            projectId: p.id,
            projectName: p.name,
            invoiceId: inv.id,
            description: `Approve credit note ₹${(cn.amount || 0).toLocaleString('en-IN')} on invoice #${inv.number || ''} — "${p.name}"`,
            requestedBy: cn.requestedBy,
            requestedAt: cn.requestedAt,
            amount: cn.amount,
          });
        }
      }));
    }

    // ACCOUNTS: approved credit notes to issue in Tally and upload
    if (isAccounts) {
      projects.forEach(p => (p.invoices || []).forEach(inv => {
        const cn = inv.creditNoteRequest;
        if (cn && cn.status === 'approved') {
          actions.push({
            type: 'issue_credit_note',
            projectId: p.id,
            projectName: p.name,
            invoiceId: inv.id,
            description: `Issue credit note ₹${(cn.amount || 0).toLocaleString('en-IN')} on invoice #${inv.number || ''} — "${p.name}" (approved by ${cn.approvedBy || 'Admin'})`,
            requestedBy: cn.requestedBy,
            requestedAt: cn.approvedAt || cn.requestedAt,
            amount: cn.amount,
          });
        }
      }));
    }

    // BD / SALES: raised invoices not yet sent to the client
    if (['BD', 'BD-LEAD', 'SALES', 'SALES-LEAD', 'ASSISTANT-SALES', 'ADMIN'].includes(dept)) {
      projects.forEach(p => (p.invoices || []).forEach(inv => {
        if (inv.status === 'raised' && !inv.sentToClientAt) {
          actions.push({
            type: 'send_invoice',
            projectId: p.id,
            projectName: p.name,
            invoiceId: inv.id,
            description: `Send invoice #${inv.number || ''} to the client — "${p.name}" (raised ${inv.raisedAt ? new Date(inv.raisedAt).toLocaleDateString('en-IN') : ''}), then tick "Sent to client"`,
            requestedAt: inv.raisedAt,
            amount: inv.amount,
          });
        }
      }));
    }

    // BD / SALES: upload client PO once the PI has been raised
    if (['BD', 'BD-LEAD', 'SALES', 'SALES-LEAD', 'ASSISTANT-SALES', 'ADMIN'].includes(dept)) {
      projects.forEach(p => {
        if ((p.pis || []).some(pi => pi.status === 'raised') && this.poSummary(p) === 'none') {
          const latestRaisedPI = [...(p.pis || [])].reverse().find(pi => pi.status === 'raised');
          actions.push({
            type: 'upload_po',
            projectId: p.id,
            projectName: p.name,
            description: `Upload PO for "${p.name}" — PI raised, awaiting client PO`,
            requestedAt: latestRaisedPI?.raisedAt,
          });
        }
      });
    }

    // Vendor payment requests pending at current user's department
    if (hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) {
      this.getVendorRequests().forEach(r => {
        const act = r.myAction || '';
        if (!act) return;
        // A purchase's quote pick is listed as the purchase's decision (the dashboard adds those from the server).
        if (act === 'select' && r.purchaseRequestId) return;
        const total = (Number(r.amount) || 0) + (r.hasGst ? Number(r.gstAmount) || 0 : 0);
        const what = act === 'select' ? `Pick a vendor quote for "${r.description}"`
          : act === 'resubmit' ? `Sent back: "${r.description}" — ${r.sentBack?.note || 'update and resubmit'}`
            : act === 'pay' ? (r.status === 'base_paid' ? `Pay GST to "${r.vendorName}" once the tax invoice is in` : `Pay vendor "${r.vendorName}" ₹${total.toLocaleString('en-IN')}`)
              : `Approve vendor payment ₹${total.toLocaleString('en-IN')} for "${r.vendorName}"`;
        actions.push({ type: 'approve_vendor', requestId: r.id, purchaseRequestId: r.purchaseRequestId || undefined, description: what, requestedBy: r.createdByName, requestedAt: r.createdAt, amount: total });
      });
    }

    // BD side: drafts waiting for my check (requester, creator, owner, BD team; business heads and Admins too).
    projects.forEach(p => {
      (p.pis || []).forEach(pi => {
        if (pi.status !== 'requested' || !this.canCheckDraftOf(p, pi)) return;
        const msLabels = this.milestoneLabels(p, pi.milestoneIds || []);
        actions.push({
          type: 'check_pi_draft',
          projectId: p.id,
          projectName: p.name,
          piId: pi.id,
          description: `Check the draft PI for "${p.name}"${msLabels.length ? ' — ' + msLabels.join(', ') : ''}`,
          requestedBy: pi.draft?.sentBy,
          requestedAt: pi.draft?.sentAt,
          amount: pi.amount,
        });
      });
      (p.invoices || []).forEach(inv => {
        if (inv.status !== 'pending' || !this.canCheckDraftOf(p, inv)) return;
        const label = invoiceMilestoneLabel(p, inv);
        actions.push({
          type: 'check_draft',
          projectId: p.id,
          projectName: p.name,
          invoiceId: inv.id,
          description: `Check the draft invoice for "${p.name}"${label ? ' — ' + label : ''}`,
          requestedBy: inv.draft?.sentBy,
          requestedAt: inv.draft?.sentAt,
          amount: inv.amount,
        });
      });
    });

    return actions.sort((a, b) =>
      new Date(b.requestedAt || 0).getTime() - new Date(a.requestedAt || 0).getTime()
    );
  }

  // ── GST / TDS Stats ────────────────────────────────────────────────────────

  getTdsPendingStats(): { totalTdsPending: number; invoices: (InvoiceRecord & { projectName: string })[] } {
    const projects = this.getProjects();
    const pending: (InvoiceRecord & { projectName: string })[] = [];
    let total = 0;
    projects.forEach(p => {
      (p.invoices || []).forEach(inv => {
        if (inv.tdsStatus === 'deducted' && !inv.tdsReceivedInPortal) {
          total += inv.tdsAmount || 0;
          pending.push({ ...inv, projectName: p.name });
        }
      });
    });
    return { totalTdsPending: total, invoices: pending };
  }

  // ── Vendors ────────────────────────────────────────────────────────────────

  getVendors(): FinanceVendor[] {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) return [];
    const raw = localStorage.getItem(KEYS.VENDORS);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') {
        if (Array.isArray(parsed.vendors)) return parsed.vendors;
        if (Array.isArray(parsed.data)) return parsed.data;
      }
      return [];
    } catch {
      return [];
    }
  }

  saveVendors(vendors: FinanceVendor[]): void {
    localStorage.setItem(KEYS.VENDORS, JSON.stringify(Array.isArray(vendors) ? vendors : []));
  }

  getVendorById(id: string): FinanceVendor | null {
    return this.getVendors().find(v => v.id === id) || null;
  }

  createVendor(data: Omit<FinanceVendor, 'id' | 'createdAt'>): FinanceVendor | null {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) return null;
    const vendor: FinanceVendor = { ...data, id: this.generateId(), createdAt: new Date().toISOString() };
    const vendors = this.getVendors();
    vendors.unshift(vendor);
    this.saveVendors(vendors);
    this.enqueue('vendor:' + vendor.id, () => this.api<FinanceVendor>('POST', '/vendors', vendor))
      .then(saved => this.upsertStore(KEYS.VENDORS, saved, 'vendors'))
      .catch(e => this.reportError('Creating vendor', e));
    return vendor;
  }

  updateVendor(id: string, updates: Partial<FinanceVendor>): void {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) return;
    this.saveVendors(this.getVendors().map(v => v.id === id ? { ...v, ...updates } : v));
    this.enqueue('vendor:' + id, () => this.api<FinanceVendor>('PUT', `/vendors/${id}`, updates))
      .then(saved => this.upsertStore(KEYS.VENDORS, saved, 'vendors'))
      .catch(e => this.reportError('Updating vendor', e));
  }

  deleteVendor(id: string): void {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) return;
    this.saveVendors(this.getVendors().filter(v => v.id !== id));
    this.enqueue('vendor:' + id, () => this.api('DELETE', `/vendors/${id}`))
      .catch(e => this.reportError('Deleting vendor', e));
  }

  // ── Vendor Payment Requests ────────────────────────────────────────────────

  getVendorRequests(): VendorPaymentRequest[] {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) return [];
    const raw = localStorage.getItem(KEYS.VENDOR_REQUESTS);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') {
        if (Array.isArray(parsed.requests)) return parsed.requests;
        if (Array.isArray(parsed.vendorRequests)) return parsed.vendorRequests;
        if (Array.isArray(parsed.data)) return parsed.data;
      }
      return [];
    } catch {
      return [];
    }
  }

  saveVendorRequests(requests: VendorPaymentRequest[]): void {
    localStorage.setItem(KEYS.VENDOR_REQUESTS, JSON.stringify(Array.isArray(requests) ? requests : []));
  }


  /** The server snapshots a request's chain (approvalChain) when the quote is picked; a request without one goes straight to Accounts. The hard-coded chains left both repos on 22 Oct 2026. */
  getFlowChain(_flowId: string): string[] {
    return ['ACCOUNTS'];
  }

  // Get which department a request is currently pending at
  getRequestPendingDepartment(req: VendorPaymentRequest): string | null {
    if (req.status === 'rejected' || req.status === 'payment_released') return null;
    const chain = req.approvalChain || this.getFlowChain(req.approvalFlow);
    const idx = req.currentStepIndex ?? 0;
    if (idx >= chain.length) return null;
    return chain[idx];
  }

  // Check if a department can act on a request
  canDepartmentAct(req: VendorPaymentRequest, department: string): boolean {
    const pending = this.getRequestPendingDepartment(req);
    if (!pending) return false;
    // ADMIN can act on any step
    if (department === 'ADMIN' || department === 'DIRECTOR') return true;
    if (pending === department) return true;
    // Handle aliases
    if (['SALES', 'BD-LEAD', 'SALES-LEAD'].includes(department) && ['SALES', 'BD-LEAD', 'SALES-LEAD'].includes(pending)) return true;
    if (['HR-HEAD', 'HR-PROCUREMENT', 'HR'].includes(department) && ['HR-HEAD', 'HR-PROCUREMENT', 'HR'].includes(pending)) return true;
    return false;
  }

  // Check if a department is in a request's approval chain
  isDepartmentInChain(req: VendorPaymentRequest, department: string): boolean {
    const chain = req.approvalChain || this.getFlowChain(req.approvalFlow);
    if (department === 'ADMIN' || department === 'DIRECTOR') return true; // admins see everything
    if (chain.includes(department)) return true;
    if (['SALES', 'BD-LEAD', 'SALES-LEAD', 'BD'].includes(department) &&
      chain.some(d => ['SALES', 'BD-LEAD', 'SALES-LEAD', 'BD'].includes(d))) return true;
    if (['HR-HEAD', 'HR-PROCUREMENT', 'HR'].includes(department) &&
      chain.some(d => ['HR-HEAD', 'HR-PROCUREMENT', 'HR'].includes(d))) return true;
    return req.createdByDepartment === department;
  }

  getVendorRequestTabCounts(requests?: VendorPaymentRequest[]): Record<string, number> {
    const list = requests || this.getVendorRequests();
    return {
      all: list.length,
      pending: list.filter(r => r.status === 'in_progress' || r.status === 'pending').length,
      SALES: list.filter(r => {
        const p = this.getRequestPendingDepartment(r);
        return p === 'SALES' || p === 'BD-LEAD' || p === 'SALES-LEAD';
      }).length,
      'BD-LEAD': list.filter(r => {
        const p = this.getRequestPendingDepartment(r);
        return p === 'SALES' || p === 'BD-LEAD' || p === 'SALES-LEAD';
      }).length,
      'HR-HEAD': list.filter(r => {
        const p = this.getRequestPendingDepartment(r);
        return p === 'HR-HEAD' || p === 'HR-PROCUREMENT' || p === 'HR';
      }).length,
      'HR-PROCUREMENT': list.filter(r => {
        const p = this.getRequestPendingDepartment(r);
        return p === 'HR-HEAD' || p === 'HR-PROCUREMENT' || p === 'HR';
      }).length,
      ADMIN: list.filter(r => {
        const pending = this.getRequestPendingDepartment(r);
        return pending === 'ADMIN' || pending === 'DIRECTOR';
      }).length,
      ACCOUNTS: list.filter(r => this.getRequestPendingDepartment(r) === 'ACCOUNTS').length,
      released: list.filter(r => r.status === 'payment_released').length,
      rejected: list.filter(r => r.status === 'rejected').length,
    };
  }

  /** Attach a missing paper (quote / PI / tax invoice / delivery challan) at any stage. Never replaces one. */
  addVendorRequestDocument(id: string, type: 'quote' | 'pi' | 'invoice' | 'challan', url: string): Promise<VendorPaymentRequest | null> {
    return this.vendorRequestAction(id, 'documents', { type, url }, 'Adding document');
  }

  // ── Vendor payments rebuild (11 Oct 2026): server-driven ─────────────────
  /** `withInternal` adds the route of the caller's department (`kind: 'internal'`) so the request form can show its steps; it is never a flow to pick. */
  listApprovalFlows(all = false, withInternal = false): Promise<ApprovalFlow[]> {
    const params = all ? { all: 1 } : withInternal ? { withInternal: 1 } : undefined;
    return this.api<ApprovalFlow[]>('GET', '/approval-flows', undefined, params)
      .then(r => r || []).catch(e => { this.reportError('Loading approval flows', e); return []; });
  }
  /** The project's budget beside a payment for it: `{ visible: false }` for someone who may not see that budget. */
  vendorProjectBudget(projectId: string, excludeId = ''): Promise<VendorProjectBudget | null> {
    return this.api<VendorProjectBudget>('GET', '/vendor-requests/project-budget', undefined, { projectId, ...(excludeId ? { excludeId } : {}) })
      .then(r => r || null).catch(() => null);
  }
  saveApprovalFlow(flow: Partial<ApprovalFlow>): Promise<ApprovalFlow | null> {
    return this.api<ApprovalFlow>('POST', '/approval-flows', flow)
      .then(r => { this.scheduleAuditRefresh(); return r || null; })
      .catch(e => { this.reportError('Saving approval flow', e); return null; });
  }
  deleteApprovalFlow(id: string): Promise<boolean> {
    return this.api('DELETE', `/approval-flows/${id}`).then(() => true).catch(e => { this.reportError('Removing approval flow', e); return false; });
  }
  /** Add (no id) or update a vendor; waits for the server so validation messages reach the user. */
  saveVendorRecord(data: Partial<FinanceVendor>, id?: string): Promise<FinanceVendor | null> {
    return this.api<FinanceVendor>(id ? 'PUT' : 'POST', id ? `/vendors/${id}` : '/vendors', data)
      .then(v => { if (v) this.upsertStore(KEYS.VENDORS, v, 'vendors'); this.scheduleAuditRefresh(); return v || null; })
      .catch(e => { this.reportError(id ? 'Saving vendor' : 'Adding vendor', e); return null; });
  }
  removeVendor(id: string): Promise<boolean> {
    return this.api('DELETE', `/vendors/${id}`)
      .then(() => { this.saveVendors(this.getVendors().filter(v => v.id !== id)); this.changed$.next('vendors'); return true; })
      .catch(e => { this.reportError('Removing vendor', e); return false; });
  }
  vendorCapUsage(id: string): Promise<{ capUsedThisMonth: number; cap: number } | null> {
    return this.api<any>('GET', `/vendors/${id}/summary`).then(r => r || null).catch(() => null);
  }
  /** The server's last refusal of a vendor request call, so a form can react to it (e.g. ask for the over-budget reason). */
  lastVendorError = '';
  createVendorPaymentRequest(body: Record<string, any>): Promise<VendorPaymentRequest | null> {
    this.lastVendorError = '';
    return this.api<VendorPaymentRequest>('POST', '/vendor-requests', body)
      .then(r => { if (r) this.upsertStore(KEYS.VENDOR_REQUESTS, r, 'vendor-requests'); this.scheduleAuditRefresh(); return r || null; })
      .catch(e => { this.lastVendorError = FinanceService.errorMessage(e); this.reportError('Creating payment request', e); return null; });
  }
  /** quotes | select-quote | approve | send-back | resubmit | reject | pay | pay-dates (Accounts) | documents | received */
  vendorRequestAction(id: string, action: string, body: Record<string, any>, label: string): Promise<VendorPaymentRequest | null> {
    this.lastVendorError = '';
    return this.api<VendorPaymentRequest>('POST', `/vendor-requests/${id}/${action}`, body)
      .then(r => { if (r) this.upsertStore(KEYS.VENDOR_REQUESTS, r, 'vendor-requests'); this.scheduleAuditRefresh(); return r || null; })
      .catch(e => { this.lastVendorError = FinanceService.errorMessage(e); this.reportError(label, e); return null; });
  }

  // ── Purchase requests (vendor flow v2, 24 Oct 2026) — server-read, no local mirror ──
  /** `filter`: mine | to_handle | all; optional projectId and a comma list of statuses. */
  listPurchaseRequests(params: { filter?: string; projectId?: string; status?: string } = {}): Promise<PurchaseRequest[]> {
    const q: Record<string, any> = { limit: 500 };
    if (params.filter) q['filter'] = params.filter;
    if (params.projectId) q['projectId'] = params.projectId;
    if (params.status) q['status'] = params.status;
    return this.api<{ requests: PurchaseRequest[]; total: number }>('GET', '/purchase-requests', undefined, q)
      .then(r => r?.requests || []).catch(e => { this.reportError('Loading purchase requests', e); return []; });
  }
  getPurchaseRequest(id: string): Promise<PurchaseRequest | null> {
    return this.api<PurchaseRequest>('GET', `/purchase-requests/${id}`).then(r => r || null)
      .catch(e => { this.reportError('Opening the purchase request', e); return null; });
  }
  // ── Internal budgets (25 Oct 2026, ADR-0021) ──
  listInternalBudgets(params: { fy?: string; quarter?: string; status?: string } = {}): Promise<InternalBudget[]> {
    return this.api<InternalBudget[]>('GET', '/internal-budgets', undefined, params).then(r => r || []).catch(e => { this.reportError('Loading the budgets', e); return []; });
  }
  getInternalBudget(id: string): Promise<InternalBudget | null> {
    return this.api<InternalBudget>('GET', `/internal-budgets/${id}`).then(r => r || null).catch(e => { this.reportError('Opening the budget', e); return null; });
  }
  createInternalBudget(body: Record<string, any>): Promise<InternalBudget | null> {
    return this.api<InternalBudget>('POST', '/internal-budgets', body).then(r => { this.scheduleAuditRefresh(); return r || null; }).catch(e => { this.reportError('Drafting the budget', e); return null; });
  }
  updateInternalBudget(id: string, body: Record<string, any>): Promise<InternalBudget | null> {
    return this.api<InternalBudget>('PUT', `/internal-budgets/${id}`, body).then(r => { this.scheduleAuditRefresh(); return r || null; }).catch(e => { this.reportError('Saving the budget', e); return null; });
  }
  /** The founder's call on one line while the budget is proposed: approved, hold or rejected. */
  answerInternalBudgetLine(id: string, lineId: string, answer: string): Promise<InternalBudget | null> {
    return this.api<InternalBudget>('POST', `/internal-budgets/${id}/lines/${lineId}/answer`, { answer }).then(r => r || null).catch(e => { this.reportError('Answering the query', e); return null; });
  }
  decideInternalBudgetLine(id: string, lineId: string, decision: BudgetLineDecision, note = ''): Promise<InternalBudget | null> {
    return this.api<InternalBudget>('POST', `/internal-budgets/${id}/lines/${lineId}/decision`, { decision, note }).then(r => { this.scheduleAuditRefresh(); return r || null; }).catch(e => { this.reportError('Deciding the line', e); return null; });
  }
  internalBudgetAction(id: string, action: 'propose' | 'approve' | 'send-back' | 'revise' | 'drop' | 'request-changes' | 'decline-changes', body: Record<string, any> = {}): Promise<InternalBudget | null> {
    const labels: Record<string, string> = { propose: 'Locking the sheet for review', approve: 'Locking the budget', 'send-back': 'Sending the sheet back', revise: 'Opening the sheet for changes', drop: 'Dropping the draft', 'request-changes': 'Asking for changes', 'decline-changes': 'Declining the change request' };
    return this.api<InternalBudget>('POST', `/internal-budgets/${id}/${action}`, body).then(r => { this.scheduleAuditRefresh(); return r || null; }).catch(e => { this.reportError(labels[action], e); return null; });
  }
  /** The locked internal budget's lines for the quarter of `on` (the raiser's department unless they may read the whole budget), with figures. */
  internalBudgetLines(company: string, on: string, exclude?: string): Promise<InternalBudgetLinesReply | null> {
    return this.api<InternalBudgetLinesReply>('GET', '/purchase-requests/budget-lines', undefined, { company, on, ...(exclude ? { exclude } : {}) })
      .then(r => r || null).catch(e => { this.reportError('Loading the budget lines', e); return null; });
  }

  /** The project's budget lines with what each has left (figures only for people who may see them). */
  purchaseBudgetLines(projectId: string): Promise<{ lines: PurchaseBudgetLine[]; amountsHidden: boolean; canRaise: boolean } | null> {
    return this.api<{ lines: PurchaseBudgetLine[]; amountsHidden: boolean; canRaise: boolean }>('GET', '/purchase-requests/budget-lines', undefined, { projectId })
      .then(r => r || null).catch(e => { this.reportError('Loading the budget lines', e); return null; });
  }
  createPurchaseRequest(body: Record<string, any>): Promise<PurchaseRequest | null> {
    return this.api<PurchaseRequest>('POST', '/purchase-requests', body).then(r => { this.scheduleAuditRefresh(); return r || null; })
      .catch(e => { this.reportError('Raising the purchase request', e); return null; });
  }
  updatePurchaseRequest(id: string, body: Record<string, any>): Promise<PurchaseRequest | null> {
    return this.api<PurchaseRequest>('PUT', `/purchase-requests/${id}`, body).then(r => { this.scheduleAuditRefresh(); return r || null; })
      .catch(e => { this.reportError('Saving the purchase request', e); return null; });
  }
  withdrawPurchaseRequest(id: string, reason: string): Promise<PurchaseRequest | null> {
    return this.api<PurchaseRequest>('POST', `/purchase-requests/${id}/withdraw`, { reason }).then(r => { this.scheduleAuditRefresh(); return r || null; })
      .catch(e => { this.reportError('Withdrawing the purchase request', e); return null; });
  }
  // One purchase, one page (slice 2): quotes, the decision, negotiations and the delivery on the purchase.
  addPurchaseQuote(id: string, body: Record<string, any>): Promise<PurchaseRequest | null> {
    return this.api<PurchaseRequest>('POST', `/purchase-requests/${id}/quotes`, body)
      .then(r => { this.refresh(['vendor-requests'], true).catch(() => { }); this.scheduleAuditRefresh(); return r || null; })
      .catch(e => { this.reportError('Adding the quote', e); return null; });
  }
  sendPurchaseForDecision(id: string, note = ''): Promise<PurchaseRequest | null> {
    return this.api<PurchaseRequest>('POST', `/purchase-requests/${id}/send-for-decision`, { note }).then(r => r || null)
      .catch(e => { this.reportError('Sending for a decision', e); return null; });
  }
  decidePurchase(id: string, body: { action: 'choose' | 'target_price' | 'another_vendor' | 'change_terms'; quoteId?: string; target?: number; terms?: any; note?: string }): Promise<{ request: PurchaseRequest; vendorRequest: VendorPaymentRequest } | null> {
    return this.api<{ request: PurchaseRequest; vendorRequest: VendorPaymentRequest }>('POST', `/purchase-requests/${id}/decision`, body)
      .then(r => { if (r?.vendorRequest) this.upsertStore(KEYS.VENDOR_REQUESTS, r.vendorRequest, 'vendor-requests'); this.scheduleAuditRefresh(); return r || null; })
      .catch(e => { this.reportError(body.action === 'choose' ? 'Choosing the quote' : 'Sending the quotes back', e); return null; });
  }
  answerPurchaseNegotiation(id: string, nid: string, note: string): Promise<PurchaseRequest | null> {
    return this.api<PurchaseRequest>('POST', `/purchase-requests/${id}/negotiations/${nid}/answer`, { note }).then(r => r || null)
      .catch(e => { this.reportError('Answering the head', e); return null; });
  }
  recordPurchaseDelivery(id: string, body: Record<string, any>): Promise<PurchaseRequest | null> {
    return this.api<PurchaseRequest>('POST', `/purchase-requests/${id}/delivery`, body).then(r => { this.scheduleAuditRefresh(); return r || null; })
      .catch(e => { this.reportError('Recording the delivery', e); return null; });
  }
  /** The handler starts the vendor request from a purchase request; the body carries quotes, payment mode and schedule. */
  startVendorRequestFromPurchase(id: string, body: Record<string, any>): Promise<{ request: PurchaseRequest; vendorRequest: VendorPaymentRequest } | null> {
    return this.api<{ request: PurchaseRequest; vendorRequest: VendorPaymentRequest }>('POST', `/purchase-requests/${id}/start-vendor-request`, body)
      .then(r => { if (r?.vendorRequest) this.upsertStore(KEYS.VENDOR_REQUESTS, r.vendorRequest, 'vendor-requests'); this.scheduleAuditRefresh(); return r || null; })
      .catch(e => { this.reportError('Starting the vendor request', e); return null; });
  }

  createVendorRequest(data: Omit<VendorPaymentRequest, 'id' | 'createdAt' | 'status' | 'approvalChain' | 'currentStepIndex' | 'pendingDepartment' | 'approvalHistory'>): VendorPaymentRequest | null {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) return null;
    const chain = this.getFlowChain(data.approvalFlow);
    const req: VendorPaymentRequest = {
      ...data,
      id: this.generateId(),
      status: 'in_progress',
      approvalChain: chain,
      currentStepIndex: 0,
      pendingDepartment: chain[0],
      approvalHistory: [],
      createdAt: new Date().toISOString(),
    };
    const requests = this.getVendorRequests();
    requests.unshift(req);
    this.saveVendorRequests(requests);
    this.enqueue('vendor-request:' + req.id, () => this.api<VendorPaymentRequest>('POST', '/vendor-requests', req))
      .then(saved => {
        this.upsertStore(KEYS.VENDOR_REQUESTS, saved, 'vendor-requests');
        this.scheduleAuditRefresh();
        // The server notifies the pending department and posts the chat alert.
      })
      .catch(e => this.reportError('Creating vendor payment request', e));
    return req;
  }

  async approveVendorRequest(id: string, department: string, note?: string): Promise<VendorPaymentRequest | void> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) return;
    const body: { note?: string } = {};
    if (note && note.trim()) {
      body.note = note.trim();
    }

    try {
      const saved = await this.enqueue('vendor-request:' + id, () =>
        this.api<VendorPaymentRequest>('POST', `/vendor-requests/${id}/approve`, body)
      );
      if (saved && (saved as any).id) {
        this.upsertStore(KEYS.VENDOR_REQUESTS, saved, 'vendor-requests');
      } else {
        this.applyLocalApproval(id, department, note);
      }
      // Audit entry is written by the server (single source).
      this.scheduleAuditRefresh();
      this.toast.success('Payment request approved successfully');
      return saved;
    } catch (e) {
      console.warn(`[FinanceService] Approving vendor payment request ${id} failed:`, e);
      const msg = FinanceService.errorMessage(e);
      this.toast.error(`Approval failed: ${msg}`, { duration: 6000 });
      throw e;
    }
  }

  async rejectVendorRequest(id: string, department: string, note?: string): Promise<VendorPaymentRequest | void> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) return;
    const body: { note?: string } = {};
    if (note && note.trim()) {
      body.note = note.trim();
    }

    try {
      const saved = await this.enqueue('vendor-request:' + id, () =>
        this.api<VendorPaymentRequest>('POST', `/vendor-requests/${id}/reject`, body)
      );
      if (saved && (saved as any).id) {
        this.upsertStore(KEYS.VENDOR_REQUESTS, saved, 'vendor-requests');
      } else {
        this.applyLocalRejection(id, department, note);
      }
      // Audit entry is written by the server (single source).
      this.scheduleAuditRefresh();
      this.toast.success('Payment request rejected');
      return saved;
    } catch (e) {
      console.warn(`[FinanceService] Rejecting vendor payment request ${id} failed:`, e);
      const msg = FinanceService.errorMessage(e);
      this.toast.error(`Rejection failed: ${msg}`, { duration: 6000 });
      throw e;
    }
  }

  private applyLocalApproval(id: string, department: string, note?: string): void {
    const requests = this.getVendorRequests();
    const idx = requests.findIndex(r => r.id === id);
    if (idx === -1) return;
    const req = { ...requests[idx] };
    const chain = req.approvalChain || this.getFlowChain(req.approvalFlow);

    req.approvalHistory = [...(req.approvalHistory || [])];
    req.approvalHistory.push({
      department: department,
      action: 'approved',
      note: note,
      actedBy: localStorage.getItem('username') || '',
      actedAt: new Date().toISOString(),
    });

    const nextIdx = (req.currentStepIndex ?? 0) + 1;
    if (nextIdx >= chain.length) {
      req.currentStepIndex = chain.length;
      req.pendingDepartment = 'ACCOUNTS';
      req.status = 'approved';
    } else {
      req.currentStepIndex = nextIdx;
      req.pendingDepartment = chain[nextIdx];
      req.status = 'in_progress';
    }

    requests[idx] = req;
    this.saveVendorRequests(requests);
  }

  private applyLocalRejection(id: string, department: string, note?: string): void {
    const requests = this.getVendorRequests();
    const idx = requests.findIndex(r => r.id === id);
    if (idx === -1) return;
    const req = { ...requests[idx] };

    req.approvalHistory = [...(req.approvalHistory || [])];
    req.approvalHistory.push({
      department: department,
      action: 'rejected',
      note: note,
      actedBy: localStorage.getItem('username') || '',
      actedAt: new Date().toISOString(),
    });
    req.status = 'rejected';
    req.pendingDepartment = undefined;

    requests[idx] = req;
    this.saveVendorRequests(requests);
  }

  async releaseVendorPayment(id: string, paymentMode: string, utr: string, paymentDate: string): Promise<VendorPaymentRequest | void> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS)) return;
    const payload = {
      paymentMode: paymentMode || 'NEFT',
      utr: utr.trim(),
      paymentDate: paymentDate || toLocalYMD(new Date()),
    };

    try {
      const saved = await this.enqueue('vendor-request:' + id, () =>
        this.api<VendorPaymentRequest>('POST', `/vendor-requests/${id}/release-payment`, payload)
      );
      if (saved && (saved as any).id) {
        this.upsertStore(KEYS.VENDOR_REQUESTS, saved, 'vendor-requests');
      } else {
        this.applyLocalReleasePayment(id, payload.paymentMode, payload.utr, payload.paymentDate);
      }
      // Audit entry is written by the server (single source).
      this.scheduleAuditRefresh();
      this.toast.success('Payment released successfully');
      return saved;
    } catch (e) {
      console.warn(`[FinanceService] Releasing payment for request ${id} failed:`, e);
      const msg = FinanceService.errorMessage(e);
      this.toast.error(`Release payment failed: ${msg}`, { duration: 6000 });
      throw e;
    }
  }

  private applyLocalReleasePayment(id: string, paymentMode: string, utr: string, paymentDate: string): void {
    const requests = this.getVendorRequests();
    const idx = requests.findIndex(r => r.id === id);
    if (idx === -1) return;
    const req = { ...requests[idx] };
    req.status = 'payment_released';
    req.paymentMode = paymentMode;
    req.paymentUtr = utr;
    req.paymentDate = paymentDate;
    req.pendingDepartment = undefined;
    req.approvalHistory = [...(req.approvalHistory || [])];
    req.approvalHistory.push({
      department: 'ACCOUNTS',
      action: 'approved',
      note: `Payment released — ${paymentMode} UTR: ${utr}`,
      actedBy: localStorage.getItem('username') || '',
      actedAt: new Date().toISOString(),
    });
    requests[idx] = req;
    this.saveVendorRequests(requests);
  }

  // ── Dashboard ──────────────────────────────────────────────────────────────

  getDashboardStats(dateFilter?: { from: Date; to: Date }): DashboardStats {
    const projects = this.getProjects();
    let allInvoices: (InvoiceRecord & { projectName: string })[] = [];
    projects.forEach(p =>
      (p.invoices || []).forEach(inv =>
        allInvoices.push({ ...inv, projectName: p.name })
      )
    );

    if (dateFilter) {
      allInvoices = allInvoices.filter(inv => {
        const d = new Date(inv.raisedAt || inv.requestedAt || '');
        return d >= dateFilter.from && d <= dateFilter.to;
      });
    }

    const now = new Date();
    const totalInvoiced = allInvoices.reduce((s, i) => s + i.amount, 0);
    // Collected = money actually received (part payments count); pending/overdue = what is still owed.
    const totalCollected = allInvoices.reduce((s, i) => s + invoicePaidAmount(i), 0);
    const totalRequested = allInvoices.filter(i => i.status === 'pending').reduce((s, i) => s + i.amount, 0);
    // Money to collect = base + GST (30 Oct 2026), the same rule as the server.
    const isOpen = (i: InvoiceRecord) => ['raised', 'sent', 'part_paid'].includes(i.status);
    const totalOutstanding = allInvoices.filter(isOpen).reduce((s, i) => s + invoiceBalance(i), 0);
    const totalPending = totalRequested + totalOutstanding;
    // Overdue = raised bills still owing money whose due date has passed
    const overdueInvoices = allInvoices.filter(i => isOpen(i) && invoiceBalance(i) > 0.01 && i.dueDate && new Date(i.dueDate) < now);
    const totalOverdue = overdueInvoices.reduce((s, i) => s + invoiceBalance(i), 0);
    // Every open bill, whatever the period
    const toCollect: ToCollect = { base: 0, gst: 0, total: 0, invoices: 0 };
    projects.forEach(p => (p.invoices || []).forEach(inv => {
      if (!isOpen(inv)) return;
      const due = invoiceDue(inv);
      if (due.open <= 0.01) return;
      toCollect.base += due.base;
      toCollect.gst += due.gst;
      toCollect.invoices += 1;
    }));
    toCollect.base = Math.round(toCollect.base * 100) / 100;
    toCollect.gst = Math.round(toCollect.gst * 100) / 100;
    toCollect.total = Math.round((toCollect.base + toCollect.gst) * 100) / 100;
    // GST pending = raised/paid invoices where client hasn't paid GST
    const gstPendingInvoices = allInvoices.filter(i => (i.gstStatus === 'not_paid' || !i.gstStatus) && i.status === 'paid');
    const totalGstPending = gstPendingInvoices.reduce((s, i) => s + (i.gstAmount || calcGstAmount(i.amount)), 0);
    // TDS pending = deducted TDS not yet confirmed received in portal
    const totalTdsPending = allInvoices
      .filter(i => i.tdsStatus === 'deducted' && !i.tdsReceivedInPortal)
      .reduce((s, i) => s + (i.tdsAmount || 0), 0);

    return {
      totalInvoiced,
      totalCollected,
      totalOutstanding,
      toCollect,
      totalRequested,
      totalPending,
      totalOverdue,
      totalGstPending,
      totalTdsPending,
      totalProjects: projects.length,
      activeProjects: projects.filter(p => p.status === 'active').length,
      totalVendors: hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS) ? this.getVendors().length : 0,
      collectionRate: totalInvoiced > 0 ? Math.round((totalCollected / totalInvoiced) * 100) : 0,
      counts: {
        invoices: allInvoices.length,
        overdue: overdueInvoices.length,
        gstPending: gstPendingInvoices.length,
        pending: allInvoices.filter(i => ['pending', 'raised', 'sent'].includes(i.status)).length,
        requested: allInvoices.filter(i => i.status === 'pending').length,
        activeProjects: projects.filter(p => p.status === 'active').length,
        totalProjects: projects.length,
        totalVendors: hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.VENDORS) ? this.getVendors().length : 0,
      },
      recentInvoices: [...allInvoices]
        .sort((a, b) => new Date(b.paymentDate || b.raisedAt || b.requestedAt || '').getTime() -
          new Date(a.paymentDate || a.raisedAt || a.requestedAt || '').getTime())
        .slice(0, 5),
      recentProjects: projects.slice(0, 5),
      pendingActions: this.getPendingActions(),
    };
  }

  async getDashboardSummary(params?: Record<string, any>, force = false): Promise<DashboardSummaryResponse | null> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) {
      return null;
    }
    const key = JSON.stringify(params || {});
    if (!force) {
      const cached = this.summaryCache.get(key);
      if (cached && Date.now() - cached.time < 5000) {
        return cached.data;
      }
      const running = this.summaryInflight.get(key);
      if (running) return running;
    }

    const task = (async () => {
      try {
        const data = await this.api<DashboardSummaryResponse>('GET', '/dashboard/summary', undefined, params);
        if (data) {
          this.summaryCache.set(key, { data, time: Date.now() });
        }
        return data || null;
      } catch (e) {
        console.warn('[FinanceService] getDashboardSummary failed:', e);
        return null;
      } finally {
        this.summaryInflight.delete(key);
      }
    })();

    this.summaryInflight.set(key, task);
    return task;
  }

  // ── Recurring Invoices ──────────────────────────────────────────────────────

  getRecurringInvoices(): RecurringInvoice[] {
    const raw = localStorage.getItem(KEYS.RECURRING);
    return raw ? JSON.parse(raw) : [];
  }

  saveRecurringInvoices(items: RecurringInvoice[]): void {
    localStorage.setItem(KEYS.RECURRING, JSON.stringify(items));
  }

  createRecurringInvoice(data: Omit<RecurringInvoice, 'id' | 'createdAt' | 'lastGenerated' | 'generatedCount'>): RecurringInvoice {
    const item: RecurringInvoice = {
      ...data,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      lastGenerated: undefined,
      generatedCount: 0,
    };
    const items = this.getRecurringInvoices();
    items.unshift(item);
    this.saveRecurringInvoices(items);
    this.logActivity({ action: 'recurring_created', entityType: 'invoice', entityId: item.id, entityName: item.label, description: `Recurring invoice "${item.label}" created for "${item.projectName}" — ₹${item.amount.toLocaleString('en-IN')} ${item.frequency}, next due ${item.nextDueDate}` });
    return item;
  }

  updateRecurringInvoice(id: string, updates: Partial<RecurringInvoice>): void {
    const items = this.getRecurringInvoices().map(r => r.id === id ? { ...r, ...updates } : r);
    this.saveRecurringInvoices(items);
  }

  deleteRecurringInvoice(id: string): void {
    const item = this.getRecurringInvoices().find(r => r.id === id);
    this.saveRecurringInvoices(this.getRecurringInvoices().filter(r => r.id !== id));
    if (item) this.logActivity({ action: 'recurring_deleted', entityType: 'invoice', entityId: id, entityName: item.label, description: `Recurring invoice "${item.label}" deleted ("${item.projectName}")` });
  }

  toggleRecurring(id: string): void {
    const items = this.getRecurringInvoices();
    const idx = items.findIndex(r => r.id === id);
    if (idx !== -1) {
      items[idx].enabled = !items[idx].enabled;
      this.saveRecurringInvoices(items);
      this.logActivity({ action: items[idx].enabled ? 'recurring_enabled' : 'recurring_paused', entityType: 'invoice', entityId: id, entityName: items[idx].label, description: `Recurring invoice "${items[idx].label}" ${items[idx].enabled ? 'enabled' : 'paused'}` });
    }
  }

  /** Check all enabled recurring invoices and auto-generate any that are due */
  /**
   * RETIRED 10 Oct 2026: retainers now live inside projects and the server
   * requests each period's invoice on its date. The old browser-only schedules
   * wrote invoices with made-up milestone ids; on live none were ever written.
   */
  checkAndGenerateRecurring(): { generated: string[]; nextDue: RecurringInvoice[] } {
    return { generated: [], nextDue: [] };
  }

  legacyCheckAndGenerateRecurring(): { generated: string[]; nextDue: RecurringInvoice[] } {
    const items = this.getRecurringInvoices();
    const now = new Date();
    const today = toLocalYMD(now);
    const generated: string[] = [];

    items.forEach(r => {
      if (!r.enabled) return;
      if (!r.nextDueDate || r.nextDueDate > today) return;
      // Time to generate!
      const project = this.getProjectById(r.projectId);
      if (!project) return;
      // Create a milestone-less invoice request (pending for accounts to raise)
      const invoice: InvoiceRecord = {
        id: this.generateId(),
        milestoneId: 'recurring_' + r.id,
        number: '',
        amount: r.amount,
        status: 'pending',
        dueDate: r.nextDueDate,
        requestedAt: new Date().toISOString(),
        requestedBy: 'Recurring Auto-Generate',
      };
      this.saveProjectWith(r.projectId, p => ({
        invoices: (p.invoices || []).some(i => i.id === invoice.id) ? p.invoices : [...(p.invoices || []), invoice],
      }), 'Generating recurring invoice');
      generated.push(`${r.label} — ${project.name} — ₹${r.amount.toLocaleString('en-IN')}`);
      this.logActivity({ action: 'recurring_generated', entityType: 'invoice', entityId: invoice.id, entityName: r.label, description: `Recurring invoice auto-generated: "${r.label}" for "${project.name}" — ₹${r.amount.toLocaleString('en-IN')} (generation #${(r.generatedCount || 0) + 1})` });
      // Advance next due date
      r.lastGenerated = today;
      r.generatedCount = (r.generatedCount || 0) + 1;
      r.nextDueDate = this.calcNextDueDate(r.nextDueDate, r.frequency);
      // Stop if we've hit the limit
      if (r.endAfterCount && r.generatedCount >= r.endAfterCount) r.enabled = false;
      if (r.endDate && r.nextDueDate > r.endDate) r.enabled = false;
      // Notify accounts
      this.sendNotification({
        recipientDepartments: ['ACCOUNTS', 'ADMIN'],
        message: `Recurring invoice auto-generated: "${r.label}" for "${project.name}" — ₹${r.amount.toLocaleString('en-IN')}`,
        subType: 'Recurring Invoice',
        redirectUrl: '/finance/invoices',
      });
    });
    this.saveRecurringInvoices(items);
    const nextDue = items.filter(r => r.enabled && r.nextDueDate).sort((a, b) => (a.nextDueDate || '').localeCompare(b.nextDueDate || ''));
    return { generated, nextDue };
  }

  private calcNextDueDate(from: string, freq: string): string {
    const d = new Date(from);
    switch (freq) {
      case 'weekly': d.setDate(d.getDate() + 7); break;
      case 'monthly': d.setMonth(d.getMonth() + 1); break;
      case 'quarterly': d.setMonth(d.getMonth() + 3); break;
      case 'half_yearly': d.setMonth(d.getMonth() + 6); break;
      case 'yearly': d.setFullYear(d.getFullYear() + 1); break;
    }
    return toLocalYMD(d);
  }

  // ── Overdue Reminders ──────────────────────────────────────────────────────

  getOverdueInvoices(): OverdueInvoice[] {
    return this.readStore<OverdueInvoice>(KEYS.OVERDUE_INVOICES);
  }

  async fetchOverdueInvoices(): Promise<OverdueInvoice[]> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) {
      return this.getOverdueInvoices();
    }
    try {
      const res = await this.api<OverdueInvoice[]>('GET', '/overdue-invoices');
      const list = Array.isArray(res) ? res : [];
      this.writeStore(KEYS.OVERDUE_INVOICES, list);
      this.changed$.next('overdue-invoices');
      return list;
    } catch (e) {
      this.reportError('Fetching overdue invoices', e);
      return this.getOverdueInvoices();
    }
  }

  /** Get invoices with overdue GST filings (paid but GST not yet paid, past gstDate) */
  getGstOverdueInvoices(): { projectName: string; gstAmount: number; daysOverdue: number }[] {
    const projects = this.getProjects();
    const now = new Date();
    const results: { projectName: string; gstAmount: number; daysOverdue: number }[] = [];
    projects.forEach(p => {
      (p.invoices || []).forEach(inv => {
        if (inv.status !== 'paid') return;
        if (inv.gstStatus === 'paid') return;
        const gstDue = inv.gstDate ? new Date(inv.gstDate) : null;
        if (!gstDue || gstDue >= now) return;
        const daysOverdue = Math.floor((now.getTime() - gstDue.getTime()) / 86400000);
        results.push({ projectName: p.name, gstAmount: inv.gstAmount || calcGstAmount(inv.amount), daysOverdue });
      });
    });
    return results.sort((a, b) => b.daysOverdue - a.daysOverdue);
  }

  getReminderHistory(): ReminderRecord[] {
    return this.readStore<ReminderRecord>(KEYS.REMINDERS);
  }

  /** What "Send reminder" would e-mail the client, and whether this person may send it; nothing is sent. */
  async previewPaymentReminder(invoiceId: string): Promise<ReminderPreview | null> {
    try {
      return await this.api<ReminderPreview>('GET', `/reminder-preview/${encodeURIComponent(invoiceId)}`);
    } catch (e) {
      this.reportError('Opening the reminder', e);
      return null;
    }
  }

  /**
   * E-mails the client the reminder shown in `preview`. The server sends only when the address and amount
   * still match what the person saw, and only once a day per bill (2 Nov 2026).
   */
  async sendPaymentReminder(preview: ReminderPreview): Promise<{ record?: ReminderRecord } | null> {
    try {
      const res = await this.api<{ record?: ReminderRecord }>('POST', '/send-reminder', {
        invoiceId: preview.invoiceId,
        confirm: true,
        expectedTo: preview.to,
        expectedAmount: preview.amount,
      });
      if (res?.record) this.upsertStore(KEYS.REMINDERS, res.record, 'reminders');
      return res || {};
    } catch (e) {
      this.reportError('Sending payment reminder', e);
      return null;
    }
  }

  async runAutoReminders(): Promise<string[] | null> {
    try {
      const res = await this.api<{ sent: string[] }>('POST', '/check-auto-reminders', {});
      await this.refresh(['reminders', 'reminder-configs'], true);
      return res?.sent || [];
    } catch (e) {
      this.reportError('Running auto-reminders', e);
      return null;
    }
  }

  getRemindersForInvoice(invoiceId: string): ReminderRecord[] {
    return this.getReminderHistory().filter(r => r.invoiceId === invoiceId);
  }

  // ── Audit Trail ────────────────────────────────────────────────────────────

  private auditRefreshTimer: any = null;

  private scheduleAuditRefresh(): void {
    clearTimeout(this.auditRefreshTimer);
    this.auditRefreshTimer = setTimeout(() => this.refresh(['audit-log'], true), 1500);
  }

  logActivity(entry: Omit<AuditLogEntry, 'id' | 'timestamp' | 'user' | 'department'>): void {
    const log = this.getAuditLog();
    const record: AuditLogEntry = {
      ...entry,
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      user: localStorage.getItem('username') || 'System',
      department: localStorage.getItem('department') || '',
    };
    log.unshift(record);
    // Keep max 500 entries
    if (log.length > 500) log.length = 500;
    localStorage.setItem(KEYS.AUDIT_LOG, JSON.stringify(log));
  }

  getAuditLog(): AuditLogEntry[] {
    const raw = localStorage.getItem(KEYS.AUDIT_LOG);
    return raw ? JSON.parse(raw) : [];
  }

  getAuditLogFiltered(filters?: {
    entityType?: string;
    entityId?: string;
    projectId?: string;
    user?: string;
    department?: string;
    action?: string;
    from?: string;
    to?: string;
  }): AuditLogEntry[] {
    let log = this.getAuditLog();
    if (!filters) return log;
    if (filters.entityType) log = log.filter(e => e.entityType === filters.entityType);
    if (filters.entityId) log = log.filter(e => e.entityId === filters.entityId);
    if (filters.projectId) log = log.filter(e => e.entityId === filters.projectId || (e as any).projectId === filters.projectId);
    if (filters.user) log = log.filter(e => e.user.toLowerCase().includes(filters.user!.toLowerCase()));
    if (filters.department) log = log.filter(e => e.department === filters.department);
    if (filters.action) log = log.filter(e => e.action.includes(filters.action!));
    if (filters.from) log = log.filter(e => e.timestamp >= filters.from!);
    if (filters.to) log = log.filter(e => e.timestamp <= filters.to!);
    return log;
  }

  clearAuditLog(): void {
    const dept = localStorage.getItem('department') || '';
    if (!['ADMIN', 'DIRECTOR'].includes(dept)) {
      console.warn('clearAuditLog blocked — only ADMIN may clear the audit trail (current: ' + dept + ')');
      return;
    }
    localStorage.removeItem(KEYS.AUDIT_LOG);
    // Log the clear itself so the fresh trail records who wiped it and when
    this.logActivity({ action: 'audit_log_cleared', entityType: 'system', entityId: 'audit_log', description: 'Audit log cleared' });
  }

  // ── Budget vs Actual ────────────────────────────────────────────────────────

  /**
   * Get actual spend per project from released vendor payments.
   * Returns a map: projectId → total released amount.
   */
  getActualSpendByProject(): Record<string, number> {
    const requests = this.getVendorRequests();
    const spendMap: Record<string, number> = {};
    requests.forEach(r => {
      if (r.status === 'payment_released' && r.projectId) {
        spendMap[r.projectId] = (spendMap[r.projectId] || 0) + r.amount;
      }
    });
    return spendMap;
  }

  /**
   * Get actual spend per vendor category for a specific project.
   */
  getActualSpendByCategory(projectId: string): Record<string, number> {
    const requests = this.getVendorRequests();
    const catMap: Record<string, number> = {};
    requests.forEach(r => {
      if (r.status === 'payment_released' && r.projectId === projectId) {
        const vendor = this.getVendorById(r.vendorId);
        const cat = vendor?.category || 'Uncategorized';
        catMap[cat] = (catMap[cat] || 0) + r.amount;
      }
    });
    return catMap;
  }

  /**
   * Full budget vs actual report across all projects with budgets.
   */
  getBudgetVsActual(): BudgetVsActualReport {
    const projects = this.getProjects().filter(p => p.budget && p.budget > 0);
    const spendMap = this.getActualSpendByProject();
    const items: BudgetProjectItem[] = projects.map(p => {
      const actual = spendMap[p.id] || 0;
      const budget = p.budget || 0;
      const variance = budget - actual;
      const pct = budget > 0 ? Math.round((actual / budget) * 100) : 0;
      return {
        projectId: p.id,
        projectName: p.name,
        clientName: p.client.legalName,
        status: p.status,
        budget,
        actual,
        variance,
        pct,
        overBudget: actual > budget,
        lineItems: (p.budgetLineItems || []).map(li => {
          const catSpend = this.getCategorySpendForProject(p.id, li.category);
          return { ...li, actual: catSpend, variance: li.planned - catSpend, pct: li.planned > 0 ? Math.round((catSpend / li.planned) * 100) : 0 };
        }),
      };
    });
    const totalBudget = items.reduce((s, i) => s + i.budget, 0);
    const totalActual = items.reduce((s, i) => s + i.actual, 0);
    return {
      items,
      totalBudget,
      totalActual,
      totalVariance: totalBudget - totalActual,
      totalPct: totalBudget > 0 ? Math.round((totalActual / totalBudget) * 100) : 0,
      projectsOverBudget: items.filter(i => i.overBudget).length,
      projectsUnderBudget: items.filter(i => !i.overBudget).length,
    };
  }

  /** Get spend in a specific category for a project (from vendor payments) */
  private getCategorySpendForProject(projectId: string, category: string): number {
    const requests = this.getVendorRequests();
    let total = 0;
    requests.forEach(r => {
      if (r.status === 'payment_released' && r.projectId === projectId) {
        // Use explicit budgetCategory if set, fallback to vendor category
        const cat = r.budgetCategory || (() => { const v = this.getVendorById(r.vendorId); return v?.category || 'Uncategorized'; })();
        if (cat === category) total += r.amount;
      }
    });
    return total;
  }

  // ── Payment Reminder Configs ──────────────────────────────────────────────

  getReminderConfigs(): ReminderConfig[] {
    return this.readStore<ReminderConfig>(KEYS.REMINDER_CONFIGS);
  }

  saveReminderConfigs(configs: ReminderConfig[]): void {
    this.writeStore(KEYS.REMINDER_CONFIGS, configs);
  }

  getReminderConfigForInvoice(invoiceId: string): ReminderConfig | null {
    return this.getReminderConfigs().find(c => c.invoiceId === invoiceId) || null;
  }

  setReminderConfig(config: ReminderConfig): Promise<ReminderConfig | null> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) {
      return Promise.resolve(null);
    }
    if (!config.invoiceId) {
      this.toast.error('Reminder config needs an invoice');
      return Promise.resolve(null);
    }
    const payload = {
      invoiceId: config.invoiceId,
      enabled: Boolean(config.enabled),
      frequencyDays: Array.isArray(config.frequencyDays) ? config.frequencyDays : [],
      repeatEveryDays: Number(config.repeatEveryDays) || 0,
    };
    const configs = this.getReminderConfigs().filter(c => c.invoiceId !== config.invoiceId);
    configs.push({ ...config, ...payload });
    this.saveReminderConfigs(configs);

    return this.api<ReminderConfig>('POST', '/reminder-configs', payload)
      .then(saved => {
        if (saved) this.upsertStore(KEYS.REMINDER_CONFIGS, saved, 'reminder-configs', 'invoiceId');
        this.toast.success('Reminder configuration saved');
        return saved || config;
      })
      .catch(e => {
        this.reportError('Saving reminder config', e);
        return null;
      });
  }

  // ── Expense Tracking ────────────────────────────────────────────────────

  getExpenses(): Expense[] {
    const raw = localStorage.getItem(KEYS.EXPENSES);
    return raw ? JSON.parse(raw) : [];
  }

  saveExpenses(expenses: Expense[]): void {
    localStorage.setItem(KEYS.EXPENSES, JSON.stringify(expenses));
  }

  createExpense(data: Omit<Expense, 'id' | 'submittedAt' | 'submittedBy' | 'submittedByDepartment' | 'status'>): Expense {
    const expense: Expense = {
      ...data,
      id: this.generateId(),
      submittedBy: localStorage.getItem('username') || '',
      submittedByDepartment: localStorage.getItem('department') || '',
      submittedAt: new Date().toISOString(),
      status: 'pending',
    };
    const expenses = this.getExpenses();
    expenses.unshift(expense);
    this.saveExpenses(expenses);
    this.logActivity({ action: 'expense_submitted', entityType: 'system', entityId: expense.id, entityName: expense.category, description: `Expense ₹${expense.amount.toLocaleString('en-IN')} submitted for "${expense.description}" (${expense.category})` });
    this.sendNotification({ recipientDepartments: ['ACCOUNTS', 'ADMIN'], message: `New expense ₹${expense.amount.toLocaleString('en-IN')} submitted by ${expense.submittedBy} — ${expense.category}`, subType: 'Expense Submitted', redirectUrl: '/finance/expenses' });
    return expense;
  }

  approveExpense(id: string): void {
    const expenses = this.getExpenses();
    const idx = expenses.findIndex(e => e.id === id);
    if (idx === -1) return;
    expenses[idx].status = 'approved';
    expenses[idx].approvedBy = localStorage.getItem('username') || '';
    expenses[idx].approvedAt = new Date().toISOString();
    this.saveExpenses(expenses);
    this.logActivity({ action: 'expense_approved', entityType: 'system', entityId: id, description: `Expense ₹${expenses[idx].amount.toLocaleString('en-IN')} approved — "${expenses[idx].description}"` });
  }

  rejectExpense(id: string, reason: string): void {
    const expenses = this.getExpenses();
    const idx = expenses.findIndex(e => e.id === id);
    if (idx === -1) return;
    expenses[idx].status = 'rejected';
    expenses[idx].rejectedBy = localStorage.getItem('username') || '';
    expenses[idx].rejectedAt = new Date().toISOString();
    expenses[idx].rejectionReason = reason;
    this.saveExpenses(expenses);
    this.logActivity({ action: 'expense_rejected', entityType: 'system', entityId: id, description: `Expense ₹${expenses[idx].amount.toLocaleString('en-IN')} rejected — ${reason}` });
  }

  reimburseExpense(id: string, utr: string): void {
    const expenses = this.getExpenses();
    const idx = expenses.findIndex(e => e.id === id);
    if (idx === -1) return;
    expenses[idx].status = 'reimbursed';
    expenses[idx].reimbursedBy = localStorage.getItem('username') || '';
    expenses[idx].reimbursedAt = new Date().toISOString();
    expenses[idx].reimbursementUtr = utr;
    this.saveExpenses(expenses);
    this.logActivity({ action: 'expense_reimbursed', entityType: 'system', entityId: id, description: `Expense ₹${expenses[idx].amount.toLocaleString('en-IN')} reimbursed — UTR: ${utr}` });
  }

  getExpenseSummary(): { pending: number; pendingCount: number; approved: number; approvedCount: number; reimbursed: number; reimbursedCount: number; total: number; totalCount: number } {
    const expenses = this.getExpenses();
    const pending = expenses.filter(e => ['pending', 'peer_approval', 'in_approval'].includes(e.status));
    const approved = expenses.filter(e => e.status === 'approved');
    const reimbursed = expenses.filter(e => e.status === 'reimbursed');
    return {
      pending: pending.reduce((s, e) => s + e.amount, 0), pendingCount: pending.length,
      approved: approved.reduce((s, e) => s + e.amount, 0), approvedCount: approved.length,
      reimbursed: reimbursed.reduce((s, e) => s + e.amount, 0), reimbursedCount: reimbursed.length,
      total: expenses.reduce((s, e) => s + e.amount, 0), totalCount: expenses.length,
    };
  }

  // ── API-backed Methods ───────────────────────────────────────────────────

  async getBudgetStatusViaApi(projectId: string): Promise<BudgetStatus | null> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) return null;
    try {
      return (await this.api<BudgetStatus>('GET', `/projects/${projectId}/budget-status`)) || null;
    } catch (e) {
      this.reportError('Loading budget status', e);
      return null;
    }
  }

  /** Get remaining budget for a category */
  getCategoryBudgetRemaining(projectId: string, category: string): number {
    const project = this.getProjectById(projectId);
    if (!project || !project.budget || !project.budgetLineItems) return Infinity;
    const li = project.budgetLineItems.find(l => l.category === category);
    if (!li) return Infinity;
    const spent = this.getCategorySpendForProject(projectId, category);
    return li.planned - spent;
  }

  /** Validate vendor payment against budget */
  validateBudget(projectId: string, category: string, amount: number): { valid: boolean; remaining: number; warning?: string } {
    const remaining = this.getCategoryBudgetRemaining(projectId, category);
    if (remaining === Infinity) return { valid: true, remaining: 0 };
    if (amount > remaining) {
      return { valid: false, remaining, warning: `Exceeds budget by ₹${(amount - remaining).toLocaleString('en-IN')}. Category "${category}" has ₹${remaining.toLocaleString('en-IN')} remaining.` };
    }
    return { valid: true, remaining };
  }

  // ── Expense Category Limits ───────────────────────────────────────────

  getExpenseCategoryLimits(): ExpenseCategoryLimit[] {
    return this.readStore<ExpenseCategoryLimit>(KEYS.EXPENSE_LIMITS);
  }

  setExpenseCategoryLimit(category: string, maxAmount: number, description?: string): void {
    const limits = this.getExpenseCategoryLimits().filter(l => l.category !== category);
    limits.push({ category, maxAmount, description });
    this.writeStore(KEYS.EXPENSE_LIMITS, limits);
    if (hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) {
      this.api('POST', '/expense-limits', { category, maxAmount, description })
        .catch(e => this.reportError('Saving expense limit', e));
    }
  }

  getExpenseLimitForCategory(category: string): number | null {
    const limit = this.getExpenseCategoryLimits().find(l => l.category === category);
    return limit ? limit.maxAmount : null;
  }

  // ── BD targets moved to BdTargetsService (features/finance/services/bd-targets.service.ts) on 11 Oct 2026;
  //    the server computes every figure, nothing is kept in the browser. ──

  // ── Credit/Debit Notes ──────────────────────────────────────────────

  private cnCounter = 0;
  private dnCounter = 0;

  getCreditDebitNotes(): CreditDebitNote[] {
    const raw = localStorage.getItem(KEYS.CREDIT_DEBIT_NOTES);
    return raw ? JSON.parse(raw) : [];
  }

  saveCreditDebitNotes(notes: CreditDebitNote[]): void {
    localStorage.setItem(KEYS.CREDIT_DEBIT_NOTES, JSON.stringify(notes));
  }

  private generateNoteNumber(type: 'credit' | 'debit'): string {
    const prefix = type === 'credit' ? 'CN' : 'DN';
    const year = new Date().getFullYear();
    const existing = this.getCreditDebitNotes().filter(n => n.type === type);
    const num = existing.length + 1;
    return `${prefix}-${year}-IIC-${String(num).padStart(4, '0')}`;
  }

  createCreditDebitNote(data: Omit<CreditDebitNote, 'id' | 'noteNumber' | 'createdAt' | 'createdBy' | 'gstImpact' | 'status'>): CreditDebitNote {
    const note: CreditDebitNote = {
      ...data,
      id: this.generateId(),
      noteNumber: this.generateNoteNumber(data.type),
      status: 'draft',
      gstImpact: Math.round(data.amount * 0.18),
      createdAt: new Date().toISOString(),
      createdBy: localStorage.getItem('username') || '',
    };
    const notes = this.getCreditDebitNotes();
    notes.unshift(note);
    this.saveCreditDebitNotes(notes);
    const { noteNumber, gstImpact, ...body } = note;
    if (hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) {
      this.enqueue('note:' + note.id, () => this.api<CreditDebitNote>('POST', '/credit-debit-notes', body))
        .then(saved => {
          this.upsertStore(KEYS.CREDIT_DEBIT_NOTES, saved, 'credit-debit-notes');
          this.scheduleAuditRefresh();
        })
        .catch(e => this.reportError('Creating credit/debit note', e));
    }
    return note;
  }

  updateCreditDebitNote(id: string, updates: Partial<CreditDebitNote>): void {
    const notes = this.getCreditDebitNotes().map(n => n.id === id ? { ...n, ...updates } : n);
    this.saveCreditDebitNotes(notes);
    if (hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) {
      this.enqueue('note:' + id, () => this.api<CreditDebitNote>('PUT', `/credit-debit-notes/${id}`, updates))
        .then(saved => this.upsertStore(KEYS.CREDIT_DEBIT_NOTES, saved, 'credit-debit-notes'))
        .catch(e => this.reportError('Updating credit/debit note', e));
    }
  }

  issueNote(id: string): void {
    this.updateCreditDebitNote(id, {
      status: 'issued',
      issuedAt: new Date().toISOString(),
      issuedBy: localStorage.getItem('username') || '',
    });
    const note = this.getCreditDebitNotes().find(n => n.id === id);
    if (note) {
      this.logActivity({
        action: 'note_issued',
        entityType: 'invoice',
        entityId: note.invoiceId,
        entityName: note.noteNumber,
        description: `${note.type === 'credit' ? 'Credit' : 'Debit'} note ${note.noteNumber} issued — ₹${note.amount.toLocaleString('en-IN')}`,
      });
    }
  }

  acceptNote(id: string): void {
    this.updateCreditDebitNote(id, {
      status: 'accepted',
      acceptedAt: new Date().toISOString(),
      acceptedBy: localStorage.getItem('username') || '',
    });
  }

  cancelNote(id: string): void {
    this.updateCreditDebitNote(id, { status: 'cancelled' });
    const note = this.getCreditDebitNotes().find(n => n.id === id);
    if (note) {
      this.logActivity({
        action: 'note_cancelled',
        entityType: 'invoice',
        entityId: note.invoiceId,
        entityName: note.noteNumber,
        description: `${note.type === 'credit' ? 'Credit' : 'Debit'} note ${note.noteNumber} cancelled`,
      });
    }
  }

  getNotesForInvoice(invoiceId: string): CreditDebitNote[] {
    return this.getCreditDebitNotes().filter(n => n.invoiceId === invoiceId && n.status !== 'cancelled');
  }

  getNetInvoiceAmount(invoiceId: string, originalAmount: number): number {
    const notes = this.getNotesForInvoice(invoiceId);
    let net = originalAmount;
    notes.forEach(n => {
      if (n.status === 'cancelled') return;
      if (n.type === 'credit') net -= n.amount;
      else net += n.amount;
    });
    return net;
  }

  getCreditDebitSummary(): { totalCredits: number; totalDebits: number; creditCount: number; debitCount: number } {
    const notes = this.getCreditDebitNotes().filter(n => n.status !== 'cancelled');
    const credits = notes.filter(n => n.type === 'credit');
    const debits = notes.filter(n => n.type === 'debit');
    return {
      totalCredits: credits.reduce((s, n) => s + n.amount, 0),
      totalDebits: debits.reduce((s, n) => s + n.amount, 0),
      creditCount: credits.length,
      debitCount: debits.length,
    };
  }

  // ── Audit Log via API ────────────────────────────────────────────────

  async getAuditLogViaApi(filters?: { entityType?: string; entityId?: string; projectId?: string; user?: string; department?: string; action?: string; from?: string; to?: string; page?: number; limit?: number }): Promise<{ entries: AuditLogEntry[]; hasMore: boolean }> {
    if (!hasFinanceSubModuleAccess(FINANCE_SUB_MODULES.PROJECTS)) {
      return { entries: this.getAuditLogFiltered(filters), hasMore: false };
    }
    const limit = filters?.limit || 50;
    try {
      const entries = (await this.api<AuditLogEntry[]>('GET', '/audit-log', undefined, { ...filters, limit })) || [];
      return { entries, hasMore: entries.length >= limit };
    } catch (e) {
      console.warn('[FinanceService] Loading audit log failed, using local copy:', e);
      return { entries: this.getAuditLogFiltered(filters), hasMore: false };
    }
  }
}

// ── Budget vs Actual Types ─────────────────────────────────────────────────

export interface BudgetLineItemActual extends BudgetLineItem {
  actual: number;
  variance: number;
  pct: number;
}

export interface BudgetProjectItem {
  projectId: string;
  projectName: string;
  clientName: string;
  status: string;
  budget: number;
  actual: number;
  variance: number;
  pct: number;           // actual/budget %
  overBudget: boolean;
  lineItems: BudgetLineItemActual[];
}

export interface BudgetVsActualReport {
  items: BudgetProjectItem[];
  totalBudget: number;
  totalActual: number;
  totalVariance: number;
  totalPct: number;
  projectsOverBudget: number;
  projectsUnderBudget: number;
}

/** The report Link returns for an opening-data run (dry run or load). */
export interface ImportRow { id: string; name: string; bdOwnerLogin: string; status: string; invoices: number; billed: number; received: number; due: number; action: 'create' | 'created' | 'merge' | 'merged' | 'update' | 'updated' | 'skip' | 'failed'; reasons: string[]; warnings: string[]; /** merge rows (30 Oct 2026): the BD-created project and the plan, line by line */ mergeInto?: string; plan?: string[]; }
export interface ImportReport { dryRun: boolean; importRef: string; totals: { projects: number; create: number; merge: number; update?: number; skip: number; failed: number; invoices: number; billed: number; received: number; due: number }; rows: ImportRow[]; }
/** The e-invoice IRN: the 64-character hex code the GST portal returns (mirrors the server rule). Empty = none given. */
export const IRN_PATTERN = /^[0-9a-f]{64}$/i;
export function irnProblem(v: unknown): string {
  const s = normalizeIrn(v);
  if (!s) return '';
  return IRN_PATTERN.test(s) ? '' : 'The IRN is the 64-character code from the e-invoice portal (letters a–f and digits only).';
}
/** An IRN as typed or pasted: spaces and line breaks dropped, lower-cased (mirrors the server, 2 Nov 2026). */
export function normalizeIrn(v: unknown): string {
  return String(v ?? '').replace(/\s+/g, '').toLowerCase();
}
/**
 * A bill raised in Link that should carry its e-invoice number and does not (2 Nov 2026): raised, GST on it, a client
 * GSTIN on the project, no IRN. Bills loaded from the books are left out. Accounts are reminded until it is in.
 */
export function irnMissing(project: Pick<FinanceProject, 'client'> | null | undefined, inv: InvoiceRecord | null | undefined): boolean {
  if (!project || !inv || inv.imported) return false;
  if (!['raised', 'sent', 'part_paid', 'paid'].includes(String(inv.status))) return false;
  if (normalizeIrn(inv.irn)) return false;
  if (!(invoiceGstTotal(inv) > 0)) return false;
  return !!String(project.client?.gst ?? '').trim();
}

/** Internal budgets = quarter sheets (25 Oct 2026, ADR-0021): one per financial year and quarter, the HR head's QBR sheet in Link. */
export type InternalBudgetStatus = 'draft' | 'proposed' | 'locked' | 'superseded' | 'dropped';
export type BudgetLineKind = 'capex' | 'subscription' | 'opex';
/**
 * The Admin's call on an A / D / G line: open until decided; only approved counts; next_quarter comes back open next quarter;
 * rejected stays in its block as a record and never counts (1 Nov 2026); removed sits at the bottom.
 */
export type BudgetLineDecision = 'open' | 'approved' | 'hold' | 'next_quarter' | 'query' | 'rejected' | 'removed';
/**
 * The blocks of the HR head's QBR sheet (v2, 1 Nov 2026), in sheet order: A new confirmed inventory · B estimated inventory
 * (hires not confirmed, no cost yet) · C hardware repairs (an open-ended budget HR draws on as repairs come, per founder) ·
 * D new projects or vendor payments · E upcoming approved payments · F already spent this quarter · G approved earlier, carried
 * in. The keys stay as they were (data uses them).
 */
export type BudgetSection = 'new' | 'estimated' | 'repairs' | 'vendor' | 'committed' | 'spent' | 'carried';
/** Blocks an Admin decides line by line; the others carry no call (link-backend `BUDGET_DECIDED_SECTIONS`). */
export const BUDGET_DECIDED_SECTIONS: readonly BudgetSection[] = ['new', 'repairs', 'vendor', 'carried'];
/** Blocks the HR head raises purchases on once the sheet is locked (approved lines only). */
export const BUDGET_DRAWABLE_SECTIONS: readonly BudgetSection[] = ['new', 'repairs', 'vendor', 'carried'];
/** How urgent an A line is (the sheet's new column; the HR head has not set values yet, these are the defaults). */
export type BudgetUrgency = '' | 'high' | 'medium' | 'low';
export const BUDGET_URGENCY_LABEL: Record<Exclude<BudgetUrgency, ''>, string> = { high: 'High', medium: 'Medium', low: 'Low' };
/**
 * The sheet's expense categories in their groups (link-backend `EXPENSE_CATEGORIES`, 1 Nov 2026), beside kind, not instead of
 * it; Miscellaneous is the approved pot for unplanned payments HR draws on after the lock (per founder).
 */
export const EXPENSE_CATEGORIES: readonly { key: string; label: string; group: string }[] = [
  { key: 'workstations', label: 'Workstations', group: 'Hardware & Infrastructure' },
  { key: 'laptops', label: 'Laptops', group: 'Hardware & Infrastructure' },
  { key: 'mobiles', label: 'Mobiles', group: 'Hardware & Infrastructure' },
  { key: 'computer_accessories', label: 'Computer Accessories (incl. repairs)', group: 'Hardware & Infrastructure' },
  { key: 'infrastructure', label: 'Infrastructure Related', group: 'Hardware & Infrastructure' },
  { key: 'cloud_infrastructure', label: 'Cloud Infrastructure', group: 'Hardware & Infrastructure' },
  { key: 'corporate_dev_licenses', label: 'Corporate & Dev Licenses', group: 'Subscriptions, Licenses & Compliance' },
  { key: 'software_subscriptions', label: 'Software Subscriptions', group: 'Subscriptions, Licenses & Compliance' },
  { key: 'compliance', label: 'Compliance', group: 'Subscriptions, Licenses & Compliance' },
  { key: 'cybersecurity', label: 'Cybersecurity', group: 'Subscriptions, Licenses & Compliance' },
  { key: 'admin', label: 'Admin', group: 'Admin & Welfare' },
  { key: 'welfare', label: 'Welfare', group: 'Admin & Welfare' },
  { key: 'hr_tools', label: 'HR Tools', group: 'Admin & Welfare' },
  { key: 'miscellaneous', label: 'Miscellaneous', group: 'Miscellaneous' },
];
export type QuarterKey = 'q1' | 'q2' | 'q3' | 'q4';
export interface InternalBudgetLine {
  id: string; section: BudgetSection; company: 'ink_in_caps' | 'pixel_studio'; companyLabel?: string; department: string; kind: BudgetLineKind; category: string;
  forWhom?: string; newOrReplacement?: '' | 'new' | 'replacement'; expectedOn?: string; buyOrRent?: '' | 'buy' | 'rent'; reuse?: string; spendMonth?: string; approvedIn?: string;
  amount: number; decision?: BudgetLineDecision; decisionLabel?: string; decisionNote?: string; decidedBy?: string; decidedAt?: string; notes?: string; drawable?: boolean;
  answer?: string; answeredBy?: string; answeredAt?: string; carriedFrom?: string;
  /** QBR v2 (1 Nov 2026): one of `EXPENSE_CATEGORIES`; A's urgency; whose hardware a repair is for; the day already-spent money was approved. */
  expenseCategory?: string; urgency?: BudgetUrgency; employee?: string; approvedOn?: string;
}
/** A quarter before Link (QBR v2): its budget against what was spent, typed in by HR; the balance is budget − spent, once. */
export interface InternalBudgetEarlierQuarter { label: string; budget: number; spent: number; note?: string; }
export interface InternalBudgetApproval { userId: string; name: string; at: string; as?: string; }
export interface InternalBudgetChangeRequest { by: string; byName: string; at: string; reason: string; status: 'pending' | 'opened' | 'declined'; answeredBy?: string; answeredAt?: string; note?: string; }
export interface InternalBudgetTotals {
  newAndVendor: number; gst: number; committedDue: number; carried: number; planned: number; cashOutlook: number; held: number; nextQuarter: number; removed: number;
  open: number; openLines: number; bySection: Record<string, number>; byCompany: Record<string, number>;
  /** QBR v2: approved C repairs (also inside planned); F already spent, in the cash outlook as entered; B estimated hires count nowhere. */
  repairs?: number; spent?: number; estimatedLines?: number; rejected?: number;
}
export interface InternalBudgetUsed { asked: number; committed: number; paid: number; left: number; unbought: number; }
export interface InternalBudget {
  id: string; fy: string; quarter: QuarterKey; quarterLabel: string; label: string; title: string;
  status: InternalBudgetStatus; revision: number; revisionOf?: string; lines: InternalBudgetLine[];
  totals: InternalBudgetTotals; used?: InternalBudgetUsed;
  earlierQuarters?: InternalBudgetEarlierQuarter[];
  figures?: Record<string, { planned: number; asked: number; committed: number; paid: number; remaining: number }>;
  lastQuarter?: { id: string; label: string; planned: number; cashOutlook: number } & InternalBudgetUsed;
  approvals: { hrHead: InternalBudgetApproval | null; admin: InternalBudgetApproval | null };
  proposedAt?: string; proposedByName?: string; lockedAt?: string; supersededAt?: string; sentBackNote?: string; changeRequest?: InternalBudgetChangeRequest | null;
  history?: { at: string; by: string; action: string; note?: string; as?: string; reason?: string; answer?: string; category?: string; decision?: string }[];
  canEdit: boolean; canPropose: boolean; canDecideLines: boolean; canAnswer: boolean; canLock: boolean; lockBlockers: string[]; canSendBack: boolean;
  canRequestChanges: boolean; canOpenForChanges: boolean; canDeclineChanges: boolean; canRevise: boolean; canDrop: boolean; canRaisePurchases: boolean;
  partial?: boolean;
}
export interface InternalBudgetLinesReply {
  lines: (PurchaseBudgetLine & { department?: string; kind?: BudgetLineKind; section?: BudgetSection })[];
  budget: { id: string; fy: string; quarter: QuarterKey; company: string; revision: number; status: string } | null;
  quarter: 'q1' | 'q2' | 'q3' | 'q4'; quarterLabel: string; amountsHidden: boolean; canRaise: boolean;
}
